<template>
    <div id="page-backlog" data-page="backlog">
        <div class="task-management-page">

                <b-row class="my-2 align-items-center" v-if="hasBacklogSource && !isServiceUnavailable">
                    <!--Sprints-->
                    <b-col cols="12" class="d-flex justify-content-center">
                        <picker-sprints connection-context="backlog" @sprint-picked="searchUserStoriesBySprint"></picker-sprints>
                    </b-col>

                </b-row>

                <!-- Missing Backlog Source Warning Card -->
                <card-missing-source
                    v-if="isMissingBacklogVisible"
                    source-type="backlog"
                    feature-name="Backlog"
                    :project="activeProject"
                />

                <!-- Service Unavailable Placeholder -->
                <div v-else-if="isServiceUnavailable" class="text-center py-5 text-muted">
                    <i class="bi bi-cloud-slash fa-2x mb-2 d-block text-warning"></i>
                    <div class="font-weight-bold mb-1">Service is currently unreachable</div>
                    <span class="small">Work items will be loaded automatically once the service is restored.</span>
                </div>

                <!-- Backlog Content Area -->
                <template v-else>
                    <b-container fluid>
                        <!-- Header Banner / Stats (Immediately under sprint picker) -->
                        <grid-header-banner
                            title="Backlog & Tasks"
                            subtitle="Committed user stories, bugs, and task hierarchy for current sprint scope"
                            icon="bi bi-kanban-fill"
                            icon-color="text-primary"
                            :stats="backlogStats"
                        />

                        <!--Sprint Capacity Goal Indicator Banner (Positioned beneath section header)-->
                        <div class="sprint-capacity-banner compact px-3 py-1 mb-3 d-flex flex-wrap align-items-center justify-content-between"
                             v-if="isGridVisible && selectedSprint">
                            <!-- Left: Icon, Working Days & Core Stats -->
                            <div class="d-flex align-items-center flex-wrap mr-2 py-1">
                                <i class="bi bi-bullseye text-primary mr-2" style="font-size: 1.05rem;"></i>
                                <b-badge v-if="sprintWorkingDays > 0" variant="secondary" class="sprint-capacity-pill mr-2">
                                    <i class="bi bi-calendar-week mr-1"></i>{{ sprintWorkingDays }} working days
                                </b-badge>
                                <div class="sprint-capacity-subtext small d-inline-flex align-items-center flex-wrap">
                                    <span>Available: <strong class="sprint-capacity-val text-body">{{ availableSprintCapacity.toFixed(1) }}h</strong></span>
                                    <span class="mx-1">&bull;</span>
                                    <span>Assigned: <strong class="sprint-capacity-val text-body">{{ assignedSprintCapacity.toFixed(1) }}h</strong><span class="extra-small ml-1" v-if="assignedTasksCount > 0">({{ assignedTasksCount }} items)</span></span>
                                    <span class="mx-1">&bull;</span>
                                    <span>Logged: <strong class="sprint-capacity-val text-body">{{ loggedSprintHours.toFixed(1) }}h</strong></span>
                                    <span
                                        v-if="midSprintAddedSummary.count > 0"
                                        class="hygiene-badge hygiene-badge-amber d-inline-flex align-items-center ml-2"
                                        data-test="badge-sprint-scope-creep"
                                        :title="midSprintAddedSummary.tooltip"
                                    >
                                        <i class="bi bi-calendar-plus mr-1"></i>
                                        <span>+{{ midSprintAddedSummary.count }} Added Mid-Sprint<template v-if="midSprintAddedSummary.hours > 0"> (+{{ midSprintAddedSummary.hours.toFixed(0) }}h)</template></span>
                                    </span>
                                </div>
                            </div>

                            <!-- Right: Progress Bar & Open Capacity Status -->
                            <div class="d-flex align-items-center flex-wrap py-1 ml-auto">
                                <!-- Progress Bar for Capacity Allocation & Open Capacity -->
                                <div class="sprint-capacity-progress-wrap d-flex align-items-center mr-3"
                                     v-b-tooltip.hover.top="sprintCapacityTooltip">
                                    <b-progress :max="sprintProgressMax" height="10px" class="sprint-capacity-progress rounded-pill">
                                        <b-progress-bar :value="loggedSprintHours" variant="success"></b-progress-bar>
                                        <b-progress-bar :value="Math.max(0, Math.min(assignedSprintCapacity, availableSprintCapacity) - loggedSprintHours)" variant="info"></b-progress-bar>
                                        <b-progress-bar v-if="assignedSprintCapacity > availableSprintCapacity" :value="assignedSprintCapacity - availableSprintCapacity" variant="danger" class="overcapacity-bar"></b-progress-bar>
                                    </b-progress>
                                    <span class="sprint-capacity-pct ml-2 extra-small font-weight-bold text-muted">
                                        {{ assignedSprintCapacityPercent.toFixed(0) }}%
                                    </span>
                                </div>

                                <!-- Status Badge Pill -->
                                <div class="sprint-capacity-badge-pill" :class="sprintCapacityStatus.variantClass">
                                    <i :class="sprintCapacityStatus.icon" class="status-icon mr-2"></i>
                                    <span class="status-label mr-1">{{ sprintCapacityStatus.label }}:</span>
                                    <strong class="status-value">{{ sprintCapacityStatus.text }}</strong>
                                </div>
                            </div>
                        </div>
                    </b-container>

                    <!--Grid Area with Reserved Vertical Space-->
                    <b-overlay :show="loadingUserStoryItems" rounded="lg" opacity="0.6" class="task-grid-overlay">
                    <template #overlay>
                        <load-spinner text="Loading backlog items..." />
                    </template>

                    <!--Grid-->
                    <b-container fluid v-if="isGridVisible" class="task-items-grid" data-test="backlog-grid"
                        :data-state="loadingUserStoryItems ? 'loading' : 'idle'" :aria-busy="loadingUserStoryItems ? 'true' : 'false'">
                    <div ref="scrollContainer" class="time-grid-scroll" :style="scrollStyle">
                        <div class="time-grid-content">
                            <b-card class="task-items-grid-header">
                                <!--Filter Toolbar on top of grid headers-->
                                <div
                                  v-if="false"></div>
                                <!-- Mobile Collapsible Filter Toggle (<=991.98px) -->
                                <div class="mobile-filter-toggle-bar d-flex d-lg-none align-items-center justify-content-between">
                                    <b-button
                                        size="sm"
                                        :variant="showMobileFilters || isAnyTaskFilterActive ? 'primary' : 'outline-secondary'"
                                        class="py-1 px-2 font-weight-bold d-inline-flex align-items-center"
                                        :aria-expanded="showMobileFilters ? 'true' : 'false'"
                                        data-test="btn-toggle-mobile-filters"
                                        @click="showMobileFilters = !showMobileFilters"
                                    >
                                        <i class="bi bi-sliders mr-1"></i>
                                        <span>{{ showMobileFilters ? 'Hide Filters' : 'Filters & Rollover' }}</span>
                                        <b-badge v-if="isAnyTaskFilterActive" variant="light" class="ml-1">Active</b-badge>
                                        <i class="bi ml-1" :class="showMobileFilters ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                                    </b-button>
                                    <b-button
                                        v-if="isAnyTaskFilterActive"
                                        size="sm"
                                        variant="outline-secondary"
                                        class="py-1 px-2"
                                        @click="resetToDefaults"
                                    >
                                        <i class="bi bi-arrow-counterclockwise mr-1"></i>Reset
                                    </b-button>
                                </div>
                                <div
                                    class="grid-filter-toolbar d-flex align-items-center flex-wrap pb-2 mb-2 border-bottom"
                                    :class="{ 'mobile-filters-collapsed': !showMobileFilters }">
                                    <div class="filter-toolbar-label mr-2">
                                        <i class="bi bi-funnel mr-1"></i><strong>Filters:</strong>
                                    </div>

                                    <!-- Assigned filter chip -->
                                    <filter-author-dropdown
                                        label="Assigned"
                                        v-model="selectedAssignees"
                                        :project-id="activeProject ? activeProject.id : null"
                                        class="mr-2"
                                        data-test="filter-chip-assigned"
                                        @change="onAssigneesChanged"
                                    />

                                    <!-- State filter chip -->
                                    <b-dropdown size="sm"
                                        :variant="isStateFilterActive ? 'primary' : 'outline-secondary'" no-caret
                                        class="grid-filter-chip"
                                        data-test="filter-chip-state">
                                        <template #button-content>
                                            <i
                                                :class="isStateFilterActive ? 'bi bi-funnel-fill mr-1' : 'bi bi-funnel mr-1'"></i>
                                            <span class="font-weight-bold mr-1">State:</span>
                                            <span>{{ stateFilterText }}</span>
                                            <i class="bi bi-chevron-down ml-1 filter-chevron"></i>
                                        </template>
                                        <b-dropdown-form class="filter-chip-dropdown-form p-2">
                                            <div
                                                class="d-flex justify-content-between align-items-center mb-2 pb-1 border-bottom">
                                                <small class="font-weight-bold">Filter by State</small>
                                                <div>
                                                    <b-button size="sm" variant="link"
                                                        class="p-0 text-decoration-none"
                                                        @click="selectAllStates">All</b-button>
                                                </div>
                                            </div>
                                            <div class="filter-options-list">
                                                <div
                                                    v-for="group in groupedStateOptions"
                                                    :key="group.key"
                                                    class="state-group-section"
                                                    :data-test="'state-group-' + group.key"
                                                >
                                                    <div class="state-group-header d-flex justify-content-between align-items-center">
                                                        <span class="text-uppercase" :title="group.types.join(', ')">{{ group.label }}</span>
                                                        <b-button size="sm" variant="link"
                                                            class="group-select-all text-decoration-none"
                                                            data-test="btn-select-all-group"
                                                            :data-test-id="group.key"
                                                            @click="selectAllStatesInGroup(group)">All</b-button>
                                                    </div>
                                                    <b-form-checkbox
                                                        v-for="opt in group.states"
                                                        :key="group.key + '-' + opt"
                                                        :checked="isDraftStateChecked(group, opt)"
                                                        :disabled="isDraftStateDisabled(group, opt)"
                                                        :title="isDraftStateDisabled(group, opt) ? 'At least one state must be selected' : stateOptionTitle(opt)"
                                                        data-test="state-option"
                                                        :data-test-id="group.key + '-' + opt"
                                                        class="m-0 compact-checkbox"
                                                        @change="toggleDraftGroupState(group, opt, $event)"
                                                    >
                                                        {{ opt }}
                                                    </b-form-checkbox>
                                                </div>
                                            </div>
                                        </b-dropdown-form>
                                    </b-dropdown>

                                    <!-- Action Buttons aligned to the right -->
                                    <div class="filter-toolbar-actions ml-auto d-flex align-items-center">
                                        <!-- Apply Button -->
                                        <b-button size="sm" variant="primary" class="mr-2"
                                            :class="{ 'btn-apply-pulse': hasUnappliedFilters && !loadingUserStoryItems }"
                                            @click="reloadGrid" :disabled="loadingUserStoryItems || isStateFilterInvalid"
                                            data-test="btn-apply-filters"
                                            :data-dirty="hasUnappliedFilters ? 'true' : 'false'"
                                            :data-state="loadingUserStoryItems ? 'loading' : 'idle'"
                                            title="Apply filters and reload">
                                            <i class="bi bi-check-circle mr-1"></i>Apply
                                        </b-button>

                                        <!-- Reset to Defaults button -->
                                        <b-button size="sm" variant="outline-secondary" class="mr-2"
                                            @click="resetToDefaults" :disabled="loadingUserStoryItems"
                                            data-test="btn-reset-filters"
                                            title="Reset filters to defaults and reload">
                                            <i class="bi bi-arrow-counterclockwise mr-1"></i>Reset to Defaults
                                        </b-button>

                                        <!-- Move Incomplete to Next Sprint button -->
                                        <b-button v-if="false"
                                            size="sm" variant="outline-info" class="mr-2"
                                            data-test="btn-move-incomplete-next-sprint"
                                            :data-state="isMovingToNextSprint ? 'loading' : 'idle'"
                                            :disabled="loadingUserStoryItems || isMovingToNextSprint"
                                            :title="`Move ${incompleteRolloverTotalCount} incomplete item(s) to ${nextSprint.name}`"
                                            @click="openMoveNextSprintModal">
                                            <i class="bi bi-fast-forward mr-1"></i>Move to {{ nextSprint.name }} ({{ incompleteRolloverTotalCount }})
                                        </b-button>

                                    </div>

                                    <!-- Presentation / View Options Section (Client Side Only - Always on New Line) -->
                                    <div class="filter-toolbar-group filter-toolbar-view d-flex align-items-center flex-wrap w-100">
                                        <div class="filter-toolbar-label mr-2">
                                            <i class="bi bi-eye mr-1"></i><strong>View:</strong>
                                        </div>
                                        <b-input-group size="sm" class="mr-2" style="width: 210px;">
                                            <b-input-group-prepend is-text>
                                                <i class="bi bi-search" :class="{ 'text-primary font-weight-bold': !!(backlogViewSearch && backlogViewSearch.trim()) }"></i>
                                            </b-input-group-prepend>
                                            <b-form-input
                                                v-model="backlogViewSearch"
                                                placeholder="Filter title or #ID..."
                                                aria-label="Filter backlog items by title or ID"
                                                data-test="input-backlog-search"
                                                style="height: 31px; font-size: 0.82rem;"
                                            ></b-form-input>
                                            <b-input-group-append v-if="backlogViewSearch">
                                                <b-button size="sm" variant="outline-secondary" aria-label="Clear search" title="Clear search" data-test="btn-clear-backlog-search" @click="backlogViewSearch = ''" style="height: 31px;">
                                                    <i class="bi bi-x"></i>
                                                </b-button>
                                            </b-input-group-append>
                                        </b-input-group>
                                        <button
                                            type="button"
                                            class="btn btn-sm hygiene-filter-chip d-inline-flex align-items-center ml-1"
                                            :class="{ 'is-active': onlyHygieneAlerts, 'has-alerts': hygieneAlertsCount > 0 }"
                                            v-if="hygieneConfig && hygieneConfig.enabled"
                                            data-test="btn-filter-hygiene-alerts"
                                            :aria-pressed="onlyHygieneAlerts ? 'true' : 'false'"
                                            title="Filter work items with state or hours hygiene mismatches"
                                            @click="onlyHygieneAlerts = !onlyHygieneAlerts"
                                        >
                                            <i class="bi bi-shield-exclamation mr-1"></i>
                                            <span>Needs Hygiene Fix</span>
                                            <span
                                                class="hygiene-filter-count ml-1"
                                                data-test="badge-hygiene-alerts-count"
                                            >{{ hygieneAlertsCount }}</span>
                                        </button>
                                        <div class="filter-toolbar-view-actions ml-auto d-flex align-items-center">
                                            <span v-if="hasFreeDailyQuota && dailyMutationsRemaining > 0"
                                                class="badge badge-info px-2 py-1 mr-2 font-weight-bold"
                                                data-test="badge-free-mutation-quota"
                                                v-b-tooltip.hover :title="`Free tier includes ${dailyMutationLimit} platform actions per UTC day. Upgrade to Pro Plan for unlimited two-way sync.`">
                                                <i class="bi bi-lightning-charge-fill mr-1"></i>{{ dailyMutationsRemaining }}/{{ dailyMutationLimit }} Free Actions Left
                                            </span>
                                            <span v-else-if="isViewOnlyMode" class="badge badge-warning text-dark px-2 py-1 mr-2 font-weight-bold"
                                                data-test="badge-view-only"
                                                v-b-tooltip.hover title="Daily free action quota (5/5) used. Upgrade to Pro Plan for unlimited task editing, state transitions, and discussions, or wait until 00:00 UTC.">
                                                <i class="bi bi-eye mr-1"></i>View-Only Mode (0/5 Left Today)
                                            </span>
                                            <!-- Auto-Arrange Best Fit Columns button -->
                                            <b-button size="sm" variant="outline-secondary"
                                                class="d-inline-flex align-items-center mr-1"
                                                data-test="btn-best-fit-columns"
                                                aria-label="Auto-arrange columns to best fit"
                                                title="Auto-arrange columns to best fit"
                                                v-b-tooltip.hover.top="'Auto-arrange columns to best fit'"
                                                @click="autoFitGridColumns">
                                                <i class="bi bi-distribute-horizontal"></i>
                                            </b-button>
                                            <!-- Customize Columns button -->
                                            <b-button size="sm" variant="outline-secondary"
                                                class="d-inline-flex align-items-center"
                                                data-test="btn-configure-columns"
                                                aria-label="Customize visible grid columns and setup"
                                                title="Customize visible grid columns and setup"
                                                v-b-tooltip.hover.top="'Customize visible grid columns and setup'"
                                                @click="() => openColumnSettings()">
                                                <i class="bi bi-gear"></i>
                                            </b-button>
                                        </div>
                                    </div>
                                    <div v-if="false">

                                        <!-- Customize Columns button -->
                                        <b-button size="sm" variant="outline-secondary"
                                            class="d-none d-md-inline-flex align-items-center"
                                            data-test="btn-configure-columns"
                                            @click="() => openColumnSettings()"
                                            title="Customize visible grid columns">
                                            <i class="bi bi-gear"></i>
                                        </b-button>

                                        <!-- View-Only Mode Badge -->
                                        <span v-if="isViewOnlyMode" class="badge badge-warning text-dark px-2 py-1 ml-2 font-weight-bold"
                                            data-test="badge-view-only"
                                            v-b-tooltip.hover title="Free tier is in View-Only mode. Upgrading to Pro Plan unlocks task editing, state transitions, and discussions.">
                                            <i class="bi bi-eye mr-1"></i>View-Only Mode
                                        </span>
                                    </div>
                                </div>

                                <b-row class="my-1 time-row backlog-header-row" :style="gridRowStyle">
                                    <b-col class="px-1 time-col-collapser">
                                        <b-button @click="toggleCollapseAll" variant="secondary" size="sm"
                                            aria-label="Toggle Collapse ALL"
                                            title="Toggle Collapse ALL"
                                            data-test="btn-toggle-collapse-all"
                                            v-b-popover.hover.top.noninteractive="actionHoverPop('Toggle Collapse ALL')">
                                            <i class="bi bi-chevron-right row-expand-icon" :class="{ 'is-expanded': isAllCollapsesOpen }"></i>
                                        </b-button>
                                    </b-col>
                                    <template v-for="col in visibleGridColumns">
                                        <b-col v-if="col.referenceName === 'System.WorkItem'" :key="col.referenceName" class="px-1 time-col-id text-center">Work Item<span class="col-resize-handle" :class="{ 'is-resizing': resizingColRef === col.referenceName }" data-test="col-resize-handle" :data-col-ref="col.referenceName" title="Drag to resize column (double-click to reset)" @mousedown.stop.prevent="onStartResizeColumn($event, col)" @dblclick.stop.prevent="onResetColumnWidth(col)"></span></b-col>
                                        <b-col v-else-if="col.referenceName === 'System.Comments'" :key="col.referenceName" class="px-1 time-col-comments text-center"
                                            v-b-popover.hover.top.noninteractive="actionHoverPop('Comments')">
                                            <i class="bi bi-chat-left-text"></i>
                                            <span class="col-resize-handle" :class="{ 'is-resizing': resizingColRef === col.referenceName }" data-test="col-resize-handle" :data-col-ref="col.referenceName" title="Drag to resize column (double-click to reset)" @mousedown.stop.prevent="onStartResizeColumn($event, col)" @dblclick.stop.prevent="onResetColumnWidth(col)"></span>
                                        </b-col>
                                        <b-col v-else-if="col.referenceName === 'System.AssignedTo'" :key="col.referenceName" class="px-1 time-col-assigned text-center">
                                            <div class="d-flex align-items-center justify-content-center">
                                                <span>Assigned</span>
                                                <span class="col-resize-handle" :class="{ 'is-resizing': resizingColRef === col.referenceName }" data-test="col-resize-handle" :data-col-ref="col.referenceName" title="Drag to resize column (double-click to reset)" @mousedown.stop.prevent="onStartResizeColumn($event, col)" @dblclick.stop.prevent="onResetColumnWidth(col)"></span>
                                                <i v-if="isAssignedFilterActive"
                                                    class="bi bi-funnel-fill column-filter-icon"
                                                    v-b-tooltip.hover.top="'Filter applied: Only Me'"></i>
                                            </div>
                                        </b-col>
                                        <b-col v-else-if="col.referenceName === 'System.Title'" :key="col.referenceName" class="px-1 time-col-title text-center">Title<span class="col-resize-handle" :class="{ 'is-resizing': resizingColRef === col.referenceName }" data-test="col-resize-handle" :data-col-ref="col.referenceName" title="Drag to resize column (double-click to reset)" @mousedown.stop.prevent="onStartResizeColumn($event, col)" @dblclick.stop.prevent="onResetColumnWidth(col)"></span></b-col>
                                        <b-col v-else-if="col.referenceName === 'System.State'" :key="col.referenceName" class="px-1 time-col-state text-center">
                                            <div class="d-flex align-items-center justify-content-center">
                                                <span>State</span>
                                                <span class="col-resize-handle" :class="{ 'is-resizing': resizingColRef === col.referenceName }" data-test="col-resize-handle" :data-col-ref="col.referenceName" title="Drag to resize column (double-click to reset)" @mousedown.stop.prevent="onStartResizeColumn($event, col)" @dblclick.stop.prevent="onResetColumnWidth(col)"></span>
                                                <i v-if="isStateFilterActive" class="bi bi-funnel-fill column-filter-icon"
                                                    v-b-tooltip.hover.top="'Filter applied'"></i>
                                            </div>
                                        </b-col>
                                        <b-col v-else-if="col.referenceName === 'System.Capacity'" :key="col.referenceName" class="px-1 time-col-capacity text-center">Capacity<span class="col-resize-handle" :class="{ 'is-resizing': resizingColRef === col.referenceName }" data-test="col-resize-handle" :data-col-ref="col.referenceName" title="Drag to resize column (double-click to reset)" @mousedown.stop.prevent="onStartResizeColumn($event, col)" @dblclick.stop.prevent="onResetColumnWidth(col)"></span></b-col>
                                        <b-col v-else-if="col.referenceName === 'System.OriginalEstimate'" :key="col.referenceName" class="px-1 time-col-estimate text-center">Original Estimate<span class="col-resize-handle" :class="{ 'is-resizing': resizingColRef === col.referenceName }" data-test="col-resize-handle" :data-col-ref="col.referenceName" title="Drag to resize column (double-click to reset)" @mousedown.stop.prevent="onStartResizeColumn($event, col)" @dblclick.stop.prevent="onResetColumnWidth(col)"></span></b-col>
                                        <b-col v-else-if="col.referenceName === 'System.Progress'" :key="col.referenceName" class="px-1 time-col-progress text-center">Progress<span class="col-resize-handle" :class="{ 'is-resizing': resizingColRef === col.referenceName }" data-test="col-resize-handle" :data-col-ref="col.referenceName" title="Drag to resize column (double-click to reset)" @mousedown.stop.prevent="onStartResizeColumn($event, col)" @dblclick.stop.prevent="onResetColumnWidth(col)"></span></b-col>


                                        <b-col v-else-if="col.referenceName === 'System.Action'" :key="col.referenceName" class="px-1 time-col-buttons text-center d-flex flex-column align-items-center justify-content-center">
                                            <span>Action</span>
                                            <div v-if="parentActionButtons.length > 0" class="mt-1" data-test="parent-add-actions">
                                                <grid-button-section :buttons="parentActionButtons"
                                                    @action="onParentAction($event)"></grid-button-section>
                                            </div>
                                        </b-col>
                                        <b-col v-else :key="col.referenceName" class="px-1 time-col-custom text-center">
                                            <span class="header-col-label" :title="col.label">{{ col.label }}</span>
                                            <span class="col-resize-handle" :class="{ 'is-resizing': resizingColRef === col.referenceName }" data-test="col-resize-handle" :data-col-ref="col.referenceName" title="Drag to resize column (double-click to reset)" @mousedown.stop.prevent="onStartResizeColumn($event, col)" @dblclick.stop.prevent="onResetColumnWidth(col)"></span>
                                        </b-col>
                                    </template>
                                </b-row>
                            </b-card>
                            <div v-if="false"
                                 class="parent-add-actions-bar time-row"
                                 :style="gridRowStyle"
                                 data-test="parent-add-actions">
                                <div class="time-col-buttons action-buttons" style="grid-column: -2 / -1;">
                                    <grid-button-section :buttons="parentActionButtons"
                                        @action="onParentAction($event)"></grid-button-section>
                                </div>
                            </div>
                            <b-alert :show="isProcessFallback" variant="warning" class="mt-2 mb-0 small" data-test="alert-process-fallback">
                                Could not read the work item process configuration from the backlog source. Generic work item types and states are shown.
                            </b-alert>
                            <b-alert :show="isTruncated" variant="warning" class="mt-2 mb-0 small" data-test="alert-backlog-truncated">
                                Too many work items match the current filters. Only the first part of the hierarchy is shown, narrow the filters to see the rest.
                            </b-alert>

                            <!--Backlog tree: one card per root item, rows are flattened with depth-->
                            <div v-for="group in gridGroups" :key="group.key" data-test="grid-group" :data-test-id="group.root.workItemId || group.root.vid">
                                <b-card style="margin: 10px 0px;">
                                    <div v-for="row in group.rows" :key="row.key" class="grid-node-row"
                                        :class="{
                                            'is-context-row': (row.node.isContext && !row.node.isAssignedToMe) || isOutOfSprintParent(row.node),
                                            'is-out-of-sprint-parent': isOutOfSprintParent(row.node)
                                        }"
                                        data-test="grid-node" :data-depth="row.depth"
                                        :data-context="(row.node.isContext && !row.node.isAssignedToMe) || isOutOfSprintParent(row.node) ? 'true' : 'false'"
                                        :data-out-of-sprint="isOutOfSprintParent(row.node) ? 'true' : 'false'">
                                    <ValidationObserver v-if="!row.node.isPlannable" :ref="'node-' + row.node.vid" v-slot="{ handleSubmit: onSubmitNode }">
                                        <b-form @submit.prevent="onSubmitNode(() => updateUserStoryItemAsync(row.node))">
                                            <b-row align-h="start" class="my-1 time-row" :style="gridRowStyle" data-test="grid-row-story" :data-test-id="row.node.workItemId || row.node.vid">
                                                <b-col class="px-1 time-col-collapser">
                                                    <b-button v-if="row.hasChildren" variant="secondary" size="sm"
                                                        @click="toggleNode(row.node)"
                                                        aria-label="Toggle collapse"
                                                        title="Toggle Collapse"
                                                        data-test="btn-toggle-collapse"
                                                        :data-state="isNodeExpanded(row.node) ? 'expanded' : 'collapsed'"
                                                        v-b-popover.hover.top.noninteractive="actionHoverPop('Toggle Collapse')">
                                                        <i class="bi bi-chevron-right row-expand-icon" :class="{ 'is-expanded': isNodeExpanded(row.node) }"></i>
                                                    </b-button>
                                                </b-col>
                                                <template v-for="col in visibleGridColumns">
                                                    <!-- Work Item -->
                                                    <b-col v-if="col.referenceName === 'System.WorkItem'" :key="col.referenceName" class="px-1 time-col-id">
                                                        <div v-if="row.node.isSaved()" class="wi-id-stack">
                                                            <span v-if="isOutOfSprintParent(row.node)"
                                                                  class="badge out-of-sprint-badge"
                                                                  data-test="badge-out-of-sprint"
                                                                  :title="getOutOfSprintTooltip(row.node)"
                                                                  v-b-tooltip.hover.top="getOutOfSprintTooltip(row.node)">
                                                                {{ getNodeSprintShortName(row.node) || 'Backlog' }}
                                                            </span>
                                                            <nav-link-button :href="row.node.url" :id="row.node.workItemId"
                                                                :type-icon="row.node.workItemTypeIcon"
                                                                :work-item-type="row.node.workItemType"
                                                                title="Open work item in Azure DevOps" />
                                                        </div>
                                                        <span v-else class="badge badge-info px-2 py-1 text-truncate d-inline-block"
                                                            style="max-width: 100%;"
                                                            data-test="badge-new-parent-type"
                                                            :title="'New ' + (row.node.workItemType || 'Work Item')">
                                                            {{ row.node.workItemType || 'New' }}
                                                        </span>
                                                    </b-col>
                                                    <!-- Comments -->
                                                    <b-col v-else-if="col.referenceName === 'System.Comments'" :key="col.referenceName" class="px-1 time-col-comments text-center">
                                                        <b-button
                                                            v-if="row.node.isSaved()"
                                                            size="sm"
                                                            variant="link"
                                                            class="p-0 wi-comment-btn"
                                                            :class="{ 'has-comments': (row.node.commentCount || 0) > 0, 'has-mentions': (row.node.mentionCount || 0) > 0 }"
                                                            aria-label="Open discussion comments"
                                                            title="Open discussion comments"
                                                            data-test="btn-open-comments"
                                                            v-b-popover.hover.top.noninteractive="actionHoverPop(getCommentsTooltip(row.node))"
                                                            @click="openCommentsModal(row.node)">
                                                            <i class="bi" :class="(row.node.mentionCount || 0) > 0 ? 'bi-chat-left-dots-fill text-warning' : ((row.node.commentCount || 0) > 0 ? 'bi-chat-left-text-fill text-primary' : 'bi-chat-left text-muted')"></i>
                                                            <b-badge v-if="(row.node.commentCount || 0) > 0" pill variant="primary" class="ml-1 wi-comment-count">{{ row.node.commentCount }}</b-badge>
                                                            <b-badge v-if="(row.node.mentionCount || 0) > 0" pill variant="warning" class="ml-1 wi-mention-count" data-test="badge-mention-count">@{{ row.node.mentionCount }}</b-badge>
                                                        </b-button>
                                                    </b-col>
                                                    <!-- Assigned -->
                                                    <b-col v-else-if="col.referenceName === 'System.AssignedTo'" :key="col.referenceName" class="px-1 time-col-assigned text-center">
                                                        <div>
                                                            <b-img class="assigned-to-image" :src="row.node.assignedToSource"
                                                                v-b-popover.hover.top
                                                                :title="getAssignedTitle(row.node.assignedToName)"
                                                                rounded="circle" @error="onAssignedToError"></b-img>
                                                        </div>
                                                        <b-img v-show="row.node.assignedToDevSource" class="assigned-to-image dev"
                                                            :src="row.node.assignedToDevSource" v-b-popover.hover.top
                                                            :title="`Dev: ${row.node.assignedToDevName}`"
                                                            rounded="circle"></b-img>
                                                        <b-img v-show="row.node.assignedToQaSource" class="assigned-to-image qa"
                                                            :src="row.node.assignedToQaSource" v-b-popover.hover.top
                                                            :title="`QA: ${row.node.assignedToQaName}`" rounded="circle"></b-img>
                                                        <b-img v-show="row.node.assignedToSaSource" class="assigned-to-image sa"
                                                            :src="row.node.assignedToSaSource" v-b-popover.hover.top
                                                            :title="`SA: ${row.node.assignedToSaName}`" rounded="circle"></b-img>
                                                    </b-col>
                                                    <!-- Title -->
                                                    <b-col v-else-if="col.referenceName === 'System.Title'" :key="col.referenceName" class="px-1 time-col-title" :style="indentStyle(row)">
                                                        <ValidationProvider name="Title"
                                                            v-slot="{ errors: storyTitleErrors }"
                                                            :rules="{ required: true }">
                                                            <grid-title-textarea :id="`storytitle-${row.node.vid}`"
                                                                :ref="`textarea-${row.node.vid}`" name="title"
                                                                v-model="row.node.title"
                                                                :placeholder="row.node.titleHint"
                                                                :disabled="isReadOnly(row.node)">
                                                            </grid-title-textarea>
                                                            <div
                                                                v-if="hasNodeAnyAttentionSignal(row.node)"
                                                                class="hygiene-alert-bar d-flex align-items-center flex-wrap mt-1"
                                                                :data-hygiene-key="getNodeHygieneAlert(row.node) ? getNodeHygieneAlert(row.node).key : 'stage2'"
                                                            >
                                                                <template v-if="getNodeHygieneAlert(row.node)">
                                                                    <span
                                                                        class="hygiene-badge d-inline-flex align-items-center"
                                                                        :class="`hygiene-badge-${getNodeHygieneAlert(row.node).variant}`"
                                                                        :data-test="getNodeHygieneAlert(row.node).badgeTestId"
                                                                        :title="getNodeHygieneAlert(row.node).tooltip"
                                                                    >
                                                                        <i :class="[getNodeHygieneAlert(row.node).icon, 'mr-1']"></i>
                                                                        <span>{{ getNodeHygieneAlert(row.node).label }}</span>
                                                                    </span>
                                                                    <button
                                                                        v-if="!isReadOnly(row.node)"
                                                                        type="button"
                                                                        class="btn btn-xs hygiene-fix-btn d-inline-flex align-items-center ml-1"
                                                                        :data-test="getNodeHygieneAlert(row.node).btnTestId"
                                                                        :data-state="isFixingHygiene(row.node) ? 'loading' : 'idle'"
                                                                        :aria-busy="isFixingHygiene(row.node) ? 'true' : 'false'"
                                                                        :disabled="isFixingHygiene(row.node)"
                                                                        :aria-label="getNodeHygieneAlert(row.node).actionTooltip"
                                                                        :title="getNodeHygieneAlert(row.node).actionTooltip"
                                                                        @click.stop="applyHygieneFixAsync(row.node, getNodeHygieneAlert(row.node))"
                                                                    >
                                                                        <b-spinner v-if="isFixingHygiene(row.node)" small class="mr-1"></b-spinner>
                                                                        <i v-else :class="[getNodeHygieneAlert(row.node).actionIcon, 'mr-1']"></i>
                                                                        <span>{{ getNodeHygieneAlert(row.node).actionLabel }}</span>
                                                                    </button>
                                                                </template>
                                                                <span
                                                                    v-for="s2 in getNodeStage2Badges(row.node)"
                                                                    :key="s2.key"
                                                                    class="hygiene-badge d-inline-flex align-items-center"
                                                                    :class="`hygiene-badge-${s2.variant}`"
                                                                    :data-test="s2.badgeTestId"
                                                                    :title="s2.tooltip"
                                                                >
                                                                    <i :class="[s2.icon, 'mr-1']"></i>
                                                                    <span>{{ s2.label }}</span>
                                                                </span>
                                                            </div>
                                                            <b-popover :target="`storytitle-${row.node.vid}`"
                                                                :show="storyTitleErrors.length > 0 && row.node.isModifying()"
                                                                variant="danger" placement="left" triggers=""
                                                                custom-class="validation-pop">
                                                                {{ storyTitleErrors[0] }}
                                                            </b-popover>
                                                        </ValidationProvider>
                                                    </b-col>
                                                    <!-- State -->
                                                    <b-col v-else-if="col.referenceName === 'System.State'" :key="col.referenceName" class="px-1 time-col-state">
                                                        <label class="mobile-col-caption d-lg-none">State</label>
                                                        <div @mouseenter="markSelectOverflow">
                                                            <b-form-select
                                                                v-model="row.node.state"
                                                                :options="getAllowedStates(row.node)"
                                                                size="sm"
                                                                :title="row.node.state"
                                                                class="value-color-select"
                                                                :style="getValueColorSelectStyle(col, row.node.state)"
                                                                data-test="select-task-state"
                                                                :disabled="isReadOnly(row.node)"
                                                                @change="onStateChanged(row.node, $event)">
                                                            </b-form-select>
                                                        </div>
                                                    </b-col>
                                                    <!-- Capacity (Empty for US) -->
                                                    <b-col v-else-if="col.referenceName === 'System.Capacity'" :key="col.referenceName" class="px-1 time-col-capacity">
                                                    </b-col>
                                                    <!-- Original Estimate (Effort for US) -->
                                                    <b-col v-else-if="col.referenceName === 'System.OriginalEstimate'" :key="col.referenceName" class="px-1 time-col-estimate">
                                                        <label class="mobile-col-caption d-lg-none">{{ effortUnitLabel ? 'Effort' : 'Original Estimate' }}</label>
                                                        <div v-if="showsEffort(row.node)">
                                                            <ValidationProvider name="Effort" v-slot="{ errors: nodeErrors }"
                                                                :rules="{ required: true, regex: /^[0-9]+(\.[0-9]{1,2})?$/, min_value: 0 }">
                                                                <b-input-group>
                                                                    <b-form-input :id="`nodehours-${row.node.vid}`" name="effort"
                                                                        v-model="row.node.effort" type="number" step="0.5"
                                                                        :disabled="isReadOnly(row.node) || row.node.isSaved()">
                                                                    </b-form-input>
                                                                    <b-input-group-append>
                                                                        <b-input-group-text>{{ effortUnitLabel }}</b-input-group-text>
                                                                    </b-input-group-append>
                                                                </b-input-group>
                                                                <b-popover :target="`nodehours-${row.node.vid}`"
                                                                    :show="nodeErrors.length > 0 && row.node.isModifying()"
                                                                    variant="danger" placement="left" triggers=""
                                                                    custom-class="validation-pop">
                                                                    {{ nodeErrors[0] }}
                                                                </b-popover>
                                                            </ValidationProvider>
                                                        </div>
                                                    </b-col>
                                                    <!-- Progress -->
                                                    <b-col v-else-if="col.referenceName === 'System.Progress'" :key="col.referenceName" class="px-1 time-col-progress">
                                                        <label class="mobile-col-caption d-lg-none">Progress</label>
                                                        <div v-show="row.node.getFullCapacityHours() > 0">
                                                            <time-progress-item compact :value="row.node.getFullCompletedHours()"
                                                                :total="row.node.getFullCapacityHours()" period="sprint"
                                                                :period-start-date="selectedSprintFrom"
                                                                :period-date="selectedSprintTo" :marker-date="todayDate"
                                                                :daily-capacity="dailyCapacity">
                                                            </time-progress-item>
                                                        </div>
                                                    </b-col>
                                                    <!-- Action -->
                                                    <b-col v-else-if="col.referenceName === 'System.Action'" :key="col.referenceName" class="px-1 time-col-buttons action-buttons">
                                                        <grid-button-section :buttons="nodeActionButtons(row)"
                                                            @action="onNodeAction(row, $event)"></grid-button-section>
                                                    </b-col>
                                                    <!-- Custom Columns -->
                                                    <b-col v-else :key="col.referenceName" class="px-1 time-col-custom">
                                                        <label class="mobile-col-caption d-lg-none">{{ col.label }}</label>
                                                        <template v-if="isColumnSupportedForWorkItem(col, row.node)">
                                                        <div v-if="getCustomFieldAllowedValues(col).length > 0" @mouseenter="markSelectOverflow">
                                                            <b-form-select
                                                                size="sm"
                                                                :name="col.referenceName"
                                                                :value="getCustomFieldValue(row.node, col.referenceName)"
                                                                class="value-color-select"
                                                                :style="getValueColorSelectStyle(col, getCustomFieldValue(row.node, col.referenceName))"
                                                                :disabled="isReadOnly(row.node)"
                                                                @change="setCustomFieldValue(row.node, col.referenceName, $event)">
                                                                <b-form-select-option value="">-- None --</b-form-select-option>
                                                                <b-form-select-option v-for="val in getCustomFieldAllowedValues(col)" :key="val" :value="val">
                                                                    {{ val }}
                                                                </b-form-select-option>
                                                            </b-form-select>
                                                        </div>
                                                        <b-input-group v-else-if="col.metricSuffix" size="sm">
                                                            <b-form-input
                                                                size="sm"
                                                                :name="col.referenceName"
                                                                :type="isNumericColumn(col) ? 'number' : 'text'"
                                                                :step="isNumericColumn(col) ? '0.5' : null"
                                                                :disabled="isReadOnly(row.node)"
                                                                :value="getCustomFieldValue(row.node, col.referenceName)"
                                                                @keydown="onCustomFieldKeydown($event, col)"
                                                                @input="setCustomFieldValue(row.node, col.referenceName, $event)">
                                                            </b-form-input>
                                                            <b-input-group-append>
                                                                <b-input-group-text class="px-1 extra-small">{{ col.metricSuffix }}</b-input-group-text>
                                                            </b-input-group-append>
                                                        </b-input-group>
                                                        <b-form-input v-else
                                                            size="sm"
                                                            :name="col.referenceName"
                                                            :type="isNumericColumn(col) ? 'number' : 'text'"
                                                            :step="isNumericColumn(col) ? '0.5' : null"
                                                            :disabled="isReadOnly(row.node)"
                                                            :value="getCustomFieldValue(row.node, col.referenceName)"
                                                            @keydown="onCustomFieldKeydown($event, col)"
                                                            @input="setCustomFieldValue(row.node, col.referenceName, $event)">
                                                        </b-form-input>
                                                        </template>
                                                        <div v-else class="text-center py-1 extra-small" :title="`Field not applicable for ${getWorkItemType(row.node) || 'this item'}`">
                                                            <span class="custom-field-na">&mdash;</span>
                                                        </div>
                                                    </b-col>
                                                </template>
                                            </b-row>
                                        </b-form>
                                    </ValidationObserver>
                                                <ValidationObserver v-else :ref="'node-' + row.node.vid"
                                                    v-slot="{ handleSubmit: onSubmitTask }">
                                                    <b-form
                                                        @submit.prevent="onSubmitTask(() => upsertTaskItemAsync(row.node))">
                                                        <b-row align-h="start" class="my-1 time-row" :style="gridRowStyle" data-test="grid-row-task" :data-test-id="row.node.workItemId || row.node.vid">
                                                            <b-col class="px-1 time-col-collapser">
                                                                <b-button v-if="row.hasChildren" variant="secondary" size="sm"
                                                                    @click="toggleNode(row.node)"
                                                                    aria-label="Toggle collapse"
                                                                    title="Toggle Collapse"
                                                                    data-test="btn-toggle-collapse"
                                                                    :data-state="isNodeExpanded(row.node) ? 'expanded' : 'collapsed'"
                                                                    v-b-popover.hover.top.noninteractive="actionHoverPop('Toggle Collapse')">
                                                                    <i class="bi bi-chevron-right row-expand-icon" :class="{ 'is-expanded': isNodeExpanded(row.node) }"></i>
                                                                </b-button>
                                                            </b-col>
                                                            <template v-for="col in visibleGridColumns">
                                                                <!-- Work Item -->
                                                                <b-col v-if="col.referenceName === 'System.WorkItem'" :key="col.referenceName" class="px-1 time-col-id">
                                                                    <nav-link-button v-if="row.node.isSaved()" :href="row.node.url"
                                                                        :id="row.node.workItemId"
                                                                        :type-icon="row.node.workItemTypeIcon"
                                                                        :work-item-type="row.node.workItemType"
                                                                        title="Open task in Azure DevOps" />
                                                                </b-col>
                                                                <!-- Comments -->
                                                                <b-col v-else-if="col.referenceName === 'System.Comments'" :key="col.referenceName" class="px-1 time-col-comments text-center">
                                                                    <b-button
                                                                        v-if="row.node.isSaved()"
                                                                        size="sm"
                                                                        variant="link"
                                                                        class="p-0 wi-comment-btn"
                                                                        :class="{ 'has-comments': (row.node.commentCount || 0) > 0, 'has-mentions': (row.node.mentionCount || 0) > 0 }"
                                                                        aria-label="Open discussion comments"
                                                                        title="Open discussion comments"
                                                                        data-test="btn-open-comments"
                                                                        v-b-popover.hover.top.noninteractive="actionHoverPop(getCommentsTooltip(row.node))"
                                                                        @click="openCommentsModal(row.node)">
                                                                        <i class="bi" :class="(row.node.mentionCount || 0) > 0 ? 'bi-chat-left-dots-fill text-warning' : ((row.node.commentCount || 0) > 0 ? 'bi-chat-left-text-fill text-primary' : 'bi-chat-left text-muted')"></i>
                                                                        <b-badge v-if="(row.node.commentCount || 0) > 0" pill variant="primary" class="ml-1 wi-comment-count">{{ row.node.commentCount }}</b-badge>
                                                                        <b-badge v-if="(row.node.mentionCount || 0) > 0" pill variant="warning" class="ml-1 wi-mention-count" data-test="badge-mention-count">@{{ row.node.mentionCount }}</b-badge>
                                                                    </b-button>
                                                                </b-col>
                                                                <!-- Assigned -->
                                                                <b-col v-else-if="col.referenceName === 'System.AssignedTo'" :key="col.referenceName" class="px-1 time-col-assigned text-center">
                                                                    <div>
                                                                        <b-img class="assigned-to-image"
                                                                            :src="row.node.assignedToSource"
                                                                            v-b-popover.hover.top
                                                                            :title="getAssignedTitle(row.node.assignedToName)"
                                                                            rounded="circle"
                                                                            @error="onAssignedToError"></b-img>
                                                                    </div>
                                                                    <div v-if="(row.node.assignedToDevSource || row.node.assignedToQaSource || row.node.assignedToSaSource)">
                                                                        <b-img v-show="row.node.assignedToDevSource"
                                                                            class="assigned-to-image dev"
                                                                            :src="row.node.assignedToDevSource"
                                                                            v-b-popover.hover.top
                                                                            :title="`Dev: ${row.node.assignedToDevName}`"
                                                                            rounded="circle"></b-img>
                                                                        <b-img v-show="row.node.assignedToQaSource"
                                                                            class="assigned-to-image qa"
                                                                            :src="row.node.assignedToQaSource"
                                                                            v-b-popover.hover.top
                                                                            :title="`QA: ${row.node.assignedToQaName}`"
                                                                            rounded="circle"></b-img>
                                                                        <b-img v-show="row.node.assignedToSaSource"
                                                                            class="assigned-to-image sa"
                                                                            :src="row.node.assignedToSaSource"
                                                                            v-b-popover.hover.top
                                                                            :title="`SA: ${row.node.assignedToSaName}`"
                                                                            rounded="circle"></b-img>
                                                                    </div>
                                                                </b-col>
                                                                <!-- Title -->
                                                                <b-col v-else-if="col.referenceName === 'System.Title'" :key="col.referenceName" class="px-1 time-col-title" :style="indentStyle(row)">
                                                                    <ValidationProvider name="Title"
                                                                        v-slot="{ errors: taskErrors }"
                                                                        :rules="{ required: true }">
                                                                        <grid-title-textarea :id="`tasktitle-${row.node.vid}`"
                                                                            :ref="`textarea-${row.node.vid}`" name="title"
                                                                            v-model="row.node.title"
                                                                            :placeholder="row.node.titleHint"
                                                                            :disabled="isReadOnly(row.node)">
                                                                        </grid-title-textarea>
                                                                        <div
                                                                            v-if="hasNodeAnyAttentionSignal(row.node)"
                                                                            class="hygiene-alert-bar d-flex align-items-center flex-wrap mt-1"
                                                                            :data-hygiene-key="getNodeHygieneAlert(row.node) ? getNodeHygieneAlert(row.node).key : 'stage2'"
                                                                        >
                                                                            <template v-if="getNodeHygieneAlert(row.node)">
                                                                                <span
                                                                                    class="hygiene-badge d-inline-flex align-items-center"
                                                                                    :class="`hygiene-badge-${getNodeHygieneAlert(row.node).variant}`"
                                                                                    :data-test="getNodeHygieneAlert(row.node).badgeTestId"
                                                                                    :title="getNodeHygieneAlert(row.node).tooltip"
                                                                                >
                                                                                    <i :class="[getNodeHygieneAlert(row.node).icon, 'mr-1']"></i>
                                                                                    <span>{{ getNodeHygieneAlert(row.node).label }}</span>
                                                                                </span>
                                                                                <button
                                                                                    v-if="!isReadOnly(row.node)"
                                                                                    type="button"
                                                                                    class="btn btn-xs hygiene-fix-btn d-inline-flex align-items-center ml-1"
                                                                                    :data-test="getNodeHygieneAlert(row.node).btnTestId"
                                                                                    :data-state="isFixingHygiene(row.node) ? 'loading' : 'idle'"
                                                                                    :aria-busy="isFixingHygiene(row.node) ? 'true' : 'false'"
                                                                                    :disabled="isFixingHygiene(row.node)"
                                                                                    :aria-label="getNodeHygieneAlert(row.node).actionTooltip"
                                                                                    :title="getNodeHygieneAlert(row.node).actionTooltip"
                                                                                    @click.stop="applyHygieneFixAsync(row.node, getNodeHygieneAlert(row.node))"
                                                                                >
                                                                                    <b-spinner v-if="isFixingHygiene(row.node)" small class="mr-1"></b-spinner>
                                                                                    <i v-else :class="[getNodeHygieneAlert(row.node).actionIcon, 'mr-1']"></i>
                                                                                    <span>{{ getNodeHygieneAlert(row.node).actionLabel }}</span>
                                                                                </button>
                                                                            </template>
                                                                            <span
                                                                                v-for="s2 in getNodeStage2Badges(row.node)"
                                                                                :key="s2.key"
                                                                                class="hygiene-badge d-inline-flex align-items-center"
                                                                                :class="`hygiene-badge-${s2.variant}`"
                                                                                :data-test="s2.badgeTestId"
                                                                                :title="s2.tooltip"
                                                                            >
                                                                                <i :class="[s2.icon, 'mr-1']"></i>
                                                                                <span>{{ s2.label }}</span>
                                                                            </span>
                                                                        </div>
                                                                        <b-popover :target="`tasktitle-${row.node.vid}`"
                                                                            :show="taskErrors.length > 0 && row.node.isModifying()"
                                                                            variant="danger" placement="left" triggers=""
                                                                            custom-class="validation-pop">
                                                                            {{ taskErrors[0] }}
                                                                        </b-popover>
                                                                    </ValidationProvider>
                                                                </b-col>
                                                                <!-- State -->
                                                                <b-col v-else-if="col.referenceName === 'System.State'" :key="col.referenceName" class="px-1 time-col-state">
                                                                    <label class="mobile-col-caption d-lg-none">State</label>
                                                                    <div @mouseenter="markSelectOverflow">
                                                                        <b-form-select
                                                                            v-model="row.node.state"
                                                                            :options="getAllowedStates(row.node)"
                                                                            size="sm"
                                                                            :title="row.node.state"
                                                                            class="value-color-select"
                                                                            :style="getValueColorSelectStyle(col, row.node.state)"
                                                                            data-test="select-task-state"
                                                                            :disabled="isReadOnly(row.node)"
                                                                            @change="onStateChanged(row.node, $event)">
                                                                        </b-form-select>
                                                                    </div>
                                                                </b-col>
                                                                <!-- Capacity -->
                                                                <b-col v-else-if="col.referenceName === 'System.Capacity'" :key="col.referenceName" class="px-1 time-col-capacity">
                                                                    <label class="mobile-col-caption d-lg-none">Capacity</label>
                                                                    <ValidationProvider name="Capacity"
                                                                        v-slot="{ errors: taskErrors }"
                                                                        :rules="{ required: true, integer: true, min_value: row.node.getFullCompletedHours() }">
                                                                        <b-input-group>
                                                                            <b-form-input :id="`taskcapacity-${row.node.vid}`"
                                                                                name="capacityHours"
                                                                                v-model="row.node.capacityHours" type="number"
                                                                                data-test="input-capacity"
                                                                                :min="row.node.getFullCompletedHours()"
                                                                                :disabled="isReadOnly(row.node) || !row.node.isSaved() || !isCapacityEditable(row.node)">
                                                                            </b-form-input>
                                                                            <b-input-group-append>
                                                                                <b-input-group-text>Hrs</b-input-group-text>
                                                                            </b-input-group-append>
                                                                        </b-input-group>
                                                                        <b-popover :target="`taskcapacity-${row.node.vid}`"
                                                                            :show="taskErrors.length > 0 && row.node.isModifying()"
                                                                            variant="danger" placement="left" triggers=""
                                                                            custom-class="validation-pop">
                                                                            {{ taskErrors[0] }}
                                                                        </b-popover>
                                                                    </ValidationProvider>
                                                                </b-col>
                                                                <!-- Original Estimate -->
                                                                <b-col v-else-if="col.referenceName === 'System.OriginalEstimate'" :key="col.referenceName" class="px-1 time-col-estimate">
                                                                    <label class="mobile-col-caption d-lg-none">Original Estimate</label>
                                                                    <b-input-group>
                                                                        <b-form-input :id="`taskestimate-${row.node.vid}`"
                                                                            name="estimateHours"
                                                                            v-model="row.node.estimateHoursRef" type="number"
                                                                            :disabled="isReadOnly(row.node) || row.node.isSaved()">
                                                                        </b-form-input>
                                                                        <b-input-group-append>
                                                                            <b-input-group-text>Hrs</b-input-group-text>
                                                                        </b-input-group-append>
                                                                    </b-input-group>
                                                                </b-col>
                                                                <!-- Progress -->
                                                                <b-col v-else-if="col.referenceName === 'System.Progress'" :key="col.referenceName" class="px-1 time-col-progress">
                                                                    <label class="mobile-col-caption d-lg-none">Progress</label>
                                                                    <div v-show="row.node.getFullCapacityHours() > 0">
                                                                        <time-progress-item compact spent-label
                                                                            :value="row.node.getFullCompletedHours()"
                                                                            :total="row.node.getFullCapacityHours()"
                                                                            :estimate-value="Number(row.node.estimateHours)"
                                                                            :is-done="isCompletedState(row.node)"
                                                                            period="sprint"
                                                                            :period-start-date="selectedSprintFrom"
                                                                            :period-date="selectedSprintTo"
                                                                            :marker-date="todayDate"
                                                                            :daily-capacity="dailyCapacity">
                                                                        </time-progress-item>
                                                                    </div>
                                                                </b-col>
                                                                <!-- Action -->
                                                                <b-col v-else-if="col.referenceName === 'System.Action'" :key="col.referenceName" class="px-1 time-col-buttons action-buttons">
                                                                    <grid-button-section :buttons="nodeActionButtons(row)"
                                                                        @action="onNodeAction(row, $event)"></grid-button-section>
                                                                </b-col>
                                                                <!-- Custom columns -->
                                                                <b-col v-else :key="col.referenceName" class="px-1 time-col-custom">
                                                                    <label class="mobile-col-caption d-lg-none">{{ col.label }}</label>
                                                                    <template v-if="isColumnSupportedForWorkItem(col, row.node)">
                                                                    <!-- If Activity -->
                                                                    <template v-if="col.referenceName === 'Microsoft.VSTS.Common.Activity'">
                                                                        <div @mouseenter="markSelectOverflow">
                                                                            <b-form-select name="activity"
                                                                                v-model="row.node.activity"
                                                                                class="value-color-select"
                                                                                :style="getValueColorSelectStyle(col, row.node.activity)"
                                                                                :disabled="isReadOnly(row.node)">
                                                                                <b-form-select-option
                                                                                    v-for="act in loadedWorkItemActivities"
                                                                                    :key="act" :value="act">
                                                                                    {{ act }}
                                                                                </b-form-select-option>
                                                                            </b-form-select>
                                                                        </div>
                                                                    </template>
                                                                    <!-- Custom picklist for Task with allowedValues -->
                                                                    <template v-else-if="getCustomFieldAllowedValues(col).length > 0">
                                                                        <div @mouseenter="markSelectOverflow">
                                                                            <b-form-select
                                                                                size="sm"
                                                                                :name="col.referenceName"
                                                                                :value="getCustomFieldValue(row.node, col.referenceName)"
                                                                                class="value-color-select"
                                                                                :style="getValueColorSelectStyle(col, getCustomFieldValue(row.node, col.referenceName))"
                                                                                :disabled="isReadOnly(row.node)"
                                                                                @change="setCustomFieldValue(row.node, col.referenceName, $event)">
                                                                                <b-form-select-option value="">-- None --</b-form-select-option>
                                                                                <b-form-select-option v-for="val in getCustomFieldAllowedValues(col)" :key="val" :value="val">
                                                                                    {{ val }}
                                                                                </b-form-select-option>
                                                                            </b-form-select>
                                                                        </div>
                                                                    </template>
                                                                    <!-- Custom input with metricSuffix -->
                                                                    <template v-else-if="col.metricSuffix">
                                                                        <b-input-group size="sm">
                                                                            <b-form-input
                                                                                size="sm"
                                                                                :name="col.referenceName"
                                                                                :type="isNumericColumn(col) ? 'number' : 'text'"
                                                                                :step="isNumericColumn(col) ? '0.5' : null"
                                                                                :disabled="isReadOnly(row.node)"
                                                                                :value="getCustomFieldValue(row.node, col.referenceName)"
                                                                                @keydown="onCustomFieldKeydown($event, col)"
                                                                                @input="setCustomFieldValue(row.node, col.referenceName, $event)">
                                                                            </b-form-input>
                                                                            <b-input-group-append>
                                                                                <b-input-group-text class="px-1 extra-small">{{ col.metricSuffix }}</b-input-group-text>
                                                                            </b-input-group-append>
                                                                        </b-input-group>
                                                                    </template>
                                                                    <!-- Generic custom field for Task -->
                                                                    <template v-else>
                                                                        <b-form-input
                                                                            size="sm"
                                                                            :name="col.referenceName"
                                                                            :type="isNumericColumn(col) ? 'number' : 'text'"
                                                                            :step="isNumericColumn(col) ? '0.5' : null"
                                                                            :disabled="isReadOnly(row.node)"
                                                                            :value="getCustomFieldValue(row.node, col.referenceName)"
                                                                            @keydown="onCustomFieldKeydown($event, col)"
                                                                            @input="setCustomFieldValue(row.node, col.referenceName, $event)">
                                                                        </b-form-input>
                                                                    </template>
                                                                    </template>
                                                                    <div v-else class="text-center py-1 extra-small" :title="`Field not applicable for ${getWorkItemType(row.node) || 'this item'}`">
                                                                        <span class="custom-field-na">&mdash;</span>
                                                                    </div>
                                                                </b-col>
                                                            </template>
                                                        </b-row>
                                                    </b-form>
                                                </ValidationObserver>
                                    </div>
                                </b-card>
                            </div>

                            <!--No Items within grid-->
                            <div class="my-3 w-100" v-if="!loadingUserStoryItems && !selectedSprint">
                                <grid-empty-state
                                    icon="bi bi-inbox"
                                    title="Select a sprint"
                                    message="Select an active or planned sprint from the picker above to view task items."
                                    :has-active-filters="false"
                                    :actions="[]"
                                />
                            </div>
                            <div class="my-3 w-100" v-else-if="!loadingUserStoryItems && !gridGroups.length">
                                <grid-empty-state
                                    :title="backlogEmptyTitle"
                                    :message="backlogEmptyMessage"
                                    :has-active-filters="isAnyTaskFilterActive"
                                    :suggestions="backlogFilterSuggestions"
                                    :actions="backlogEmptyActions"
                                    @reset="resetToDefaults"
                                    @action="onBacklogEmptyAction"
                                />
                            </div>
                        </div>
                    </div>
                </b-container>
            </b-overlay>
            </template>
            <modal-task-columns
                ref="modalTaskColumns"
                modal-title="Customize Backlog Columns & Hygiene Setup"
                :current-columns="activeGridColumns"
                :process-states="stateOptions"
                :process-info="processInfo"
                :feature-config="backlogFeatureConfig"
                @columns-saved="onColumnsSaved"
                @feature-settings-saved="onHygieneSettingsSaved"
            ></modal-task-columns>
            <modal-work-item-comments ref="workItemCommentsModal" @comment-added="onCommentAdded" @comments-updated="onCommentAdded" />

            <!-- Move Incomplete Work Items to Next Sprint Confirmation Modal -->
            <b-modal
                id="modal-move-next-sprint"
                v-model="showMoveNextSprintModal"
                size="lg"
                centered
                no-close-on-backdrop
                header-class="py-2"
                footer-class="py-2"
                @hidden="moveNextSprintScopeNode = null">
                <template #modal-title>
                    <div class="d-flex align-items-center" data-test="modal-move-next-sprint-header">
                        <i class="bi bi-fast-forward text-info mr-2"></i>
                        <span v-if="moveNextSprintScopeNode">
                            Move <strong>#{{ moveNextSprintScopeNode.workItemId }}</strong> to <strong>{{ nextSprint ? nextSprint.name : 'Next Sprint' }}</strong>
                        </span>
                        <span v-else>
                            Move Incomplete Work Items to <strong>{{ nextSprint ? nextSprint.name : 'Next Sprint' }}</strong>
                        </span>
                    </div>
                </template>

                <div data-test="modal-move-next-sprint">
                    <div v-if="moveNextSprintScopeNode && scopedEligibleChildIds.length > 0"
                         class="parent-move-prompt p-2 mb-2 rounded"
                         data-test="parent-move-children-prompt">
                        <div class="small mb-1">
                            <strong>#{{ moveNextSprintScopeNode.workItemId }} {{ moveNextSprintScopeNode.title }}</strong> has
                            <strong>{{ scopedEligibleChildIds.length }}</strong> incomplete child item(s) in <strong>{{ selectedSprintName }}</strong>.
                            Do you want to move its child items to <strong>{{ nextSprint ? nextSprint.name : 'Next Sprint' }}</strong> as well?
                        </div>
                        <b-form-checkbox
                            :checked="areAllScopedChildrenSelected"
                            data-test="checkbox-include-children"
                            class="font-weight-bold small"
                            @change="toggleIncludeScopedChildren($event)">
                            Also move incomplete child items ({{ scopedEligibleChildIds.length }})
                        </b-form-checkbox>
                    </div>
                    <p v-else class="small text-muted mb-2">
                        Select the incomplete work items from <strong>{{ selectedSprintName }}</strong> to roll over into
                        <strong>{{ nextSprint ? nextSprint.name : '' }}</strong>. Completed and Removed items stay in the current sprint.
                    </p>

                    <div class="d-flex justify-content-between align-items-center mb-2 pb-1 border-bottom">
                        <span class="small font-weight-bold">
                            Selected: {{ moveNextSprintSelectedIds.length }} of {{ modalRolloverTotalCount }} item(s)
                        </span>
                        <div>
                            <b-button size="sm" variant="link" class="p-0 mr-2 text-decoration-none"
                                data-test="btn-select-all-rollover"
                                @click="selectAllRolloverItems">Select All</b-button>
                            <b-button size="sm" variant="link" class="p-0 text-decoration-none text-muted"
                                data-test="btn-clear-all-rollover"
                                @click="clearAllRolloverItems">Clear</b-button>
                        </div>
                    </div>

                    <div class="rollover-items-list" style="max-height: 340px; overflow-y: auto;">
                        <div v-for="group in modalRolloverGroups" :key="group.root.vid" class="mb-2 pb-2 border-bottom">
                            <div class="d-flex align-items-center">
                                <b-form-checkbox
                                    v-if="group.rootEligible"
                                    :checked="moveNextSprintSelectedIds.includes(group.root.workItemId)"
                                    data-test="checkbox-rollover-item"
                                    :data-test-id="group.root.workItemId"
                                    class="mr-2"
                                    @change="toggleRolloverItem(group.root.workItemId, $event)">
                                    <span class="font-weight-bold">#{{ group.root.workItemId }}</span>
                                    <b-badge variant="secondary" class="mx-1">{{ group.root.workItemType || 'Story' }}</b-badge>
                                    <span>{{ group.root.title }}</span>
                                    <b-badge variant="info" class="ml-2">{{ group.root.state }}</b-badge>
                                </b-form-checkbox>
                                <div v-else class="small text-muted font-weight-bold">
                                    #{{ group.root.workItemId }}
                                    <b-badge variant="secondary" class="mx-1">{{ group.root.workItemType || 'Story' }}</b-badge>
                                    {{ group.root.title }}
                                    <b-badge variant="secondary" class="ml-1">{{ group.root.state }}</b-badge>
                                </div>
                            </div>

                            <div v-if="group.children.length > 0" class="pl-4 mt-1">
                                <div v-for="child in group.children" :key="child.vid" class="py-1 d-flex align-items-center justify-content-between">
                                    <b-form-checkbox
                                        :checked="moveNextSprintSelectedIds.includes(child.workItemId)"
                                        data-test="checkbox-rollover-item"
                                        :data-test-id="child.workItemId"
                                        @change="toggleRolloverItem(child.workItemId, $event)">
                                        <span class="font-weight-bold">#{{ child.workItemId }}</span>
                                        <b-badge variant="outline-secondary" class="mx-1">{{ child.workItemType || 'Task' }}</b-badge>
                                        <span>{{ child.title }}</span>
                                        <b-badge variant="info" class="ml-2">{{ child.state }}</b-badge>
                                    </b-form-checkbox>
                                    <span class="extra-small text-muted ml-2 text-nowrap" v-if="child.capacityHours > 0">
                                        {{ child.capacityHours }}h rem
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <template #modal-footer="{ cancel }">
                    <b-button size="sm" variant="outline-secondary" data-test="btn-cancel-move-next-sprint" @click="cancel()" :disabled="isMovingToNextSprint">
                        Cancel
                    </b-button>
                    <b-button
                        v-if="moveNextSprintScopeNode && isNodeEligibleForRollover(moveNextSprintScopeNode) && scopedEligibleChildIds.length > 0"
                        size="sm"
                        variant="outline-info"
                        data-test="btn-move-parent-only"
                        :disabled="isMovingToNextSprint"
                        @click="confirmMoveParentOnlyAsync">
                        Move Parent Only
                    </b-button>
                    <b-button size="sm" variant="info"
                        data-test="btn-confirm-move-next-sprint"
                        :data-state="isMovingToNextSprint ? 'loading' : 'idle'"
                        :disabled="isMovingToNextSprint || moveNextSprintSelectedIds.length === 0"
                        @click="confirmMoveNextSprintAsync">
                        <b-spinner small v-if="isMovingToNextSprint" class="mr-1"></b-spinner>
                        <i v-else class="bi bi-fast-forward mr-1"></i>
                        Move {{ moveNextSprintSelectedIds.length }} Item(s) to {{ nextSprint ? nextSprint.name : 'Next Sprint' }}
                    </b-button>
                </template>
            </b-modal>
        </div>
    </div>
</template>

<script>
import _ from "lodash";
import { v4 as uuidv4 } from "uuid";
import accountMixin from "../mixinAccount";
import ptoMixin from "../mixinPtoSignals";
import toastMixin from "../mixinToast";
import commonMixin from "../mixinCommon";
import eventBus from '../event-bus';
import axios from 'axios';
import PickerSprints from './picker-sprints.vue';
import TimeProgressItem from './time-progress-item.vue';
import GridButtonSection from './grid-button-section.vue';
import GridTitleTextarea from './grid-title-textarea.vue';
import NavLinkButton from './nav-link-button.vue';
import CardMissingSource from './card-missing-source.vue';
import ModalTaskColumns, { DEFAULT_STATE_COLORS, DEFAULT_STATE_VALUES, getStoredStateColors, setStoredStateColors } from './modal-task-columns.vue';
import ModalWorkItemComments from './modal-work-item-comments.vue';
import FilterAuthorDropdown from './filter-author-dropdown.vue';
import GridEmptyState from './grid-empty-state.vue';
import GridHeaderBanner from './grid-header-banner.vue';

const FALLBACK_STATE_OPTIONS = [
    { name: 'New', removed: false },
    { name: 'To Do', removed: false },
    { name: 'Active', removed: false },
    { name: 'In Progress', removed: false },
    { name: 'Resolved', removed: false },
    { name: 'Done', removed: false },
    { name: 'Closed', removed: false },
    { name: 'Removed', removed: true }
];

const DEFAULT_GRID_COLUMNS = [
    { referenceName: 'System.WorkItem', label: 'Work Item', order: 1, isSystem: true, width: '4.375rem', isVisible: true, hidden: false },
    { referenceName: 'System.Comments', label: 'Comments', order: 2, isSystem: true, width: '3.625rem', isVisible: true, hidden: false },
    { referenceName: 'System.AssignedTo', label: 'Assigned', order: 3, isSystem: true, width: '4.375rem', isVisible: true, hidden: false },
    { referenceName: 'System.Title', label: 'Title', order: 4, isSystem: true, width: 'minmax(18rem, 1fr)', isVisible: true, hidden: false },
    {
        referenceName: 'System.State',
        label: 'State',
        order: 5,
        isSystem: true,
        width: '9.25rem',
        isVisible: true,
        hidden: false,
        allowedValues: [...DEFAULT_STATE_VALUES],
        valueColors: { ...DEFAULT_STATE_COLORS }
    },
    { referenceName: 'System.Capacity', label: 'Capacity', order: 6, isSystem: true, width: '6.25rem', isVisible: true, hidden: false },
    { referenceName: 'System.OriginalEstimate', label: 'Original Estimate', order: 7, isSystem: true, width: '6.25rem', isVisible: true, hidden: false },
    { referenceName: 'System.Progress', label: 'Progress', order: 8, isSystem: true, width: '5rem', isVisible: true, hidden: false },
    { referenceName: 'System.Action', label: 'Action', order: 9, isSystem: true, width: '10rem', isVisible: true, hidden: false }
];

export default {
    name: 'page-backlog',
    mixins: [accountMixin, toastMixin, commonMixin, ptoMixin],
    components: {
        'picker-sprints': PickerSprints,
        'time-progress-item': TimeProgressItem,
        'grid-button-section': GridButtonSection,
        'grid-title-textarea': GridTitleTextarea,
        'nav-link-button': NavLinkButton,
        'card-missing-source': CardMissingSource,
        'modal-task-columns': ModalTaskColumns,
        'modal-work-item-comments': ModalWorkItemComments,
        'filter-author-dropdown': FilterAuthorDropdown,
        'grid-empty-state': GridEmptyState,
        'grid-header-banner': GridHeaderBanner
    },
    data() {
        return {
            processInfo: null,
            userStoryItems: [],
            loadedUsSubStates: [],
            loadedUsSubStatesDev: [],
            loadedUsSubStatesQA: [],
            loadedWorkItemActivities: [],
            boolOptions: ["Yes", "No"],
            tasksAbortController: null,
            loadingUserStoryItems: !!this.$store.state.hasBacklogSetup,
            activeGridColumns: [...DEFAULT_GRID_COLUMNS],
            discoveredCustomFields: [],
            loadingGridColumns: false,
            resizingColRef: null,
            selectedSprint: "",
            selectedSprintFrom: null,
            selectedSprintTo: null,
            selectedSprintInfo: null,
            todayDate: new Date(),
            dailyCapacity: 8,
            draftStateFilter: [...(this.$store.state.lastTaskStateFilter || [])],
            stateFilter: [...(this.$store.state.lastTaskStateFilter || [])],
            stateFilterOpen: false,
            showRemovedWorkItems: false,
            selectedAssignees: this.$store.state.lastSwitchedShowAnyAssignedWorkItems ? ['team'] : ['me'],
            appliedAssignees: this.$store.state.lastSwitchedShowAnyAssignedWorkItems ? ['team'] : ['me'],
            showAnyAssignedWorkItems: this.$store.state.lastSwitchedShowAnyAssignedWorkItems,
            isAllCollapsesOpen: this.$store.state.lastToggleAllCollapsesAreOpen,
            expandOverrides: {},
            dynamicMaxHeight: null,
            isTruncated: false,
            isMovingToNextSprint: false,
            showMoveNextSprintModal: false,


            moveNextSprintSelectedIds: [],
            moveNextSprintScopeNode: null,
            showMobileFilters: false,
            backlogViewSearch: '',
            onlyHygieneAlerts: false,
            fixingHygieneIds: {},
            hygieneConfig: this.loadStoredHygieneConfig()
        };
    },
    watch: {
        loadingUserStoryItems(val) {
            if (!val) {
                this.$nextTick(() => {
                    this.computeAvailableHeight();
                });
            }
        },
        userStoryItems: {
            handler() {
                this.fixAllTextAreas();
            },
            deep: true,
            immediate: true // run immediately if items already have data
        },
        stateFilter: {
            handler(newVal) {
                this.$store.commit("setLastTaskStateFilter", newVal);
                this.showRemovedWorkItems = this.includesRemovedState(newVal);
            }
        },
        showRemovedWorkItems: {
            handler(newVal) {
                this.$store.commit("setLastSwitchedShowRemovedWorkItems", newVal);
            }
        },
        showAnyAssignedWorkItems: {
            handler(newVal) {
                this.$store.commit("setLastSwitchedShowAnyAssignedWorkItems", newVal);
            }
        },
        isAllCollapsesOpen: {
            handler(newValue) {
                this.expandOverrides = {};
                this.$store.commit("setLastToggleAllCollapsesAreOpen", newValue);
                if (newValue) {
                    this.fixAllTextAreas();
                }
            }
        },
        effectiveEmployeeRole: {
            async handler(newVal, oldVal) {
                if (newVal === oldVal) return;
                this._processInfoRequest = null;
                this.processInfo = null;
                await this.loadProcessInfoAsync();
                await this.reloadGrid();
            }
        },
        globalProjectId: {
            async handler() {
                this._processInfoRequest = null;
                this.processInfo = null;
                await this.loadProcessInfoAsync();
                await this.reloadGrid();
            }
        }
    },
    computed: {
        effortUnitLabel() {
            if (!this.processInfo) return 'd';
            if (this.processInfo.hasRoleEffortFields) return 'd';
            if (this.processInfo.effortFieldDisplayName === 'Story Points') return 'pts';
            if (this.processInfo.effortFieldDisplayName === 'Days' || this.processInfo.effortFieldDisplayName === 'days') return 'd';
            return this.processInfo.effortFieldDisplayName || 'd';
        },
        stateOptionDetails() {
            const pi = this.processInfo;
            const byType = pi && pi.statesByWorkItemType;
            if (!byType || Object.keys(byType).length === 0) {
                return FALLBACK_STATE_OPTIONS.map(o => ({ ...o, types: [] }));
            }
            const gridTypes = new Set([
                ...(pi.requirementWorkItemTypes || []),
                ...(pi.taskWorkItemTypes || []),
                ...(pi.bugWorkItemTypes || []),
                ...(pi.plannableWorkItemTypes || [])
            ].map(t => (t || '').toLowerCase()));
            let types = Object.keys(byType).filter(t => gridTypes.has(t.toLowerCase()));
            if (types.length === 0) types = Object.keys(byType);

            const rankByCategory = { proposed: 0, inprogress: 1, resolved: 2, completed: 3, removed: 4 };
            const options = new Map();
            types.forEach(type => {
                (byType[type] || []).forEach(state => {
                    const category = (this.stateCategoryOf(type, state) || '').toLowerCase();
                    const rank = category in rankByCategory ? rankByCategory[category] : 2;
                    const entry = options.get(state) || { name: state, rank, removed: false, types: [], order: options.size };
                    entry.rank = Math.min(entry.rank, rank);
                    entry.removed = entry.removed || category === 'removed';
                    entry.types.push(type);
                    options.set(state, entry);
                });
            });
            return [...options.values()].sort((a, b) => a.rank - b.rank || a.order - b.order);
        },
        stateOptions() {
            return this.stateOptionDetails.map(o => o.name);
        },
        removedStateNames() {
            return this.stateOptionDetails.filter(o => o.removed).map(o => o.name);
        },
        groupedStateOptions() {
            const pi = this.processInfo;
            const byType = {};
            if (pi && pi.statesByWorkItemType && Object.keys(pi.statesByWorkItemType).length > 0) {
                Object.assign(byType, pi.statesByWorkItemType);
            }

            const rankByCategory = { proposed: 0, inprogress: 1, resolved: 2, completed: 3, removed: 4 };
            const getRank = (type, state) => {
                const category = (this.stateCategoryOf(type, state) || '').toLowerCase();
                return category in rankByCategory ? rankByCategory[category] : 2;
            };

            const reqTypeSet = new Set((pi && pi.requirementWorkItemTypes || []).map(t => (t || '').toLowerCase()));
            const bugTypeSet = new Set((pi && pi.bugWorkItemTypes || []).map(t => (t || '').toLowerCase()));
            const taskTypeSet = new Set((pi && pi.taskWorkItemTypes || []).map(t => (t || '').toLowerCase()));
            const plannableTypeSet = new Set((pi && pi.plannableWorkItemTypes || []).map(t => (t || '').toLowerCase()));

            if (Object.keys(byType).length === 0) {
                const discovered = {};
                const scan = (nodes) => {
                    (nodes || []).forEach(n => {
                        if (n.workItemType && n.state) {
                            if (!discovered[n.workItemType]) discovered[n.workItemType] = new Set();
                            discovered[n.workItemType].add(n.state);
                        }
                        if (n.children && n.children.length) scan(n.children);
                    });
                };
                scan(this.userStoryItems);
                if (Object.keys(discovered).length > 0) {
                    Object.keys(discovered).forEach(t => {
                        byType[t] = Array.from(discovered[t]);
                    });
                }
            }

            if (Object.keys(byType).length === 0) {
                const reqStates = FALLBACK_STATE_OPTIONS.map(o => o.name);
                const taskStates = ['To Do', 'In Progress', 'Done', 'Removed'];
                return [
                    {
                        key: 'requirements',
                        label: 'User Story / Bug',
                        types: ['User Story', 'Bug'],
                        states: reqStates
                    },
                    {
                        key: 'tasks',
                        label: 'Task',
                        types: ['Task'],
                        states: taskStates
                    }
                ];
            }

            const allTypes = Object.keys(byType);
            const backlogTypes = [];
            const taskTypes = [];
            const otherTypes = [];

            allTypes.forEach(t => {
                const lower = t.toLowerCase();
                const isReq = reqTypeSet.has(lower);
                const isBug = bugTypeSet.has(lower);
                const isTask = taskTypeSet.has(lower);
                const isPlannable = plannableTypeSet.has(lower);

                if (isReq || isBug) {
                    backlogTypes.push(t);
                } else if (isTask || isPlannable) {
                    taskTypes.push(t);
                } else if (/story|pbi|backlog|requirement|bug/i.test(lower)) {
                    backlogTypes.push(t);
                } else if (/task|defect|issue|subtask/i.test(lower)) {
                    taskTypes.push(t);
                } else {
                    otherTypes.push(t);
                }
            });

            const categories = [];
            if (backlogTypes.length > 0) {
                categories.push({ key: 'requirements', types: backlogTypes });
            }
            if (taskTypes.length > 0) {
                categories.push({ key: 'tasks', types: taskTypes });
            }
            if (otherTypes.length > 0) {
                if (categories.length === 0) {
                    otherTypes.forEach((t, i) => categories.push({ key: 'type-' + i, types: [t] }));
                } else {
                    categories.push({ key: 'other', types: otherTypes });
                }
            }

            const groups = [];
            categories.forEach(cat => {
                const types = cat.types;
                const stateMap = new Map();
                types.forEach(t => {
                    (byType[t] || []).forEach(st => {
                        const r = getRank(t, st);
                        if (!stateMap.has(st) || stateMap.get(st) > r) {
                            stateMap.set(st, r);
                        }
                    });
                });

                const sortedStates = Array.from(stateMap.entries())
                    .sort((a, b) => a[1] - b[1])
                    .map(e => e[0]);

                if (sortedStates.length > 0) {
                    groups.push({
                        key: cat.key,
                        label: types.join(' / '),
                        types: [...types],
                        states: sortedStates
                    });
                }
            });

            return groups;
        },
        allDraftStateKeys() {
            const keys = [];
            this.groupedStateOptions.forEach(g => {
                g.states.forEach(s => {
                    keys.push(this.stateKey(g.key, s));
                });
            });
            return keys;
        },
        defaultDraftStateKeys() {
            const keys = [];
            this.groupedStateOptions.forEach(g => {
                g.states.forEach(s => {
                    if (!this.removedStateNames.includes(s)) {
                        keys.push(this.stateKey(g.key, s));
                    }
                });
            });
            return keys;
        },
        defaultStateFilter() {
            return this.defaultDraftStateKeys;
        },
        isProcessFallback() {
            return !!(this.processInfo && this.processInfo.isFallback);
        },
        isStateFilterInvalid() {
            return !this.draftStateFilter || this.draftStateFilter.length === 0;
        },
        stateFilterText() {
            const count = this.draftStateFilter.length;
            if (count === 0) return "Invalid";
            const allKeys = this.allDraftStateKeys;
            const defaultKeys = this.defaultDraftStateKeys;
            const sameAs = (list) => list.length === count && list.every(s => this.draftStateFilter.includes(s));
            if (sameAs(allKeys)) return "All";
            if (sameAs(defaultKeys)) return "All except Removed";
            const names = [];
            this.groupedStateOptions.forEach(g => {
                g.states.forEach(s => {
                    const key = this.stateKey(g.key, s);
                    if (this.draftStateFilter.includes(key) && !names.includes(s)) {
                        names.push(s);
                    }
                });
            });
            return names.length ? names.join(", ") : "None";
        },
        baseVisibleTree() {
            const prune = (node) => {
                const kids = (node.children || []).map(prune).filter(Boolean);
                if (!this.isBaseMatchedNode(node) && kids.length === 0) return null;
                return { node, kids };
            };
            return (this.userStoryItems || []).map(prune).filter(Boolean);
        },
        hygieneAlertsCount() {
            let count = 0;
            const walk = (entry) => {
                if (!entry || !entry.node) return;
                if (this.hasNodeAnyAttentionSignal(entry.node)) {
                    count++;
                }
                (entry.kids || []).forEach(walk);
            };
            (this.baseVisibleTree || []).forEach(walk);
            return count;
        },
        isStateFilterActive() {
            if (!this.stateFilter.length) return false;
            const def = this.defaultDraftStateKeys;
            return this.stateFilter.length !== def.length || !this.stateFilter.every(s => def.includes(s));
        },
        visibleTree() {
            if (!this.onlyHygieneAlerts) {
                return this.baseVisibleTree;
            }
            const pruneHygiene = (entry) => {
                const nodeHasAlert = Boolean(this.hasNodeAnyAttentionSignal(entry.node));
                const filteredKids = (entry.kids || []).map(pruneHygiene).filter(Boolean);
                if (!nodeHasAlert && filteredKids.length === 0) return null;
                return {
                    node: entry.node,
                    kids: nodeHasAlert && filteredKids.length === 0 ? entry.kids : filteredKids
                };
            };
            return (this.baseVisibleTree || []).map(pruneHygiene).filter(Boolean);
        },
        rawMatchedTree() {
            const prune = (node) => {
                const kids = (node.children || []).map(prune).filter(Boolean);
                if (!this.isMatchedNode(node) && kids.length === 0) return null;
                return { node, kids };
            };
            return (this.userStoryItems || []).map(prune).filter(Boolean);
        },
        gridGroups() {
            return this.visibleTree.map(tree => {
                const rows = [];
                const walk = (entry, depth, parent) => {
                    const node = entry.node;
                    rows.push({ key: this.nodeKey(node), node, parent, depth, hasChildren: entry.kids.length > 0 });
                    if (entry.kids.length > 0 && this.isNodeExpanded(node)) {
                        entry.kids.forEach(kid => walk(kid, depth + 1, node));
                    }
                };
                walk(tree, 0, null);
                return { key: this.nodeKey(tree.node), root: tree.node, rows };
            });
        },
        assignedItemsList() {
            const list = [];
            const onlyMe = this.selectedAssignees && this.selectedAssignees.length === 1 && this.selectedAssignees[0] === 'me';
            const walk = (entry) => {
                const node = entry.node;
                const counts = this.isMatchedNode(node) && (!onlyMe || node.isAssignedToMe);
                if (counts && (node.isPlannable || entry.kids.length === 0)) {
                    list.push(node);
                }
                entry.kids.forEach(walk);
            };
            this.visibleTree.forEach(walk);
            return list;
        },
        isAssignedFilterActive() {
            return !this.selectedAssignees || this.selectedAssignees.length !== 1 || this.selectedAssignees[0] !== 'me';
        },
        hasUnappliedFilters() {
            if (!this.areArraysEqual(this.draftStateFilter, this.stateFilter)) return true;
            if (!this.areArraysEqual(this.selectedAssignees, this.appliedAssignees)) return true;
            return false;
        },
        isAnyTaskFilterActive() {
            return this.isStateFilterActive || this.isAssignedFilterActive || this.onlyHygieneAlerts;
        },
        backlogEmptyTitle() {
            if (this.isAnyTaskFilterActive) {
                return 'No work items match your filters';
            }
            return 'No work items found';
        },
        backlogEmptyMessage() {
            if (this.isAnyTaskFilterActive) {
                return 'No work items match the current combination of assignee and state filters for this sprint.';
            }
            return 'There are no committed backlog items assigned to you for the selected sprint.';
        },
        backlogFilterSuggestions() {
            const suggestions = [];
            if (this.isAssignedFilterActive) {
                suggestions.push({
                    text: "Switch Assigned filter to your team or 'All' to view work items across the sprint.",
                    action: 'assignee-all',
                    actionLabel: 'Show All Assigned'
                });
            } else {
                suggestions.push({
                    text: "Switch Assigned filter from 'Me' to your team or 'All' to view work items across the sprint.",
                    action: 'assignee-all',
                    actionLabel: 'Show All Assigned'
                });
            }
            if (this.isStateFilterActive) {
                suggestions.push({
                    text: "Enable all work item states.",
                    action: 'all-states',
                    actionLabel: 'All States'
                });
            }
            if (this.onlyHygieneAlerts) {
                suggestions.push({
                    text: "Turn off the 'Needs Hygiene Fix' filter to view all work items in this sprint.",
                    action: 'clear-hygiene-filter',
                    actionLabel: 'Show All Items'
                });
            }
            suggestions.push("Select a different sprint above to view items in other iterations.");
            return suggestions;
        },
        backlogEmptyActions() {
            const actions = [...(this.baseBacklogEmptyActions || [])];
            if (this.onlyHygieneAlerts) {
                actions.unshift({
                    text: 'Show All Items',
                    icon: 'bi bi-shield-check',
                    action: 'clear-hygiene-filter',
                    variant: 'outline-secondary'
                });
            }
            return actions;
        },
        baseBacklogEmptyActions() {
            const actions = [];
            if (this.isAssignedFilterActive) {
                actions.push({
                    text: 'Show All Assigned',
                    icon: 'bi bi-people',
                    action: 'assignee-all',
                    variant: 'outline-secondary'
                });
            }
            if (this.isStateFilterActive) {
                actions.push({
                    text: 'All States',
                    icon: 'bi bi-collection',
                    action: 'all-states',
                    variant: 'outline-secondary'
                });
            }
            return actions;
        },
        activeProject() {
            return this.globalProject;
        },
        projectResolved() {
            return !!this.globalProject;
        },
        hasBacklogSource() {
            const proj = this.globalProject;
            return !!proj && !!proj.backlogLinkId && !!proj.backlogProjectId;
        },
        isMissingBacklogVisible() {
            if (!this.isAuthenticated) return false;
            return !this.hasBacklogSource;
        },
        backlogFeatureConfig() {
            const cfg = this.hygieneConfig || {};
            return {
                title: 'Backlog Hygiene Signals (State vs. Reality)',
                subtitle: 'Automatically detect mismatches between logged hours, child task completion, and work item state, and offer 1-click inline fixes.',
                icon: 'bi bi-shield-exclamation',
                masterLabel: 'Enable Backlog Hygiene',
                masterTestId: 'toggle-hygiene-master',
                storageKey: 'vt_backlog_hygiene_config',
                enabled: cfg.enabled !== false,
                items: [
                    {
                        key: 'loggedOnNew',
                        label: 'Logged Hours on Proposed / New Items',
                        badgeText: '4h logged while To Do',
                        badgeVariant: 'amber',
                        badgeIcon: 'bi bi-exclamation-triangle-fill',
                        description: 'Flags items with > 0h completed work that are still in New, To Do, or Proposed state, with a 1-click move to In Progress.',
                        testId: 'toggle-hygiene-logged-on-new',
                        enabled: cfg.loggedOnNew !== false
                    },
                    {
                        key: 'zeroRemaining',
                        label: 'Active Task with 0h Remaining',
                        badgeText: '0h remaining — Ready to close?',
                        badgeVariant: 'emerald',
                        badgeIcon: 'bi bi-check2-circle',
                        description: 'Flags active tasks that have logged work and 0h remaining capacity, offering a 1-click Mark Done action.',
                        testId: 'toggle-hygiene-zero-remaining',
                        enabled: cfg.zeroRemaining !== false
                    },
                    {
                        key: 'closedWithRemaining',
                        label: 'Completed Task with Remaining Hours',
                        badgeText: 'Done with 2h remaining',
                        badgeVariant: 'rose',
                        badgeIcon: 'bi bi-fire',
                        description: 'Flags Closed or Done tasks that still carry > 0h remaining work (distorting burndown), offering a 1-click Zero Remaining fix.',
                        testId: 'toggle-hygiene-closed-with-remaining',
                        enabled: cfg.closedWithRemaining !== false
                    },
                    {
                        key: 'parentReadyToClose',
                        label: 'All Child Tasks Done, Parent Still Open',
                        badgeText: 'All child tasks done — Close story?',
                        badgeVariant: 'emerald',
                        badgeIcon: 'bi bi-check2-all',
                        description: 'Flags open User Stories, Bugs, or PBIs where 100% of active child tasks are completed, offering a 1-click close action.',
                        testId: 'toggle-hygiene-parent-ready-to-close',
                        enabled: cfg.parentReadyToClose !== false
                    },
                    {
                        key: 'sprintRollover',
                        label: 'Sprint Carry-Over Debt ("Rolled Over x2")',
                        badgeText: 'Rolled over x2',
                        badgeVariant: 'violet',
                        badgeIcon: 'bi bi-arrow-repeat',
                        description: 'Flags work items moved across sprint iterations one or more times, showing the carry-over count and previous sprint.',
                        testId: 'toggle-hygiene-sprint-rollover',
                        enabled: cfg.sprintRollover !== false
                    },
                    {
                        key: 'addedMidSprint',
                        label: 'Added Mid-Sprint Scope Creep',
                        badgeText: 'Added Mid-Sprint',
                        badgeVariant: 'amber',
                        badgeIcon: 'bi bi-calendar-plus',
                        description: 'Flags work items added to the sprint after the 24-hour planning window, and surfaces a scope-creep counter in the sprint capacity bar.',
                        testId: 'toggle-hygiene-added-mid-sprint',
                        enabled: cfg.addedMidSprint !== false
                    },
                    {
                        key: 'blockedWorkItems',
                        label: 'Blocked Work Items & Unresolved Predecessors',
                        badgeText: 'Blocked by #105 (In Progress)',
                        badgeVariant: 'rose',
                        badgeIcon: 'bi bi-octagon-fill',
                        description: 'Flags work items marked Blocked, tagged Blocked, or waiting on unresolved Predecessor links.',
                        testId: 'toggle-hygiene-blocked-work-items',
                        enabled: cfg.blockedWorkItems !== false
                    },
                    {
                        key: 'prMergedWorkItemOpen',
                        label: 'PR Merged — Work Item Still Open',
                        badgeText: 'PR Merged — Ready to Resolve/Close?',
                        badgeVariant: 'violet',
                        badgeIcon: 'bi bi-git',
                        description: 'Flags New / Active work items whose linked pull requests are all merged, with a 1-click Resolve / Close action.',
                        testId: 'toggle-hygiene-pr-merged-work-item-open',
                        enabled: cfg.prMergedWorkItemOpen !== false
                    },
                    {
                        key: 'assigneeOnPto',
                        label: 'Assignee on PTO / Planned Absence',
                        badgeText: 'Assignee on PTO',
                        badgeVariant: 'amber',
                        badgeIcon: 'bi bi-airplane',
                        description: 'Flags open work items whose assignee has planned absence (vacation, sick leave, holiday) during the rest of the sprint, so work can be reassigned in time. Requires the Team plan.',
                        testId: 'toggle-hygiene-assignee-on-pto',
                        enabled: cfg.assigneeOnPto !== false
                    }
                ]
            };
        },
        visibleGridColumns() {
            return (this.activeGridColumns || []).filter(c => !c.hidden && c.isVisible !== false);
        },
        gridRowStyle() {

            const colTracks = (this.visibleGridColumns || []).map(col => {
                const customPx = col && col.width && /^\d+(\.\d+)?px$/i.test(String(col.width).trim()) ? String(col.width).trim() : '';
                if (customPx) return customPx;
                switch (col.referenceName) {
                    case 'System.WorkItem': return '5.75rem';
                    case 'System.Comments': return '3.75rem';
                    case 'System.AssignedTo': return '4.75rem';
                    case 'System.Title': return 'minmax(18rem, 1fr)';
                    case 'System.State': return '9rem';
                    case 'System.Capacity': return '5.75rem';
                    case 'System.OriginalEstimate': return '5.75rem';
                    case 'System.Progress': return '8.5rem';
                    case 'System.Action': return '4.5rem';
                    default: return (this.isNumericColumn(col) || col.metricSuffix || this.columnFieldType(col) === 'boolean') ? '7.5rem' : '9.5rem';
                }
            }).join(' ');

            return {
                gridTemplateColumns: `2rem ${colTracks}`
            };
        },
        isServiceUnavailable() {
            return !!this.$store.state.isServiceUnavailable;
        },
        isGridVisible() {
            if (this.isServiceUnavailable) return false;
            return this.projectResolved && this.hasBacklogSource;
        },
        selectedSprintName() {
            if (this.selectedSprintInfo && this.selectedSprintInfo.name) {
                return this.selectedSprintInfo.name;
            }
            if (this.selectedSprint) {
                const parts = this.selectedSprint.split('\\');
                return parts[parts.length - 1];
            }
            return '';
        },
        nextSprint() {
            if (!this.selectedSprint) return null;
            const sprints = Array.isArray(this.$store.state.sprints) ? [...this.$store.state.sprints] : [];
            if (sprints.length < 2) return null;
            sprints.sort((a, b) => {
                const tA = a && a.from ? new Date(a.from).getTime() : 0;
                const tB = b && b.from ? new Date(b.from).getTime() : 0;
                if (tA !== tB) return tA - tB;
                return (a.position || 0) - (b.position || 0);
            });
            const norm = (p) => String(p || '').toLowerCase().trim();
            const curPath = norm(this.selectedSprint);
            const curName = norm(this.selectedSprintName);
            let idx = sprints.findIndex(s => norm(s.path) === curPath);
            if (idx === -1 && curName) {
                idx = sprints.findIndex(s => norm(s.name) === curName || norm(s.path).endsWith('\\' + curName));
            }
            if (idx >= 0 && idx + 1 < sprints.length) {
                const candidate = sprints[idx + 1];
                if (candidate && !candidate.isLocked && candidate.path) {
                    return candidate;
                }
            }
            return null;
        },
        incompleteRolloverGroups() {
            const groups = [];
            (this.visibleTree || []).forEach(entry => {
                const root = entry.node;
                const rootEligible = this.isNodeEligibleForRollover(root);
                const children = (entry.kids || [])
                    .map(k => k.node)
                    .filter(ch => this.isNodeEligibleForRollover(ch));
                if (rootEligible || children.length > 0) {
                    groups.push({ root, rootEligible, children });
                }
            });
            return groups;
        },
        incompleteRolloverAllIds() {
            const ids = [];
            this.incompleteRolloverGroups.forEach(g => {
                if (g.rootEligible && g.root.workItemId) {
                    ids.push(g.root.workItemId);
                }
                g.children.forEach(ch => {
                    if (ch.workItemId) ids.push(ch.workItemId);
                });
            });
            return _.uniq(ids);
        },
        incompleteRolloverTotalCount() {
            return this.incompleteRolloverAllIds.length;
        },
        modalRolloverGroups() {
            if (this.moveNextSprintScopeNode) {
                const root = this.moveNextSprintScopeNode;
                const rootEligible = this.isNodeEligibleForRollover(root);
                const children = (root.children || []).filter(ch => this.isNodeEligibleForRollover(ch));
                return (rootEligible || children.length > 0) ? [{ root, rootEligible, children }] : [];
            }
            return this.incompleteRolloverGroups;
        },
        modalRolloverAllIds() {
            const ids = [];
            this.modalRolloverGroups.forEach(g => {
                if (g.rootEligible && g.root.workItemId) {
                    ids.push(g.root.workItemId);
                }
                g.children.forEach(ch => {
                    if (ch.workItemId) ids.push(ch.workItemId);
                });
            });
            return _.uniq(ids);
        },
        modalRolloverTotalCount() {
            return this.modalRolloverAllIds.length;
        },
        scopedEligibleChildIds() {
            if (!this.moveNextSprintScopeNode || !Array.isArray(this.moveNextSprintScopeNode.children)) return [];
            return this.moveNextSprintScopeNode.children
                .filter(ch => this.isNodeEligibleForRollover(ch) && ch.workItemId)
                .map(ch => ch.workItemId);
        },
        areAllScopedChildrenSelected() {
            if (this.scopedEligibleChildIds.length === 0) return false;
            return this.scopedEligibleChildIds.every(id => this.moveNextSprintSelectedIds.includes(id));
        },
        parentActionButtons() {
            if (this.isViewOnlyMode || !this.selectedSprint || this.loadingUserStoryItems) {
                return [];
            }
            const hasUnsavedParent = (this.userStoryItems || []).some(item => item && typeof item.isSaved === 'function' && !item.isSaved());
            const parentItems = this.getCreatableParentTypes().map(type => ({
                key: 'new-parent:' + type,
                icon: 'bi bi-clipboard-plus',
                variant: 'info',
                title: 'New ' + type,
                ariaLabel: 'Add ' + type,
                disabled: hasUnsavedParent,
                dataTest: 'btn-add-parent-' + type.toLowerCase().replace(/[^a-z0-9]+/g, '-')
            }));
            if (this.nextSprint && this.incompleteRolloverTotalCount > 0) {
                parentItems.push({
                    key: 'move-incomplete-next-sprint',
                    icon: 'bi bi-fast-forward',
                    variant: 'outline-info',
                    title: `Move ${this.incompleteRolloverTotalCount} incomplete item(s) to ${this.nextSprint.name}`,
                    ariaLabel: `Move ${this.incompleteRolloverTotalCount} incomplete item(s) to ${this.nextSprint.name}`,
                    disabled: Boolean(this.loadingUserStoryItems || this.isMovingToNextSprint),
                    dataTest: 'btn-move-incomplete-next-sprint'
                });
            }
            return parentItems || this.getCreatableParentTypes().map(type => ({
                key: 'new-parent:' + type,
                icon: 'bi bi-clipboard-plus',
                variant: 'info',
                title: 'New ' + type,
                ariaLabel: 'Add ' + type,
                disabled: hasUnsavedParent,
                dataTest: 'btn-add-parent-' + type.toLowerCase().replace(/[^a-z0-9]+/g, '-')
            }));
        },
        sprintWorkingDays() {
            if (!this.selectedSprintFrom || !this.selectedSprintTo) return 10;
            const start = new Date(this.selectedSprintFrom);
            const end = new Date(this.selectedSprintTo);
            if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return 10;

            let days = 0;
            const cur = new Date(start);
            while (cur <= end) {
                const dayOfWeek = cur.getDay();
                if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                    days++;
                }
                cur.setDate(cur.getDate() + 1);
            }
            return days > 0 ? days : 1;
        },
        sprintDateRangeLabel() {
            if (!this.selectedSprintFrom || !this.selectedSprintTo) return '';
            try {
                const start = new Date(this.selectedSprintFrom);
                const end = new Date(this.selectedSprintTo);
                if (isNaN(start.getTime()) || isNaN(end.getTime())) return '';
                const fmt = d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                return `${fmt(start)} - ${fmt(end)}`;
            } catch {
                return '';
            }
        },
        availableSprintCapacity() {
            return this.sprintWorkingDays * (this.dailyCapacity || 8);
        },
        assignedTasksCount() {
            return this.assignedItemsList.length;
        },
        assignedSprintCapacity() {
            return this.assignedItemsList.reduce((acc, item) => {
                const cap = Number(item.capacityHours || item.estimateHours || item.effort || 0);
                return acc + (isNaN(cap) ? 0 : cap);
            }, 0);
        },
        loggedSprintHours() {
            return this.assignedItemsList.reduce((acc, item) => {
                let logged = 0;
                if (typeof item.getFullCompletedHours === 'function') {
                    logged = item.getFullCompletedHours();
                } else if (item.completedHours !== undefined) {
                    logged = Number(item.completedHours) || 0;
                }
                return acc + (isNaN(logged) ? 0 : logged);
            }, 0);
        },
        sprintCapacityVariance() {
            return this.assignedSprintCapacity - this.availableSprintCapacity;
        },
        openSprintCapacity() {
            return Math.max(0, this.availableSprintCapacity - this.assignedSprintCapacity);
        },
        assignedSprintCapacityPercent() {
            if (this.availableSprintCapacity <= 0) return 0;
            return (this.assignedSprintCapacity / this.availableSprintCapacity) * 100;
        },
        openSprintCapacityPercent() {
            if (this.availableSprintCapacity <= 0) return 0;
            return (this.openSprintCapacity / this.availableSprintCapacity) * 100;
        },
        sprintProgressMax() {
            return Math.max(this.availableSprintCapacity, this.assignedSprintCapacity) || 1;
        },
        midSprintAddedSummary() {
            const cfg = this.hygieneConfig || {};
            if (cfg.enabled === false || cfg.addedMidSprint === false) {
                return { count: 0, hours: 0, tooltip: '' };
            }
            let count = 0;
            let hours = 0;
            const walk = (entry) => {
                if (!entry || !entry.node) return;
                const n = entry.node;
                if (!this.isOutOfSprintParent(n) && n.isAddedMidSprint && this.stateCategory(n) !== 'Removed') {
                    count++;
                    if (n.isPlannable || !entry.kids || entry.kids.length === 0) {
                        const h = Number(n.capacityHours || n.estimateHours || n.effort || 0);
                        if (!isNaN(h) && h > 0) hours += h;
                    }
                }
                (entry.kids || []).forEach(walk);
            };
            (this.baseVisibleTree || []).forEach(walk);
            const tooltip = count > 0
                ? `${count} work item${count === 1 ? '' : 's'} (${hours.toFixed(1)}h) added to this sprint after the initial 24h planning window.`
                : '';
            return { count, hours, tooltip };
        },
        sprintCapacityTooltip() {
            return `Capacity: ${this.availableSprintCapacity.toFixed(1)}h | Assigned: ${this.assignedSprintCapacity.toFixed(1)}h (Logged: ${this.loggedSprintHours.toFixed(1)}h) | Open: ${this.openSprintCapacity.toFixed(1)}h (${this.openSprintCapacityPercent.toFixed(0)}%)`;
        },
        sprintCapacityStatus() {
            const available = this.availableSprintCapacity;
            const assigned = this.assignedSprintCapacity;
            const count = this.assignedTasksCount;

            if (count === 0 || assigned === 0) {
                return {
                    variantClass: 'status-neutral',
                    icon: 'bi bi-dash-circle',
                    label: 'No Allocation',
                    text: `${available.toFixed(1)}h open`
                };
            }

            const pct = available > 0 ? (assigned / available) * 100 : 0;
            const diff = Math.abs(this.sprintCapacityVariance);

            if (assigned > available) {
                return {
                    variantClass: 'status-overspend',
                    icon: 'bi bi-exclamation-triangle-fill',
                    label: 'Over-Allocated',
                    text: `+${diff.toFixed(1)}h over capacity`
                };
            } else if (pct >= 80 && pct <= 100) {
                return {
                    variantClass: 'status-met',
                    icon: 'bi bi-check-circle-fill',
                    label: pct >= 99 ? 'Capacity Met' : 'Optimal Capacity',
                    text: diff > 0 ? `${diff.toFixed(1)}h open` : 'Fully Allocated'
                };
            } else {
                return {
                    variantClass: 'status-info',
                    icon: 'bi bi-info-circle-fill',
                    label: 'Open Capacity',
                    text: `${diff.toFixed(1)}h`
                };
            }
        },
        backlogStats() {
            const allNodes = [];
            const walk = (node) => {
                if (!node) return;
                if (!this.isOutOfSprintParent(node)) {
                    allNodes.push(node);
                }
                if (node.children && node.children.length) {
                    node.children.forEach(walk);
                }
            };
            (this.userStoryItems || []).forEach(walk);

            const getCategory = (item) => {
                const cat = (this.stateCategory(item) || '').toLowerCase();
                if (cat) return cat;
                const state = (item.state || '').toLowerCase();
                if (/inprogress|active|doing|in development|started/i.test(state)) return 'inprogress';
                if (/proposed|to do|new|open|ready/i.test(state)) return 'proposed';
                if (/resolved|completed|done|closed/i.test(state)) return 'completed';
                if (/removed|cut/i.test(state)) return 'removed';
                return 'proposed';
            };

            const inProgressCount = allNodes.filter(n => getCategory(n) === 'inprogress').length;
            const toDoCount = allNodes.filter(n => getCategory(n) === 'proposed').length;
            const doneCount = allNodes.filter(n => ['resolved', 'completed'].includes(getCategory(n))).length;
            const removedCount = allNodes.filter(n => getCategory(n) === 'removed').length;
            const totalCount = allNodes.length;

            const stats = [
                {
                    key: 'in-progress',
                    label: 'In Progress',
                    count: inProgressCount,
                    icon: 'bi bi-play-circle',
                    variant: 'amber',
                    testId: 'stat-backlog-in-progress',
                    showZero: true,
                    tooltip: 'Work items actively in progress'
                },
                {
                    key: 'to-do',
                    label: 'To Do',
                    count: toDoCount,
                    icon: 'bi bi-list-task',
                    variant: 'slate',
                    testId: 'stat-backlog-todo',
                    showZero: true,
                    tooltip: 'Work items pending to be started'
                },
                {
                    key: 'done',
                    label: 'Done',
                    count: doneCount,
                    icon: 'bi bi-check2-circle',
                    variant: 'emerald',
                    testId: 'stat-backlog-done',
                    showZero: true,
                    tooltip: 'Work items resolved or closed'
                },
                {
                    key: 'total',
                    label: 'Total Items',
                    count: totalCount,
                    icon: 'bi bi-kanban',
                    variant: 'violet',
                    testId: 'stat-backlog-total',
                    showZero: true,
                    tooltip: 'Total work items in scope for this sprint'
                }
            ];

            if (removedCount > 0 && this.showRemovedWorkItems) {
                stats.push({
                    key: 'removed',
                    label: 'Removed',
                    count: removedCount,
                    icon: 'bi bi-trash3',
                    variant: 'rose',
                    testId: 'stat-backlog-removed',
                    tooltip: 'Work items marked as removed'
                });
            }

            return stats;
        },
        scrollStyle() {
            return this.dynamicMaxHeight
                ? { maxHeight: this.dynamicMaxHeight }
                : {};
        }
    },
    mounted() {
        this.initAutoFit();
    },
    beforeUnmount() {
        this.cleanupAutoFit();
        if (this.tasksAbortController) {
            this.tasksAbortController.abort();
            this.tasksAbortController = null;
        }
        if (this.onServiceReconnected) {
            eventBus.$off('service-reconnected', this.onServiceReconnected);
        }
    },
    async created() {
        if (!this.isAuthenticated)
            return;

        this.onServiceReconnected = () => {
            if (this.hasBacklogSource && (!this.userStoryItems || this.userStoryItems.length === 0)) {
                this.reloadGrid();
            }
        };
        eventBus.$on('service-reconnected', this.onServiceReconnected);

        const initColsPromise = this.loadConfiguredColumns();
        if (this.hasBacklogSetup) {
            this.loadProcessInfoAsync();
        }
        await initColsPromise;

        if (!this.hasBacklogSetup) {
            return;
        }

        await this.loadProcessInfoAsync();
        await this.enrichColumnsWithAllowedValues();

        try {
            const resp = await this.$http.get("/Common/ListMetadata");
            this.loadedWorkItemActivities = resp.data.workItemActivities;
            this.dailyCapacity = resp.data.dailyCapacity;
        } catch (metaErr) {
            console.warn("Could not load common metadata in Backlog:", metaErr);
        }
    },
    methods: {
        initAutoFit() {
            this.onResizeThrottled = () => {
                if (this.resizeRaf) {
                    cancelAnimationFrame(this.resizeRaf);
                }
                this.resizeRaf = requestAnimationFrame(() => {
                    this.computeAvailableHeight();
                });
            };
            window.addEventListener('resize', this.onResizeThrottled, { passive: true });
            window.addEventListener('orientationchange', this.onResizeThrottled, { passive: true });

            if (typeof ResizeObserver !== 'undefined') {
                this.resizeObserver = new ResizeObserver(() => {
                    this.onResizeThrottled();
                });
                const parent = this.$el ? this.$el.parentElement : null;
                if (parent) {
                    this.resizeObserver.observe(parent);
                }
            }

            this.$nextTick(() => {
                this.computeAvailableHeight();
            });
        },
        cleanupAutoFit() {
            if (this.onResizeThrottled) {
                window.removeEventListener('resize', this.onResizeThrottled);
                window.removeEventListener('orientationchange', this.onResizeThrottled);
                this.onResizeThrottled = null;
            }
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
                this.resizeObserver = null;
            }
            if (this.resizeRaf) {
                cancelAnimationFrame(this.resizeRaf);
                this.resizeRaf = null;
            }
        },
        computeAvailableHeight() {
            const el = this.$refs.scrollContainer;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            if (rect.height === 0 && rect.top === 0) return;

            const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
            const available = viewportHeight - rect.top - 20;
            const min = 260;
            this.dynamicMaxHeight = `${Math.max(min, Math.floor(available))}px`;
        },
        onCustomFieldKeydown(event, col) {
            if (!this.isNumericColumn(col)) return;
            const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', '.', '-'];
            if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) return;
            if (!/^[0-9]$/.test(event.key)) {
                event.preventDefault();
            }
        },
        openCommentsModal(item) {
            if (!item || !item.workItemId || !this.$refs.workItemCommentsModal) return;
            this.$refs.workItemCommentsModal.open(item);
        },
        onCommentAdded({ workItemId, commentCount, mentionCount, newCount }) {
            const count = commentCount !== undefined ? commentCount : newCount;
            const findAndPatch = (items) => {
                if (!items) return false;
                for (const it of items) {
                    if (it.workItemId == workItemId) {
                        this.$set(it, 'commentCount', count);
                        if (mentionCount !== undefined) {
                            this.$set(it, 'mentionCount', mentionCount);
                        }
                        return true;
                    }
                    if (it.children && findAndPatch(it.children)) {
                        return true;
                    }
                    if (it.taskItems && findAndPatch(it.taskItems)) {
                        return true;
                    }
                }
                return false;
            };
            findAndPatch(this.userStoryItems);
        },
        stateKey(groupKey, state) {
            return `${groupKey}::${state}`;
        },
        isDraftStateChecked(group, opt) {
            return this.draftStateFilter.includes(this.stateKey(group.key, opt));
        },
        isDraftStateDisabled(group, opt) {
            return this.draftStateFilter.length <= 1 && this.isDraftStateChecked(group, opt);
        },
        toggleDraftGroupState(group, opt, isChecked) {
            const key = this.stateKey(group.key, opt);
            if (isChecked) {
                if (!this.draftStateFilter.includes(key)) {
                    this.draftStateFilter = [...this.draftStateFilter, key];
                }
            } else {
                if (this.draftStateFilter.length <= 1) return;
                this.draftStateFilter = this.draftStateFilter.filter(s => s !== key);
            }
        },
        selectAllStates() {
            this.draftStateFilter = [...this.allDraftStateKeys];
        },
        selectAllStatesInGroup(group) {
            const groupKeys = group.states.map(s => this.stateKey(group.key, s));
            const otherKeys = this.draftStateFilter.filter(k => !k.startsWith(group.key + '::'));
            this.draftStateFilter = [...otherKeys, ...groupKeys];
        },
        toggleDraftState(opt, isChecked) {
            const matchedGroups = this.groupedStateOptions.filter(g => g.states.includes(opt));
            matchedGroups.forEach(g => this.toggleDraftGroupState(g, opt, isChecked));
        },
        resetToDefaults() {
            this.resetAllTaskFilters();
            this.reloadGrid();
        },
        onBacklogEmptyAction(actionKey) {
            if (actionKey === 'clear-hygiene-filter') {
                this.onlyHygieneAlerts = false;
                return;
            }
            if (actionKey === 'assignee-all') {
                this.selectedAssignees = ['all'];
                this.reloadGrid();
            } else if (actionKey === 'all-states') {
                this.selectAllStates();
                this.reloadGrid();
            }
        },
        areArraysEqual(a, b) {
            if (!a && !b) return true;
            if (!a || !b) return false;
            if (a.length !== b.length) return false;
            const sortedA = [...a].sort();
            const sortedB = [...b].sort();
            return sortedA.every((v, i) => v === sortedB[i]);
        },
        pinStateFilter(event) {
            if (this.stateFilterOpen) return;
            this.pinStateFilterEl(event.currentTarget);
        },
        unpinStateFilter(event) {
            if (this.stateFilterOpen) return;
            this.unpinStateFilterEl(event.currentTarget);
        },
        onStateFilterShown() {
            this.stateFilterOpen = true;
            this.pinStateFilterEl();
        },
        onStateFilterHidden() {
            this.stateFilterOpen = false;
            this.unpinStateFilterEl();
        },
        pinStateFilterEl(wrapArg) {
            const wrap = wrapArg instanceof Element ? wrapArg : this.$el.querySelector(".time-status-filter-wrap");
            const toggle = wrap ? wrap.querySelector(".dropdown-toggle") : null;
            if (!toggle) return;
            const rect = toggle.getBoundingClientRect();
            toggle.classList.add("is-pinned");
            toggle.style.top = `${rect.top}px`;
            toggle.style.left = `${rect.left}px`;
        },
        unpinStateFilterEl(wrapArg) {
            const wrap = wrapArg instanceof Element ? wrapArg : this.$el.querySelector(".time-status-filter-wrap");
            const toggle = wrap ? wrap.querySelector(".dropdown-toggle") : null;
            if (!toggle) return;
            toggle.classList.remove("is-pinned");
            toggle.style.top = "";
            toggle.style.left = "";
        },
        onStateChanged() {
            this.$nextTick(() => {
                this.markSelectOverflow();
            });
        },
        resizeTextArea(vid) {
            const ref = this.$refs['textarea-' + vid];
            const comp = Array.isArray(ref) ? ref[0] : ref;
            if (comp && typeof comp.resize === 'function') {
                comp.resize();
            }
        },
        toggleCollapseAll: function () {
            this.isAllCollapsesOpen = !this.isAllCollapsesOpen;
        },
        markSelectOverflow(event) {
            if (!event) {
                if (this.$el) {
                    const allSelects = this.$el.querySelectorAll('.time-col-state select, .time-col-custom select');
                    allSelects.forEach(selectEl => this.checkSingleSelectOverflow(selectEl));
                }
                return;
            }
            const target = event.currentTarget || event.target || event;
            const el = target ? (target.tagName === "SELECT" ? target : (typeof target.querySelector === 'function' ? target.querySelector("select") : null)) : null;
            if (!el) return;
            this.checkSingleSelectOverflow(el, target);
        },
        checkSingleSelectOverflow(el, target = null) {
            if (!el || typeof window === 'undefined') return;
            const wrapper = el.parentElement || target;
            if (!wrapper) return;
            const style = window.getComputedStyle(el);
            const paddingLeft = parseFloat(style.paddingLeft) || 0;
            const paddingRight = parseFloat(style.paddingRight) || 0;
            const arrowWidth = 18;
            const available = wrapper.clientWidth - paddingLeft - paddingRight - arrowWidth;
            if (available <= 0) return;
            const canvas = this._textMeasureCanvas || (this._textMeasureCanvas = document.createElement("canvas"));
            const context = canvas.getContext("2d");
            context.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
            const selectedText = el.options && el.selectedIndex >= 0 ? el.options[el.selectedIndex].text : "";
            const textWidth = context.measureText(selectedText).width;
            el.classList.toggle("is-overflowing", textWidth > available);
        },
        getUserStoryItemInstance() {
            let component = this;
            let item = {
                vid: uuidv4(),
                workItemId: null,
                url: "#",
                title: "",
                titleHint: "",
                effort: 0,
                commentCount: 0,
                customFields: {},
                isAssignedToMe: true,
                isPlannable: false,
                sprintRolloverCount: 0,
                previousIterationName: null,
                isAddedMidSprint: false,
                addedToSprintDate: null,
                isBlocked: false,
                blockedReason: null,
                unresolvedPredecessors: [],
                isPrMergedWorkItemOpen: false,
                mergedPullRequestIds: [],
                children: [],
                getFullCompletedHours: function () {
                    return _.sumBy(this.children, (ch) => typeof ch.getFullCompletedHours === 'function' ? ch.getFullCompletedHours() : parseInt(ch.totalHours ?? 0));
                },
                getFullCapacityHours: function () {
                    return parseFloat(this.effort ?? 0).toFixed(2) * component.dailyCapacity;
                },
                getFullSparedHours: function () {
                    const spared = this.getFullCapacityHours() - this.getFullCompletedHours();
                    return spared > 0 ? spared : 0;
                },
                getFullExceededHours: function () {
                    const exceeded = this.getFullCompletedHours() - this.getFullCapacityHours();
                    return exceeded > 0 ? exceeded : 0;
                },
                getProgressStyle: function () {
                    const completed = this.getFullCompletedHours();
                    const max = this.getFullCapacityHours();
                    if (completed < max)
                        return "secondary";
                    if (completed === max)
                        return "success";
                    if (completed > max)
                        return "danger";
                },
                getEstimateLineValue: function () {
                    return (this.getFullCapacityHours() / 100) * 2; //2% of total
                },
                getProgressPieces: function () {
                    const estimated = this.effort * component.AZURE_DAILY_CAPACITY_HOURS;
                    const completed = this.getFullCompletedHours();
                    const line = this.getEstimateLineValue();
                    if (estimated == 0) {
                        return [];
                    }
                    let pieces = [];
                    if (completed == estimated) {
                        pieces.push({ style: "success", value: completed });
                        pieces.push({ style: "dark", value: line });
                    }
                    else if (completed > estimated) {
                        const succeededValue = estimated;
                        const overflownValue = completed - estimated;
                        pieces.push({ style: "success", value: succeededValue });
                        pieces.push({ style: "dark", value: line });
                        pieces.push({ style: "danger", value: overflownValue });
                    }
                    else {
                        const sparedHoursValue = estimated - completed;
                        pieces.push({ style: "success", value: completed });
                        pieces.push({ style: "dark", value: line });
                        pieces.push({ style: "info", value: sparedHoursValue });
                    }
                    return pieces;
                },
                assignChanges: function (data) {
                    _.assign(this, data);
                    if (data && data.customFields) {
                        this.customFields = { ...data.customFields };
                    }
                    this.loadStash();
                }
            };

            component.addChangeResetSupport(item, "workItemId", ["title", "state", "effort", "customFields"]);

            return item;
        },
        getTaskItemInstance() {
            let component = this;
            let item = {
                vid: uuidv4(),
                workItemId: null,
                url: "#",
                title: "",
                titleHint: "",
                totalHours: 0,
                capacityHours: 0,
                estimateHours: 0,
                activity: "",
                commentCount: 0,
                customFields: {},
                isAssignedToMe: true,
                isPlannable: true,
                sprintRolloverCount: 0,
                previousIterationName: null,
                isAddedMidSprint: false,
                addedToSprintDate: null,
                isBlocked: false,
                blockedReason: null,
                unresolvedPredecessors: [],
                isPrMergedWorkItemOpen: false,
                mergedPullRequestIds: [],
                children: [],

                isOfType: function (...types) {
                    return this.workItemType && _.some(types, s => this.workItemType.toLowerCase() == s.toLowerCase());
                },
                getFullCompletedHours: function () {
                    return parseInt(this.totalHours ?? 0);
                },
                getFullCapacityHours: function () {
                    return parseInt(this.capacityHours ?? 0);
                },
                getFullRemainingHours: function () {
                    return this.getFullCapacityHours() - this.getFullCompletedHours();
                },
                getProgressStyle: function () {
                    const completed = this.getFullCompletedHours();
                    const max = this.getFullCapacityHours();
                    if (completed < max)
                        return "secondary";
                    if (completed === max)
                        return "success";
                    if (completed > max)
                        return "danger";
                },
                isProgressCompleted: function () {
                    return this.getFullCompletedHours() === this.getFullCapacityHours();
                },
                getEstimateLineValue: function () {
                    return (this.getFullCapacityHours() / 100) * 2; //2% of total
                },
                getProgressPieces: function () {
                    const estimated = this.estimateHours;
                    const completed = this.getFullCompletedHours();
                    const remaining = this.getFullRemainingHours();
                    const capacity = completed + remaining;
                    const line = this.getEstimateLineValue();
                    if (estimated == 0) {
                        return [
                            { style: "success", value: completed },
                            { style: "secondary", value: remaining }
                        ];
                    }
                    let pieces = [];
                    if (completed == estimated) {
                        pieces.push({ style: "success", value: completed });
                        pieces.push({ style: "dark", value: line });
                        pieces.push({ style: "secondary", value: remaining });
                    }
                    else if (completed > estimated) {
                        const succeededValue = estimated;
                        const overflownValue = completed - estimated;
                        pieces.push({ style: "success", value: succeededValue });
                        pieces.push({ style: "dark", value: line });
                        pieces.push({ style: "danger", value: overflownValue });
                        pieces.push({ style: "secondary", value: remaining });
                    }
                    else if (capacity == estimated) {
                        pieces.push({ style: "success", value: completed });
                        pieces.push({ style: "secondary", value: remaining });
                        pieces.push({ style: "dark", value: line });

                    }
                    else if (capacity < estimated) {
                        const sparedHoursValue = estimated - capacity;
                        pieces.push({ style: "success", value: completed });
                        pieces.push({ style: "secondary", value: remaining });
                        pieces.push({ style: "dark", value: line });
                        pieces.push({ style: "info", value: sparedHoursValue });
                    }
                    else { //capacity > estimated
                        const overflawValue = capacity - estimated;
                        const remainingInCapacityValue = remaining - overflawValue;
                        pieces.push({ style: "success", value: completed });
                        pieces.push({ style: "secondary", value: remainingInCapacityValue });
                        pieces.push({ style: "dark", value: line });
                        pieces.push({ style: "warning", value: overflawValue });
                    }
                    return pieces;
                },
                assignChanges: function (data) {
                    _.assign(this, data);
                    if (data && data.customFields) {
                        this.customFields = { ...data.customFields };
                    }
                    this.loadStash();
                }
            };

            Object.defineProperty(item, 'estimateHoursRef', {
                get() {
                    return item.estimateHours;
                },
                set(newVal) {
                    item.estimateHours = newVal;
                    item.capacityHours = newVal;
                }
            });


            component.addChangeResetSupport(item, "workItemId", ["title", "state", "capacityHours", "activity", "customFields"]);

            return item;
        },
        async reloadGrid() {
            if (this.isStateFilterInvalid) return;
            this.stateFilter = [...this.draftStateFilter];
            this.appliedAssignees = [...(this.selectedAssignees || [])];
            this.$store.commit("setLastTaskStateFilter", this.stateFilter);
            this.showRemovedWorkItems = this.includesRemovedState(this.stateFilter);
            if (!this.hasBacklogSource) return;
            let sprint = this.$store.state.lastPickedSprint;
            await this.searchUserStoriesBySprint(sprint);
        },
        openUpgradeModal(plan = 'Pro Plan') {
            this.$router.push({
                path: '/plans',
                query: {
                    reason: 'Backlog',
                    requiredPlan: plan
                }
            }).catch(() => { });
        },
        getMockTaskItems() {
            return [
                {
                    workItemId: 1042,
                    workItemType: "User Story",
                    workItemTypeIcon: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#009ccc" viewBox="0 0 16 16"><path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V2z"/></svg>',
                    title: "[SAMPLE] Implement User Authentication & OAuth Flow",
                    state: "Committed",
                    assignedToName: "Alex Morgan",
                    assignedToSource: "",
                    isAssignedToMe: true,
                    commentCount: 2,
                    effort: 3,
                    url: "#",
                    createdDate: "2026-09-15T09:00:00Z",
                    children: [
                        {
                            workItemId: 1043,
                            workItemType: "Task",
                            workItemTypeIcon: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#f2c811" viewBox="0 0 16 16"><path d="M2 2h12v12H2z"/></svg>',
                            title: "[SAMPLE] Configure JWT validation middleware in WebApi",
                            state: "In Progress",
                            assignedToName: "Alex Morgan",
                            assignedToSource: "",
                            isAssignedToMe: true,
                            commentCount: 2,
                            activity: "Development",
                            capacityHours: 8,
                            estimateHours: 8,
                            totalHours: 5,
                            url: "#",
                            createdDate: "2026-09-15T10:00:00Z"
                        },
                        {
                            workItemId: 1044,
                            workItemType: "Task",
                            workItemTypeIcon: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#f2c811" viewBox="0 0 16 16"><path d="M2 2h12v12H2z"/></svg>',
                            title: "[SAMPLE] Unit tests for token expiration handlers",
                            state: "To Do",
                            assignedToName: "Alex Morgan",
                            assignedToSource: "",
                            isAssignedToMe: true,
                            activity: "Testing",
                            capacityHours: 4,
                            estimateHours: 4,
                            totalHours: 0,
                            url: "#",
                            createdDate: "2026-09-15T11:00:00Z"
                        }
                    ]
                },
                {
                    workItemId: 1045,
                    workItemType: "Bug",
                    workItemTypeIcon: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#cc293d" viewBox="0 0 16 16"><circle cx="8" cy="8" r="8"/></svg>',
                    title: "[SAMPLE] Timezone offset discrepancy on monthly report export",
                    state: "Committed",
                    assignedToName: "Jordan Lee",
                    assignedToSource: "",
                    isAssignedToMe: false,
                    effort: 1,
                    url: "#",
                    createdDate: "2026-09-16T14:30:00Z",
                    children: [
                        {
                            workItemId: 1046,
                            workItemType: "Task",
                            workItemTypeIcon: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#f2c811" viewBox="0 0 16 16"><path d="M2 2h12v12H2z"/></svg>',
                            title: "[SAMPLE] Normalize UTC parsing in frontend date formatter",
                            state: "Done",
                            assignedToName: "Jordan Lee",
                            assignedToSource: "",
                            isAssignedToMe: false,
                            activity: "Development",
                            capacityHours: 4,
                            estimateHours: 4,
                            totalHours: 4,
                            url: "#",
                            createdDate: "2026-09-16T15:00:00Z"
                        }
                    ]
                }
            ];
        },
        async searchUserStoriesBySprint(sprint, sprintInfo) {
            let component = this;
            if (!this.hasBacklogSource || !sprint) {
                this.userStoryItems = [];
                this.selectedSprint = sprint || '';
                this.loadingUserStoryItems = false;
                return;
            }
            const prevSprint = this.selectedSprint;
            const prevSprintFrom = this.selectedSprintFrom;
            const prevSprintTo = this.selectedSprintTo;

            this.selectedSprint = sprint || '';
            if (sprintInfo && sprintInfo.from) {
                this.selectedSprintFrom = sprintInfo.from;
                this.selectedSprintTo = sprintInfo.to || null;
                this.selectedSprintInfo = sprintInfo;
            } else if (sprint !== prevSprint) {
                this.selectedSprintFrom = null;
                this.selectedSprintTo = null;
                this.selectedSprintInfo = null;
            }

            const processInfoPromise = this.ensureProcessInfoAsync();

            const params = new URLSearchParams();
            params.append('sprint', sprint);
            params.append('includeRemoved', this.showRemovedWorkItems);
            params.append('anyAssigned', this.showAnyAssignedWorkItems);
            if (this.stateFilter && this.stateFilter.length) {
                const uniqueStates = Array.from(new Set(
                    this.stateFilter.map(s => s.includes('::') ? s.split('::')[1] : s)
                ));
                if (uniqueStates.length) {
                    params.append('statesCSV', uniqueStates.join(','));
                }
            }

            if (this.tasksAbortController) {
                this.tasksAbortController.abort();
                this.tasksAbortController = null;
            }
            const controller = new AbortController();
            this.tasksAbortController = controller;

            this.loadingUserStoryItems = true;
            try {
                const resp = await this.$http.get('/TaskManagement/FilterWorkItems', {
                    params,
                    signal: controller.signal
                });
                await processInfoPromise;
                this.loadSprintPtoAsync();
                this.isTruncated = String((resp && resp.headers && resp.headers['x-backlog-truncated']) || '').toLowerCase() === 'true';
                if (resp && Array.isArray(resp.data) && resp.data.length) {
                    this.applySearchResults(resp.data);
                } else {
                    this.userStoryItems = [];
                }
                this.expandOverrides = {};
            } catch (err) {
                if (axios.isCancel(err) || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
                    return;
                }
                if (err?.status === 402 || err?.response?.status === 402 || err?.isPlanUpgrade) {
                    if (prevSprint) {
                        this.selectedSprint = prevSprint;
                        this.selectedSprintFrom = prevSprintFrom;
                        this.selectedSprintTo = prevSprintTo;
                    }
                    return;
                }
                this.userStoryItems = [];
                console.error("Failed to filter work items", err);
                const errMsg = err?.response?.data?.message || err?.message || 'Failed to filter work items';
                this.toastError('Backlog Filter Error', errMsg);
            } finally {
                if (this.tasksAbortController === controller) {
                    component.stopSpinners();
                    this.tasksAbortController = null;
                }
            }
        },
        onAssigneesChanged(newVal) {
            const isAny = !newVal || newVal.length !== 1 || newVal[0] !== 'me';
            this.showAnyAssignedWorkItems = isAny;
        },
        async upsertTaskItemAsync(item) {
            let component = this;
            const formData = new FormData();
            formData.append("vid", item.vid);
            const wiIdNum = parseInt(item.workItemId);
            const isUpdating = !isNaN(wiIdNum);
            //UPDATE
            if (isUpdating) {
                if (this.isCompletedState(item) && item.totalHours == 0) {
                    this.$bvModal.msgBoxOk("Invalid Action! Task must have completed hours!", {
                        size: "sm",
                        buttonSize: "sm",
                        okVariant: "danger",
                        headerClass: "p-2 border-bottom-0",
                        footerClass: "p-2 border-top-0",
                        centered: true
                    });
                    return;
                }

                formData.append("title", item.title);
                formData.append("workItemId", wiIdNum.toString());
                formData.append("state", item.state);
                formData.append("remainingWork", item.capacityHours - item.totalHours);
            }
            //ADD
            else {
                formData.append("parentWorkItemId", item.parentWorkItemId);
                formData.append("workItemType", item.workItemType);
                formData.append("area", item.area);
                formData.append("iteration", item.iteration);
                formData.append("title", item.title);
                formData.append("remainingWork", item.capacityHours);
                formData.append("estimatedEffort", item.estimateHours);
            }
            formData.append("activity", item.activity);
            formData.append("customFieldsJson", JSON.stringify(isUpdating ? this.getChangedCustomFields(item) : (item.customFields || {})));

            this.loadingUserStoryItems = true;
            let action = isUpdating ?
                this.$http.patch("/TaskManagement/UpdateTaskItem", formData) :
                this.$http.post("/TaskManagement/CreateTaskItem", formData);
            try {
                const resp = await action;
                let upsertedItem = this.getTaskItemInstance();
                var upserted = _.assign(upsertedItem, resp.data, { children: item.children, isContext: false });
                if (resp.data && resp.data.customFields) {
                    upserted.customFields = { ...resp.data.customFields };
                }
                upserted.loadStash();
                this.replaceNode(item, upserted);
                this.toastSuccess("Saved!");
            } catch (err) {
                this.handleMutationError(err, "Failed to save task item");
            } finally {
                component.stopSpinners();
            }
        },
        async updateUserStoryItemAsync(item) {
            let component = this;
            const formData = new FormData();
            formData.append("vid", item.vid);
            const wiIdNum = parseInt(item.workItemId);
            const isUpdating = !isNaN(wiIdNum);

            if (isUpdating) {
                formData.append("workItemId", wiIdNum.toString());
            } else {
                formData.append("workItemType", item.workItemType || "");
                formData.append("area", item.area || "");
                formData.append("iteration", item.iteration || this.selectedSprint || "");
            }
            formData.append("title", item.title);
            formData.append("estimatedEffort", item.effort || 0);
            formData.append("state", item.state);
            formData.append("customFieldsJson", JSON.stringify(isUpdating ? this.getChangedCustomFields(item) : (item.customFields || {})));

            this.loadingUserStoryItems = true;
            const action = isUpdating
                ? this.$http.patch("/TaskManagement/UpdateUserStoryItem", formData)
                : this.$http.post("/TaskManagement/CreateUserStoryItem", formData);
            try {
                const resp = await action;
                let updatedItem = this.getUserStoryItemInstance();
                var updated = _.assign(updatedItem, resp.data, { children: item.children || [], isContext: false });
                if (resp.data && resp.data.customFields) {
                    updated.customFields = { ...resp.data.customFields };
                }
                updated.loadStash();
                this.replaceNode(item, updated);
                this.toastSuccess("Saved!");
            } catch (err) {
                this.handleMutationError(err, isUpdating ? "Failed to update user story item" : "Failed to create work item");
            } finally {
                component.stopSpinners();
            }
        },
        onAssignedToError() {
            event.target.src = require('@/assets/default-avatar.png'); // or a full URL
        },
        getAssignedTitle(name) {
            return name ? `Assigned: ${name}` : "N/A";
        },
        actionHoverPop(title) {
            return {
                title,
                container: "body",
                offset: "0, 8",
                customClass: "action-hover-pop",
                boundary: "window"
            };
        },
        copyToClip(text) {
            const el = document.createElement('textarea');
            el.value = text;
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            this.toastSuccess("Copied!");
        },
        checkChangedItemsBeforeLeave(next) {
            console.log("Checking changes before leaving!")
            next();
        },
        getWorkItemType(node) {
            if (!node) return '';
            return node.workItemType || node.type || '';
        },
        isColumnSupportedForWorkItem(col, node) {
            if (!col) return false;
            if (col.isSystem) return true;
            const rawType = this.getWorkItemType(node);
            const itemType = String(rawType || '').trim().toLowerCase();

            let supportedTypes = col.workItemTypes;
            if ((!supportedTypes || !supportedTypes.length) && this.discoveredCustomFields && this.discoveredCustomFields.length) {
                const df = this.discoveredCustomFields.find(f => f.referenceName.toLowerCase() === col.referenceName.toLowerCase());
                if (df && df.workItemTypes && df.workItemTypes.length) {
                    supportedTypes = df.workItemTypes;
                }
            }

            if (!supportedTypes || !supportedTypes.length) {
                return true;
            }

            if (!itemType) {
                return false;
            }
            return supportedTypes.some(t => String(t || '').trim().toLowerCase() === itemType);
        },
        normalizeGridColumns(cols) {
            if (!cols || !Array.isArray(cols) || cols.length === 0) {
                return DEFAULT_GRID_COLUMNS.map(c => ({ ...c, valueColors: c.referenceName === 'System.State' ? getStoredStateColors() : (c.valueColors ? { ...c.valueColors } : {}) }));
            }
            const list = [...cols];
            if (!list.some(c => c.referenceName === 'System.Comments')) {
                const commentsCol = DEFAULT_GRID_COLUMNS.find(c => c.referenceName === 'System.Comments');
                if (commentsCol) {
                    const wiIdx = list.findIndex(c => c.referenceName === 'System.WorkItem');
                    if (wiIdx !== -1) {
                        list.splice(wiIdx + 1, 0, { ...commentsCol });
                    } else {
                        list.unshift({ ...commentsCol });
                    }
                }
            }
            const actionIdx = list.findIndex(c => c.referenceName === 'System.Action');
            if (actionIdx !== -1 && actionIdx !== list.length - 1) {
                const actionCol = list.splice(actionIdx, 1)[0];
                list.push(actionCol);
            }
            const mapped = list.map((c, i) => ({
                ...c,
                allowedValues: c.allowedValues && c.allowedValues.length ? [...c.allowedValues] : (c.referenceName === 'System.State' ? [...DEFAULT_STATE_VALUES] : []),
                valueColors: c.referenceName === 'System.State'
                    ? { ...getStoredStateColors(), ...(c.valueColors && typeof c.valueColors === 'object' ? c.valueColors : {}) }
                    : (c.valueColors && typeof c.valueColors === 'object' ? { ...c.valueColors } : {}),
                workItemTypes: c.workItemTypes && Array.isArray(c.workItemTypes) ? [...c.workItemTypes] : [],
                fieldType: c.fieldType || '',
                metricSuffix: c.metricSuffix || '',
                width: c.width || '',
                order: i + 1,
                isVisible: c.isVisible !== undefined ? !!c.isVisible : !c.hidden,
                hidden: c.hidden !== undefined ? !!c.hidden : c.isVisible === false
            }));
            const stateCol = mapped.find(c => c.referenceName === 'System.State');
            if (stateCol && stateCol.valueColors) {
                try {
                    setStoredStateColors(stateCol.valueColors);
                } catch (e) {
                    // ignore
                }
            }
            return mapped;
        },
        getCustomFieldAllowedValues(col) {
            if (!col) return [];
            if (col.allowedValues && col.allowedValues.length > 0) {
                return col.allowedValues;
            }
            if (col.referenceName && this.discoveredCustomFields && this.discoveredCustomFields.length > 0) {
                const match = this.discoveredCustomFields.find(d => d.referenceName.toLowerCase() === col.referenceName.toLowerCase());
                if (match && match.allowedValues && match.allowedValues.length > 0) {
                    return match.allowedValues;
                }
            }
            return [];
        },
        getValueColorSelectStyle(col, val) {
            if (!col || !val) return null;
            let hex = null;
            if (col.valueColors && typeof col.valueColors === 'object') {
                hex = col.valueColors[val] || col.valueColors[String(val).trim()];
                if (!hex) {
                    const entry = Object.entries(col.valueColors).find(([k]) => k.toLowerCase() === String(val).trim().toLowerCase());
                    if (entry) hex = entry[1];
                }
            }
            if (!hex && col.referenceName === 'System.State') {
                const normVal = String(val).trim().toLowerCase();
                const storedColors = getStoredStateColors();
                const defEntry = Object.entries(storedColors).find(([k]) => k.toLowerCase() === normVal);
                if (defEntry) {
                    hex = defEntry[1];
                } else if (normVal === 'to do' || normVal === 'todo' || normVal === 'proposed' || normVal === 'design') {
                    hex = col.valueColors?.['New'] || col.valueColors?.['To Do'] || DEFAULT_STATE_COLORS['To Do'];
                } else if (normVal === 'in progress' || normVal === 'inprogress' || normVal === 'active' || normVal === 'doing' || normVal === 'committed' || normVal === 'started') {
                    hex = col.valueColors?.['Active'] || col.valueColors?.['In Progress'] || DEFAULT_STATE_COLORS['In Progress'];
                } else if (normVal === 'done' || normVal === 'resolved' || normVal === 'closed' || normVal === 'completed') {
                    hex = col.valueColors?.['Done'] || col.valueColors?.['Resolved'] || col.valueColors?.['Closed'] || DEFAULT_STATE_COLORS['Done'];
                } else if (normVal === 'removed' || normVal === 'cut' || normVal === 'abandoned') {
                    hex = col.valueColors?.['Removed'] || DEFAULT_STATE_COLORS['Removed'];
                }
            }
            if (!hex) return null;
            return {
                '--val-color': hex
            };
        },
        getCustomFieldValue(item, referenceName) {
            if (!item) return '';
            if (item.customFields && item.customFields[referenceName] !== undefined && item.customFields[referenceName] !== null) {
                return item.customFields[referenceName];
            }
            if (item.customFields && referenceName) {
                const lowerRef = String(referenceName).toLowerCase();
                const entry = Object.entries(item.customFields).find(([k]) => String(k).toLowerCase() === lowerRef);
                if (entry && entry[1] !== undefined && entry[1] !== null) {
                    return entry[1];
                }
            }
            if (referenceName === 'Microsoft.VSTS.Common.Activity' && item.activity) {
                return item.activity;
            }
            return '';
        },
        setCustomFieldValue(item, referenceName, value) {
            if (!item) return;
            if (!item.customFields) {
                this.$set(item, 'customFields', {});
            }
            this.$set(item.customFields, referenceName, value);
        },
        getChangedCustomFields(item) {
            const current = item.customFields || {};
            const stashed = (item.stashedData && item.stashedData.customFields) || {};
            const changed = {};
            const allKeys = new Set([...Object.keys(current), ...Object.keys(stashed)]);
            for (const key of allKeys) {
                if (!key || key.startsWith('WEF_')) continue;
                const curVal = current[key];
                const stashVal = stashed[key];
                if (String(curVal ?? '').trim() !== String(stashVal ?? '').trim()) {
                    changed[key] = curVal ?? '';
                }
            }
            return changed;
        },
        async enrichColumnsWithAllowedValues() {
            try {
                if (!this._discoverFieldsPromise) {
                    this._discoverFieldsPromise = this.$http.get("/TaskManagement/DiscoverCustomFields")
                        .finally(() => { this._discoverFieldsPromise = null; });
                }
                const resp = (this.discoveredCustomFields && this.discoveredCustomFields.length > 0)
                    ? { data: this.discoveredCustomFields }
                    : await this._discoverFieldsPromise;
                const discovered = resp.data || [];
                this.discoveredCustomFields = discovered;
                (this.activeGridColumns || []).forEach(c => {
                    const match = discovered.find(d => d.referenceName.toLowerCase() === c.referenceName.toLowerCase());
                    if (match && match.allowedValues && match.allowedValues.length > 0) {
                        this.$set(c, 'allowedValues', [...match.allowedValues]);
                    }
                    if (match && match.workItemTypes && match.workItemTypes.length > 0 && (!c.workItemTypes || !c.workItemTypes.length)) {
                        this.$set(c, 'workItemTypes', [...match.workItemTypes]);
                    }
                    if (match && match.fieldType && !c.fieldType) {
                        this.$set(c, 'fieldType', match.fieldType);
                    }
                    if (match && (!c.allowedValues || !c.allowedValues.length) && (match.fieldType || '').toLowerCase() === 'boolean') {
                        this.$set(c, 'allowedValues', ['Yes', 'No']);
                    }
                    if (match && match.metricSuffix && !c.metricSuffix) {
                        this.$set(c, 'metricSuffix', match.metricSuffix);
                    }
                });
                // Enrich System.State with actual project stateOptions
                const stateCol = (this.activeGridColumns || []).find(c => c.referenceName === 'System.State');
                if (stateCol && this.stateOptions && this.stateOptions.length > 0) {
                    const combined = Array.from(new Set([...this.stateOptions, ...(stateCol.allowedValues || [])]));
                    this.$set(stateCol, 'allowedValues', combined);
                }
                this.activeGridColumns = [...this.activeGridColumns];
            } catch (err) {
                console.warn("Could not enrich columns with custom field allowed values:", err);
            }
        },
        async saveGridColumnsSilentAsync() {
            try {
                const payload = (this.activeGridColumns || []).map((c, index) => ({
                    referenceName: c.referenceName,
                    label: c.label,
                    fieldType: c.fieldType || '',
                    metricSuffix: c.metricSuffix || '',
                    width: c.width || '',
                    isSystem: !!c.isSystem,
                    allowedValues: c.allowedValues || [],
                    valueColors: c.valueColors && typeof c.valueColors === 'object' ? c.valueColors : {},
                    workItemTypes: c.workItemTypes && Array.isArray(c.workItemTypes) ? [...c.workItemTypes] : [],
                    order: index + 1,
                    isVisible: c.isVisible !== undefined ? !!c.isVisible : !c.hidden,
                    hidden: c.hidden !== undefined ? !!c.hidden : c.isVisible === false
                }));
                await this.$http.post('/TaskManagement/SaveTaskGridColumns', payload);
            } catch (err) {
                console.warn('Could not persist resized task grid column width:', err);
            }
        },
        getUiZoomScale() {
            if (typeof document !== 'undefined' && document.documentElement) {
                const z = parseFloat(document.documentElement.style.zoom);
                if (Number.isFinite(z) && z > 0) return z;
            }
            return 1;
        },
        getElementCssWidthPx(el) {
            if (!el) return 0;
            if (typeof window !== 'undefined' && typeof window.getComputedStyle === 'function') {
                const computed = parseFloat(window.getComputedStyle(el).width);
                if (Number.isFinite(computed) && computed > 0) {
                    return Math.round(computed);
                }
            }
            if (el.offsetWidth > 0) {
                return Math.round(el.offsetWidth);
            }
            return Math.round(el.getBoundingClientRect().width / this.getUiZoomScale());
        },
        onStartResizeColumn(event, col) {
            if (!col) return;
            const handleEl = event.currentTarget;
            const parentEl = handleEl ? handleEl.parentElement : null;
            const headerColEl = parentEl && typeof parentEl.closest === 'function'
                ? (parentEl.closest('.col, [class*="time-col-"]') || parentEl)
                : parentEl;
            if (!headerColEl) return;

            this.cleanupColResize();
            const colRef = col.referenceName;
            const startX = event.clientX;
            const startWidth = this.getElementCssWidthPx(headerColEl);
            const zoomScale = this.getUiZoomScale();
            const minWidth = colRef === 'System.Title' ? 140 : 56;
            const visCols = this.visibleGridColumns || [];
            const titleIdx = visCols.findIndex(c => c.referenceName === 'System.Title');
            let lockedTitleCol = null;
            let lockedTitleWidthPx = 0;
            if (colRef !== 'System.Title' && titleIdx !== -1 && headerColEl.parentElement) {
                const titleCol = (this.activeGridColumns || []).find(c => c.referenceName === 'System.Title');
                const hasCustomPx = titleCol && titleCol.width && /^\d+(\.\d+)?px$/i.test(String(titleCol.width).trim());
                if (titleCol && !hasCustomPx) {
                    const titleHeaderEl = headerColEl.parentElement.querySelector('.time-col-title');
                    if (titleHeaderEl) {
                        const titleWidthPx = this.getElementCssWidthPx(titleHeaderEl);
                        if (titleWidthPx >= 140) {
                            lockedTitleCol = titleCol;
                            lockedTitleWidthPx = titleWidthPx;
                        }
                    }
                }
            }
            const applyTitleLockOnce = () => {
                if (lockedTitleCol && lockedTitleWidthPx >= 140) {
                    this.$set(lockedTitleCol, 'width', `${lockedTitleWidthPx}px`);
                    lockedTitleCol = null;
                }
            };
            let lastAppliedWidth = startWidth;
            let pendingWidth = null;
            let changed = false;

            this.resizingColRef = colRef;
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';

            this._onColResizeMove = (moveEvent) => {
                const deltaX = (moveEvent.clientX - startX) / zoomScale;
                const newWidth = Math.max(minWidth, Math.round(startWidth + deltaX));
                if (newWidth === lastAppliedWidth) return;
                pendingWidth = newWidth;
                if (this._colResizeRaf) return;
                this._colResizeRaf = requestAnimationFrame(() => {
                    this._colResizeRaf = null;
                    if (pendingWidth === null || pendingWidth === lastAppliedWidth) return;
                    applyTitleLockOnce();
                    lastAppliedWidth = pendingWidth;
                    const target = (this.activeGridColumns || []).find(c => c.referenceName === colRef);
                    if (target) {
                        this.$set(target, 'width', `${pendingWidth}px`);
                        changed = true;
                    }
                });
            };
            this._onColResizeUp = () => {
                if (this._colResizeRaf) {
                    cancelAnimationFrame(this._colResizeRaf);
                    this._colResizeRaf = null;
                }
                if (pendingWidth !== null && pendingWidth !== lastAppliedWidth) {
                    applyTitleLockOnce();
                    lastAppliedWidth = pendingWidth;
                    const target = (this.activeGridColumns || []).find(c => c.referenceName === colRef);
                    if (target) {
                        this.$set(target, 'width', `${pendingWidth}px`);
                        changed = true;
                    }
                }
                const shouldSave = changed;
                this.cleanupColResize();
                this.fixAllTextAreas();
                if (shouldSave) {
                    this.saveGridColumnsSilentAsync();
                }
            };
            window.addEventListener('mousemove', this._onColResizeMove);
            window.addEventListener('mouseup', this._onColResizeUp);
        },
        onResetColumnWidth(col) {
            if (!col) return;
            const target = (this.activeGridColumns || []).find(c => c.referenceName === col.referenceName);
            if (target && target.width) {
                this.$set(target, 'width', '');
                this.fixAllTextAreas();
                this.saveGridColumnsSilentAsync();
            }
        },
        autoFitGridColumns() {
            const cols = this.activeGridColumns || [];
            if (!cols.length) return;

            const allNodes = [];
            const walk = (node) => {
                if (!node) return;
                allNodes.push(node);
                if (Array.isArray(node.children)) node.children.forEach(walk);
            };
            (this.userStoryItems || []).forEach(walk);

            const customAllocations = [];
            for (const col of cols) {
                const isVis = col.isVisible !== undefined ? !!col.isVisible : !col.hidden;
                if (!isVis || col.isSystem || col.referenceName === 'System.Title' || col.referenceName === 'System.Action') {
                    this.$set(col, 'width', '');
                    continue;
                }
                const labelLen = (col.label || '').trim().length;
                const labelPx = Math.round(labelLen * 6.5 + 22);
                let idealPx;
                let minPx;
                if (this.isBooleanColumn(col)) {
                    minPx = 76;
                    idealPx = Math.max(84, Math.min(116, labelPx));
                } else if (this.isNumericColumn(col)) {
                    const suffixLen = (col.metricSuffix || '').trim().length;
                    const controlPx = suffixLen > 0 ? (82 + suffixLen * 7) : 78;
                    minPx = suffixLen > 0 ? 84 : 74;
                    idealPx = Math.max(88, Math.min(130, Math.max(controlPx, Math.min(labelPx, 124))));
                } else {
                    minPx = 88;
                    let maxValLen = 0;
                    for (const n of allNodes) {
                        if (!this.isColumnSupportedForWorkItem(col, n)) continue;
                        const val = this.getCustomFieldValue(n, col.referenceName);
                        if (val !== null && val !== undefined) {
                            const len = String(val).trim().length;
                            if (len > maxValLen) maxValLen = len;
                        }
                    }
                    const contentPx = maxValLen > 0 ? Math.round(maxValLen * 6.8 + 36) : 96;
                    idealPx = Math.max(96, Math.min(172, Math.max(contentPx, Math.min(labelPx, 144))));
                }
                customAllocations.push({ col, idealPx, minPx });
            }

            if (customAllocations.length > 0) {
                const scrollEl = this.$refs.scrollContainer;
                const containerWidth = scrollEl ? (this.getElementCssWidthPx(scrollEl) - 32) : 0;
                const sysPxMap = {
                    'System.WorkItem': 112,
                    'System.Comments': 52,
                    'System.AssignedTo': 128,
                    'System.Title': 288,
                    'System.State': 120,
                    'System.Capacity': 84,
                    'System.OriginalEstimate': 116,
                    'System.Progress': 160,
                    'System.Action': 112
                };
                const visCols = this.visibleGridColumns || [];
                const gapsPx = (visCols.length) * 6;
                let fixedSysTotal = 40 + gapsPx;
                for (const c of visCols) {
                    if (c.isSystem) {
                        fixedSysTotal += sysPxMap[c.referenceName] || 100;
                    }
                }
                const idealCustomTotal = customAllocations.reduce((sum, a) => sum + a.idealPx, 0);
                const maxSlack = customAllocations.reduce((sum, a) => sum + (a.idealPx - a.minPx), 0);
                const overflow = containerWidth > 0 ? (fixedSysTotal + idealCustomTotal - containerWidth) : 0;

                for (const a of customAllocations) {
                    let finalPx = a.idealPx;
                    if (overflow > 0 && maxSlack > 0) {
                        const ratio = Math.min(1, overflow / maxSlack);
                        finalPx = Math.round(a.idealPx - (a.idealPx - a.minPx) * ratio);
                    }
                    this.$set(a.col, 'width', `${finalPx}px`);
                }
            }

            this.$nextTick(() => {
                this.fixAllTextAreas();
            });
            this.saveGridColumnsSilentAsync();
        },
        cleanupColResize() {
            if (this._colResizeRaf) {
                cancelAnimationFrame(this._colResizeRaf);
                this._colResizeRaf = null;
            }
            if (this._onColResizeMove) {
                window.removeEventListener('mousemove', this._onColResizeMove);
                this._onColResizeMove = null;
            }
            if (this._onColResizeUp) {
                window.removeEventListener('mouseup', this._onColResizeUp);
                this._onColResizeUp = null;
            }
            if (this.resizingColRef) {
                this.resizingColRef = null;
                if (typeof document !== 'undefined' && document.body) {
                    document.body.style.cursor = '';
                    document.body.style.userSelect = '';
                }
            }
        },
        loadStoredHygieneConfig() {
            const defaults = {
                enabled: true,
                loggedOnNew: true,
                zeroRemaining: true,
                closedWithRemaining: true,
                parentReadyToClose: true,
                sprintRollover: true,
                addedMidSprint: true,
                blockedWorkItems: true,
                prMergedWorkItemOpen: true,
                assigneeOnPto: true
            };
            try {
                if (typeof localStorage !== 'undefined') {
                    const raw = localStorage.getItem('vt_backlog_hygiene_config');
                    if (raw) {
                        const parsed = JSON.parse(raw);
                        if (parsed && typeof parsed === 'object') {
                            return {
                                enabled: parsed.enabled !== false,
                                loggedOnNew: parsed.loggedOnNew !== false,
                                zeroRemaining: parsed.zeroRemaining !== false,
                                closedWithRemaining: parsed.closedWithRemaining !== false,
                                parentReadyToClose: parsed.parentReadyToClose !== false,
                                sprintRollover: parsed.sprintRollover !== false,
                                addedMidSprint: parsed.addedMidSprint !== false,
                                blockedWorkItems: parsed.blockedWorkItems !== false,
                                prMergedWorkItemOpen: parsed.prMergedWorkItemOpen !== false,
                                assigneeOnPto: parsed.assigneeOnPto !== false
                            };
                        }
                    }
                }
            } catch (e) {
                // ignore storage errors
            }
            return defaults;
        },
        onHygieneSettingsSaved(newSettings) {
            if (!newSettings || typeof newSettings !== 'object') return;
            this.hygieneConfig = {
                enabled: newSettings.enabled !== false,
                loggedOnNew: newSettings.loggedOnNew !== false,
                zeroRemaining: newSettings.zeroRemaining !== false,
                closedWithRemaining: newSettings.closedWithRemaining !== false,
                parentReadyToClose: newSettings.parentReadyToClose !== false,
                sprintRollover: newSettings.sprintRollover !== false,
                addedMidSprint: newSettings.addedMidSprint !== false,
                blockedWorkItems: newSettings.blockedWorkItems !== false,
                prMergedWorkItemOpen: newSettings.prMergedWorkItemOpen !== false,
                assigneeOnPto: newSettings.assigneeOnPto !== false
            };
            if (!this.hygieneConfig.enabled && this.onlyHygieneAlerts) {
                this.onlyHygieneAlerts = false;
            }
        },
        async onColumnsSaved(newColumns) {
            this.activeGridColumns = this.normalizeGridColumns(newColumns);
            await this.enrichColumnsWithAllowedValues();
        },
        async loadConfiguredColumns() {
            this.loadingGridColumns = true;
            try {
                const resp = await this.$http.get("/TaskManagement/GetTaskGridColumns");
                if (resp && resp.data && Array.isArray(resp.data) && resp.data.length > 0) {
                    this.activeGridColumns = this.normalizeGridColumns(resp.data);
                } else {
                    this.activeGridColumns = this.normalizeGridColumns([]);
                }
                await this.enrichColumnsWithAllowedValues();
            } catch (err) {
                console.warn("Could not load task grid columns, using defaults:", err);
                this.activeGridColumns = [...DEFAULT_GRID_COLUMNS];
            } finally {
                this.loadingGridColumns = false;
            }
        },
        openColumnSettings(initialSearchField = '') {
            const field = typeof initialSearchField === 'string' ? initialSearchField : '';
            if (this.$refs.modalTaskColumns) {
                this.$refs.modalTaskColumns.showModal(field);
            }
        },
        getCommentsTooltip(item) {
            const count = item ? (item.commentCount || 0) : 0;
            const mentions = item ? (item.mentionCount || 0) : 0;
            if (count <= 0) return 'No comments';
            const commentPart = count === 1 ? '1 comment' : `${count} comments`;
            if (mentions > 0) {
                return `${commentPart} (${mentions} mention${mentions === 1 ? '' : 's'} to you)`;
            }
            return commentPart;
        },
        handleMutationError(err, defaultTitle, defaultMsg) {
            if (err?.status === 402 || err?.response?.status === 402 || err?.isPlanUpgrade) return;
            console.error(defaultTitle, err);

            const data = err?.response?.data;
            let fieldName = '';
            let ruleMessage = '';

            if (data?.isRuleValidationError) {
                fieldName = data.fieldName || data.fieldReferenceName || '';
                ruleMessage = data.ruleMessage || '';
            } else {
                const rawError = (typeof data === 'string' ? data : null) ||
                    (data?.errors && Array.isArray(data.errors) ? data.errors.join(' ') : null) ||
                    data?.message ||
                    err?.message || '';

                const isRuleError = rawError.includes('TF401320') ||
                    rawError.includes('Rule validation failed') ||
                    rawError.includes('RequiredField') ||
                    rawError.includes('cannot be empty');

                if (isRuleError) {
                    const match = rawError.match(/field\s+['"]([^'"]+)['"]/i) ||
                        rawError.match(/Field\s+['"]([^'"]+)['"]\s+cannot be empty/i);
                    fieldName = match ? match[1].trim() : '';
                    const display = fieldName ? `'${fieldName}'` : 'a required field';
                    ruleMessage = `Azure DevOps process rules require a value for ${display}. Add this column to your grid in VelocyTrack to provide a value.`;
                }
            }

            if (ruleMessage || fieldName) {
                this.toastRuleValidation(
                    fieldName,
                    ruleMessage,
                    () => this.openColumnSettings(fieldName),
                    10
                );
                return;
            }

            const errMsg = (data?.errors && data.errors[0]) || data?.message || defaultMsg || 'An error occurred during save.';
            this.toastError(defaultTitle, errMsg);
        },
        ciGet(obj, key) {
            if (!obj || key === undefined || key === null || key === '') return undefined;
            if (obj[key] !== undefined) return obj[key];
            const lower = String(key).toLowerCase();
            const match = Object.keys(obj).find(k => k.toLowerCase() === lower);
            return match !== undefined ? obj[match] : undefined;
        },
        typeInList(type, list) {
            if (!type || !Array.isArray(list)) return false;
            const lower = type.toLowerCase();
            return list.some(t => (t || '').toLowerCase() === lower);
        },
        isPlannableType(type) {
            return !!this.processInfo && this.typeInList(type, this.processInfo.plannableWorkItemTypes);
        },
        stateCategoryOf(workItemType, state) {
            if (!state) return '';
            const pi = this.processInfo || {};
            const direct = this.ciGet(this.ciGet(pi.stateCategoriesByWorkItemType, workItemType), state);
            if (direct) return direct;
            const lower = state.toLowerCase();
            const map = pi.stateCategoryMap || {};
            return Object.keys(map).find(c => (map[c] || []).some(s => (s || '').toLowerCase() === lower)) || '';
        },
        stateCategory(item) {
            return item ? this.stateCategoryOf(item.workItemType, item.state) : '';
        },
        stateRankOf(item, inProgressFirst) {
            const order = inProgressFirst
                ? ['InProgress', 'Proposed', 'Resolved', 'Completed', 'Removed']
                : ['Proposed', 'InProgress', 'Resolved', 'Completed', 'Removed'];
            const idx = order.indexOf(this.stateCategory(item));
            return idx === -1 ? order.length : idx;
        },
        isCompletedState(item) {
            return this.stateCategory(item) === 'Completed';
        },
        isCapacityEditable(item) {
            const category = this.stateCategory(item);
            return !category || category === 'Proposed' || category === 'InProgress';
        },
        isNodeInSelectedSprint(node) {
            if (!node) return true;
            if (!this.selectedSprint || !node.iteration) return !node.isContext;
            const norm = (s) => String(s || '').trim().replace(/^\\+|\\+$/g, '').toLowerCase();
            const nodeIter = norm(node.iteration);
            const selIter = norm(this.selectedSprint);
            if (!nodeIter || !selIter) return !node.isContext;
            if (nodeIter === selIter) return true;
            const selLeaf = selIter.split('\\').pop();
            const nodeLeaf = nodeIter.split('\\').pop();
            return Boolean(selLeaf && nodeLeaf && selLeaf === nodeLeaf);
        },
        isOutOfSprintParent(node) {
            if (!node || node.isPlannable) return false;
            if (typeof node.isSaved === 'function' && !node.isSaved()) return false;
            return Boolean(node.isContext) || !this.isNodeInSelectedSprint(node);
        },
        getNodeSprintShortName(node) {
            if (!node || !node.iteration) return '';
            const parts = String(node.iteration).split('\\').filter(Boolean);
            return parts.length > 0 ? parts[parts.length - 1] : '';
        },
        getOutOfSprintTooltip(node) {
            const currentSprint = this.selectedSprintName || 'selected sprint';
            const itemSprint = this.getNodeSprintShortName(node);
            const sprintDetail = itemSprint ? ` (currently in ${itemSprint})` : '';
            return `This parent item is not part of ${currentSprint}${sprintDetail}. It is shown in disabled mode only as context for its child items in ${currentSprint}.`;
        },
        isReadOnly(item) {
            if (this.isViewOnlyMode) return true;
            if (!item || !item.isAssignedToMe) return true;
            if (this.isOutOfSprintParent(item)) return true;
            return false;
        },
        showsEffort(node) {
            const pi = this.processInfo;
            if (!pi || !pi.requirementWorkItemTypes) {
                return node.effort !== null && node.effort !== undefined && node.effort !== '';
            }
            return !node.isPlannable && this.typeInList(node.workItemType, [...(pi.requirementWorkItemTypes || []), ...(pi.bugWorkItemTypes || [])]);
        },
        removedStateFor(type) {
            const pi = this.processInfo;
            const states = pi && pi.statesByWorkItemType ? this.ciGet(pi.statesByWorkItemType, type) : null;
            if (!Array.isArray(states)) return this.WI_STATE_REMOVED;
            return states.find(s => this.stateCategoryOf(type, s) === 'Removed') || '';
        },
        initialStateFor(type) {
            const pi = this.processInfo;
            const states = pi && pi.statesByWorkItemType ? this.ciGet(pi.statesByWorkItemType, type) : null;
            if (Array.isArray(states) && states.length > 0) {
                const preferred = pi.defaultInitialTaskState;
                const match = preferred ? states.find(s => s.toLowerCase() === preferred.toLowerCase()) : null;
                return match || states[0];
            }
            return (pi && pi.defaultInitialTaskState) || this.WI_STATE_TODO;
        },
        defaultActivity() {
            const activities = this.loadedWorkItemActivities || [];
            const pick = (pattern) => activities.find(a => pattern.test(a)) || '';
            if (this.isRoleDev) return pick(/dev/i);
            if (this.isRoleQA) return pick(/test|qa/i);
            if (this.isRoleSA) return pick(/analy|require|design|spec/i);
            return '';
        },
        columnFieldType(col) {
            if (!col) return '';
            const match = col.referenceName && (this.discoveredCustomFields || []).find(d => d.referenceName.toLowerCase() === col.referenceName.toLowerCase());
            return ((match && match.fieldType) || col.fieldType || '').toLowerCase();
        },
        isNumericColumn(col) {
            const ft = this.columnFieldType(col);
            if (/^(picklist)?(integer|double|decimal|number|int|float)$/.test(ft)) return true;
            if (ft && ft !== 'string') return false;
            const combined = `${col?.referenceName || ''} ${col?.label || ''}`.toLowerCase();
            return combined.includes('point') || combined.includes('effort') || combined.includes('estimate') || combined.includes('time') || combined.includes('hour') || combined.includes('hrs') || combined.includes('duration') || combined.includes('saved');
        },
        includesRemovedState(states) {
            const stateNames = (states || []).map(s => s.includes('::') ? s.split('::')[1] : s);
            return stateNames.some(s => this.removedStateNames.includes(s));
        },
        initStateFilter() {
            const allKeys = this.allDraftStateKeys;
            const defKeys = this.defaultDraftStateKeys;
            const saved = this.$store.state.lastTaskStateFilter || [];

            let next = [];
            if (saved.length > 0) {
                const hasGroupKeys = saved.some(s => s && s.includes('::'));
                if (hasGroupKeys) {
                    next = saved.filter(k => allKeys.includes(k));
                } else {
                    this.groupedStateOptions.forEach(g => {
                        g.states.forEach(st => {
                            if (saved.includes(st)) {
                                next.push(this.stateKey(g.key, st));
                            }
                        });
                    });
                }
            }

            if (next.length === 0) {
                next = [...defKeys];
            }

            this.draftStateFilter = [...next];
            this.stateFilter = [...next];
            this.showRemovedWorkItems = this.includesRemovedState(next);
        },
        stateOptionTitle(opt) {
            const entry = this.stateOptionDetails.find(o => o.name === opt);
            return entry && entry.types.length > 0 ? 'Used by: ' + entry.types.join(', ') : '';
        },
        resetAllTaskFilters() {
            this.onlyHygieneAlerts = false;
            this.backlogViewSearch = '';
            this.selectedAssignees = ['me'];
            const def = [...this.defaultDraftStateKeys];
            this.draftStateFilter = [...def];
            this.stateFilter = [...def];
            this.$store.commit("setLastTaskStateFilter", this.stateFilter);
            this.showRemovedWorkItems = false;
            this.showAnyAssignedWorkItems = false;
        },
        getGroupForItem(item) {
            if (!item || !item.workItemType) return null;
            const type = (item.workItemType || '').toLowerCase();
            return this.groupedStateOptions.find(g => (g.types || []).some(t => (t || '').toLowerCase() === type)) || null;
        },
        isItemStateMatching(item) {
            if (!item) return false;
            if (!this.stateFilter || !this.stateFilter.length) return true;
            const group = this.getGroupForItem(item);
            if (group) {
                const key = this.stateKey(group.key, item.state);
                return this.stateFilter.includes(key);
            }
            return this.stateFilter.some(k => (k.includes('::') ? k.split('::')[1] : k) === item.state);
        },
        matchesAssigneeFilter(obj) {
            if (!obj) return false;
            const assignees = this.selectedAssignees;
            if (!assignees || assignees.length === 0 || assignees.includes('team') || assignees.includes('all')) return true;
            if (assignees.includes('me') && obj.isAssignedToMe) return true;
            const assignedSources = [obj.assignedToSource, obj.assignedToDevSource, obj.assignedToQaSource, obj.assignedToSaSource].filter(Boolean).map(s => String(s).toLowerCase());
            const assignedNames = [obj.assignedToName, obj.assignedToDevName, obj.assignedToQaName, obj.assignedToSaName].filter(Boolean).map(n => String(n).toLowerCase());
            return assignees.some(a => {
                const token = (a || '').toLowerCase();
                if (token === 'me') return obj.isAssignedToMe;
                if (!token) return false;
                if (assignedSources.some(src => src.includes(token) || token.includes(src))) return true;
                if (assignedNames.some(nm => nm.includes(token) || token.includes(nm))) return true;
                const emailPrefix = token.split('@')[0].replace(/[._-]/g, ' ');
                if (assignedNames.some(nm => nm.includes(emailPrefix) || emailPrefix.includes(nm))) return true;
                return false;
            });
        },
        getDefaultInProgressStateFor(node) {
            const pi = this.processInfo || {};
            const type = node ? node.workItemType : '';
            const states = pi.statesByWorkItemType ? this.ciGet(pi.statesByWorkItemType, type) : null;
            if (Array.isArray(states) && states.length > 0) {
                const preferred = node && node.isPlannable ? pi.defaultInProgressTaskState : pi.defaultCommittedRequirementState;
                if (preferred) {
                    const exact = states.find(s => s.toLowerCase() === preferred.toLowerCase());
                    if (exact && this.stateCategoryOf(type, exact) === 'InProgress') return exact;
                }
                const inProg = states.find(s => this.stateCategoryOf(type, s) === 'InProgress');
                if (inProg) return inProg;
            }
            return (node && node.isPlannable ? pi.defaultInProgressTaskState : pi.defaultCommittedRequirementState) || this.WI_STATE_INPROGRESS || 'In Progress';
        },
        getDefaultCompletedStateFor(node) {
            const pi = this.processInfo || {};
            const type = node ? node.workItemType : '';
            const states = pi.statesByWorkItemType ? this.ciGet(pi.statesByWorkItemType, type) : null;
            if (Array.isArray(states) && states.length > 0) {
                if (node && node.isPlannable && pi.defaultDoneTaskState) {
                    const exact = states.find(s => s.toLowerCase() === pi.defaultDoneTaskState.toLowerCase());
                    if (exact && this.stateCategoryOf(type, exact) === 'Completed') return exact;
                }
                const completed = states.find(s => this.stateCategoryOf(type, s) === 'Completed');
                if (completed) return completed;
            }
            return (node && node.isPlannable ? pi.defaultDoneTaskState : 'Closed') || this.WI_STATE_DONE || 'Done';
        },
        getNodeHygieneAlert(node) {
            const cfg = this.hygieneConfig || {};
            if (cfg.enabled === false) return null;
            if (!node || typeof node.isSaved !== 'function' || !node.isSaved()) return null;
            if (this.isOutOfSprintParent(node)) return null;
            const category = this.stateCategory(node);
            if (!category || category === 'Removed') return null;

            if (node.isPlannable) {
                const completed = Number(node.totalHours || 0);
                const capacity = Number(node.capacityHours || 0);
                const remaining = Math.max(0, Math.round((capacity - completed) * 100) / 100);

                if (cfg.loggedOnNew !== false && category === 'Proposed' && completed > 0) {
                    const targetState = this.getDefaultInProgressStateFor(node);
                    if (!targetState || targetState.toLowerCase() === String(node.state || '').toLowerCase()) return null;
                    return {
                        key: 'logged-on-new',
                        badgeTestId: 'badge-hygiene-logged-on-new',
                        btnTestId: 'btn-fix-activate-item',
                        variant: 'amber',
                        icon: 'bi bi-exclamation-triangle-fill',
                        label: `${completed}h logged while ${node.state}`,
                        tooltip: `${completed}h of completed work has been logged, but this work item is still in '${node.state}' state.`,
                        actionLabel: `Move to ${targetState}`,
                        actionIcon: 'bi bi-play-fill',
                        actionTooltip: `1-click fix: Transition state from '${node.state}' to '${targetState}'`,
                        targetState
                    };
                }

                if (cfg.zeroRemaining !== false && category === 'InProgress' && completed > 0 && remaining <= 0) {
                    const targetState = this.getDefaultCompletedStateFor(node);
                    if (!targetState || targetState.toLowerCase() === String(node.state || '').toLowerCase()) return null;
                    return {
                        key: 'zero-remaining',
                        badgeTestId: 'badge-hygiene-zero-remaining',
                        btnTestId: 'btn-fix-close-item',
                        variant: 'teal',
                        icon: 'bi bi-check2-circle',
                        label: '0h remaining — Ready to close?',
                        tooltip: `All planned capacity (${completed}h) is completed with 0h remaining while still '${node.state}'.`,
                        actionLabel: `Mark ${targetState}`,
                        actionIcon: 'bi bi-check-lg',
                        actionTooltip: `1-click fix: Transition state from '${node.state}' to '${targetState}'`,
                        targetState
                    };
                }

                if (cfg.closedWithRemaining !== false && category === 'Completed' && remaining > 0) {
                    return {
                        key: 'closed-with-remaining',
                        badgeTestId: 'badge-hygiene-closed-with-remaining',
                        btnTestId: 'btn-fix-zero-remaining',
                        variant: 'rose',
                        icon: 'bi bi-fire',
                        label: `${node.state} with ${remaining}h remaining`,
                        tooltip: `This work item is '${node.state}' but still carries ${remaining}h of remaining work, which distorts sprint burndown.`,
                        actionLabel: 'Zero Remaining (0h)',
                        actionIcon: 'bi bi-eraser-fill',
                        actionTooltip: `1-click fix: Clear remaining work to 0h (set capacity to ${completed}h)`
                    };
                }
                return this.getPrMergedAlert(node, cfg, category);
            }

            if (cfg.parentReadyToClose !== false && (category === 'Proposed' || category === 'InProgress')) {
                if (!this.matchesAssigneeFilter(node)) return null;
                const savedKids = (node.children || []).filter(
                    ch => ch && typeof ch.isSaved === 'function' && ch.isSaved() && this.stateCategory(ch) !== 'Removed'
                );
                if (savedKids.length > 0 && savedKids.every(ch => this.stateCategory(ch) === 'Completed')) {
                    const targetState = this.getDefaultCompletedStateFor(node);
                    if (!targetState || targetState.toLowerCase() === String(node.state || '').toLowerCase()) return null;
                    return {
                        key: 'parent-ready-to-close',
                        badgeTestId: 'badge-hygiene-parent-ready-to-close',
                        btnTestId: 'btn-fix-close-parent',
                        variant: 'emerald',
                        icon: 'bi bi-check2-all',
                        label: `All ${savedKids.length} child task${savedKids.length === 1 ? '' : 's'} done — Close story?`,
                        tooltip: `Every active child task under this item is completed, but the parent item is still '${node.state}'.`,
                        actionLabel: `Move to ${targetState}`,
                        actionIcon: 'bi bi-check2-circle',
                        actionTooltip: `1-click fix: Transition parent state from '${node.state}' to '${targetState}'`,
                        targetState
                    };
                }
            }
            return this.getPrMergedAlert(node, cfg, category);
        },
        getPrMergedAlert(node, cfg, category) {
            if (cfg.prMergedWorkItemOpen === false || !node.isPrMergedWorkItemOpen) return null;
            if (category !== 'Proposed' && category !== 'InProgress') return null;
            const targetState = this.getDefaultCompletedStateFor(node);
            if (!targetState || targetState.toLowerCase() === String(node.state || '').toLowerCase()) return null;
            const prIds = Array.isArray(node.mergedPullRequestIds) ? node.mergedPullRequestIds : [];
            const prText = prIds.length ? ` (${prIds.map(id => '!' + id).join(', ')})` : '';
            return {
                key: 'pr-merged-wi-open',
                badgeTestId: 'badge-pr-merged-wi-open',
                btnTestId: 'btn-fix-pr-merged-close',
                variant: 'violet',
                icon: 'bi bi-git',
                label: 'PR Merged — Ready to Resolve/Close?',
                tooltip: `All linked pull requests${prText} are merged, but this work item is still '${node.state}'.`,
                actionLabel: `Mark ${targetState}`,
                actionIcon: 'bi bi-check-lg',
                actionTooltip: `1-click fix: Transition state from '${node.state}' to '${targetState}'`,
                targetState
            };
        },
        getNodeStage2Badges(node) {
            const cfg = this.hygieneConfig || {};
            if (cfg.enabled === false) return [];
            if (!node || typeof node.isSaved !== 'function' || !node.isSaved()) return [];
            if (this.isOutOfSprintParent(node)) return [];
            const category = this.stateCategory(node);
            if (!category || category === 'Removed') return [];

            const badges = [];

            if (cfg.blockedWorkItems !== false && node.isBlocked && category !== 'Completed') {
                const preds = Array.isArray(node.unresolvedPredecessors) ? node.unresolvedPredecessors : [];
                if (preds.length > 0) {
                    const first = preds[0];
                    const extra = preds.length > 1 ? ` +${preds.length - 1}` : '';
                    badges.push({
                        key: 'blocked',
                        badgeTestId: 'badge-work-item-blocked',
                        variant: 'rose',
                        icon: 'bi bi-octagon-fill',
                        label: `Blocked by #${first.id} (${first.state})${extra}`,
                        tooltip: preds.map(p => `Predecessor #${p.id}: ${p.title} (${p.state})`).join(' | ')
                    });
                } else {
                    badges.push({
                        key: 'blocked',
                        badgeTestId: 'badge-work-item-blocked',
                        variant: 'rose',
                        icon: 'bi bi-octagon-fill',
                        label: node.blockedReason || 'Blocked',
                        tooltip: node.blockedReason || 'This work item is flagged as blocked.'
                    });
                }
            }

            if (cfg.sprintRollover !== false && Number(node.sprintRolloverCount) >= 1 && category !== 'Completed') {
                const count = Number(node.sprintRolloverCount);
                const prevText = node.previousIterationName ? ` (from ${node.previousIterationName})` : '';
                badges.push({
                    key: 'sprint-rollover',
                    badgeTestId: 'badge-sprint-rollover',
                    variant: 'violet',
                    icon: 'bi bi-arrow-repeat',
                    label: `Rolled over x${count}`,
                    tooltip: `Sprint carry-over debt: Moved across ${count} sprint iteration${count === 1 ? '' : 's'}${prevText}.`
                });
            }

            if (cfg.addedMidSprint !== false && node.isAddedMidSprint) {
                let dateText = '';
                if (node.addedToSprintDate) {
                    try {
                        const dt = new Date(node.addedToSprintDate);
                        if (!isNaN(dt.getTime())) {
                            dateText = ` on ${dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                        }
                    } catch (e) {
                        dateText = '';
                    }
                }
                badges.push({
                    key: 'added-mid-sprint',
                    badgeTestId: 'badge-added-mid-sprint',
                    variant: 'amber',
                    icon: 'bi bi-calendar-plus',
                    label: 'Added Mid-Sprint',
                    tooltip: `Scope creep: Added to this sprint after the initial 24h planning window${dateText}.`
                });
            }

            if (cfg.assigneeOnPto !== false && category !== 'Completed' && node.assignedToName) {
                const pto = this.getPersonPto(node.assignedToName, '');
                if (pto) {
                    badges.push({
                        key: 'assignee-on-pto',
                        badgeTestId: 'badge-assignee-on-pto',
                        variant: 'amber',
                        icon: 'bi bi-airplane',
                        label: `Assignee on PTO (${pto.label})`,
                        tooltip: `${node.assignedToName}: ${pto.tooltip}. Consider reassigning this work item before the sprint ends.`
                    });
                }
            }

            return badges;
        },
        loadSprintPtoAsync() {
            const toRaw = this.selectedSprintTo;
            if (!toRaw) {
                this.ptoEntries = [];
                return Promise.resolve();
            }
            const today = new Date();
            const fromRaw = this.selectedSprintFrom ? new Date(this.selectedSprintFrom) : today;
            const from = fromRaw > today ? fromRaw : today;
            return this.loadPtoEntriesAsync(from, new Date(toRaw));
        },
        hasNodeAnyAttentionSignal(node) {
            return Boolean(this.getNodeHygieneAlert(node)) || this.getNodeStage2Badges(node).length > 0;
        },
        isFixingHygiene(node) {
            if (!node) return false;
            return Boolean(this.fixingHygieneIds[this.nodeKey(node)]);
        },
        async applyHygieneFixAsync(node, alert) {
            if (!node || !alert || this.isReadOnly(node) || this.isFixingHygiene(node)) return;
            const wiIdNum = parseInt(node.workItemId, 10);
            if (isNaN(wiIdNum)) return;

            const key = this.nodeKey(node);
            this.$set(this.fixingHygieneIds, key, true);
            try {
                const formData = new FormData();
                formData.append('workItemId', wiIdNum.toString());

                let resp;
                let toastMsg = 'Hygiene fix applied!';
                if (alert.key === 'closed-with-remaining') {
                    formData.append('vid', node.vid);
                    formData.append('title', node.title || '');
                    formData.append('state', node.state || '');
                    formData.append('remainingWork', '0');
                    formData.append('activity', node.activity || '');
                    formData.append('customFieldsJson', JSON.stringify(this.getChangedCustomFields(node)));
                    resp = await this.$http.patch('/TaskManagement/UpdateTaskItem', formData);
                    toastMsg = `Cleared remaining hours on #${wiIdNum} to 0h.`;
                } else if (node.isPlannable) {
                    formData.append('state', alert.targetState);
                    resp = await this.$http.patch('/TaskManagement/ChangeTaskItemState', formData);
                    toastMsg = `Moved #${wiIdNum} to '${alert.targetState}'.`;
                } else {
                    formData.append('state', alert.targetState);
                    resp = await this.$http.patch('/TaskManagement/ChangeUserStoryItemState', formData);
                    toastMsg = `Moved #${wiIdNum} to '${alert.targetState}'.`;
                }

                const baseInstance = node.isPlannable ? this.getTaskItemInstance() : this.getUserStoryItemInstance();
                const updated = _.assign(baseInstance, resp.data, {
                    children: node.children || [],
                    isContext: Boolean(node.isContext)
                });
                if (resp.data && resp.data.customFields) {
                    updated.customFields = { ...resp.data.customFields };
                }
                updated.loadStash();
                this.replaceNode(node, updated);
                this.toastSuccess(toastMsg);
            } catch (err) {
                this.handleMutationError(err, 'Failed to apply hygiene fix');
            } finally {
                this.$delete(this.fixingHygieneIds, key);
            }
        },
        isMatchedNode(node) {
            if (!this.isBaseMatchedNode(node)) return false;
            if (this.onlyHygieneAlerts && !this.hasNodeAnyAttentionSignal(node)) return false;
            return true;
        },
        isBaseMatchedNode(node) {
            if (!node.isSaved()) return true;
            // If the item has unsaved changes in progress, keep it visible until saved or reset
            if (typeof node.isModifying === 'function' && node.isModifying()) return true;
            if (this.isOutOfSprintParent(node)) return false;
            if (this.backlogViewSearch && this.backlogViewSearch.trim()) {
                const q = this.backlogViewSearch.trim().toLowerCase().replace(/^#/, '');
                const titleMatch = (node.title || '').toLowerCase().includes(q);
                const idMatch = String(node.workItemId || '').toLowerCase().includes(q);
                if (!titleMatch && !idMatch) return false;
            }
            const isContextOnly = !!node.isContext && !node.isAssignedToMe;
            return !isContextOnly && this.isItemStateMatching(node) && this.matchesAssigneeFilter(node);
        },
        nodeKey(node) {
            return node.workItemId ? 'wi-' + node.workItemId : node.vid;
        },
        isNodeExpanded(node) {
            const key = this.nodeKey(node);
            return Object.prototype.hasOwnProperty.call(this.expandOverrides, key) ? this.expandOverrides[key] : this.isAllCollapsesOpen;
        },
        toggleNode(node) {
            this.$set(this.expandOverrides, this.nodeKey(node), !this.isNodeExpanded(node));
            this.fixAllTextAreas();
        },
        indentStyle(row) {
            return row.depth > 0 ? { paddingLeft: (row.depth * 1.25 + 0.25) + 'rem' } : null;
        },
        fixAllTextAreas() {
            this.$nextTick(() => {
                (this.gridGroups || []).forEach(group => group.rows.forEach(row => this.resizeTextArea(row.node.vid)));
            });
        },
        getAllowedStates(item) {
            const pi = this.processInfo;
            let states = [];
            if (pi && pi.statesByWorkItemType && item) {
                const list = this.ciGet(pi.statesByWorkItemType, item.workItemType);
                if (Array.isArray(list)) states = [...list];
            }
            if (states.length === 0) states = [...this.stateOptions];
            if (item && item.state && !states.some(s => s.toLowerCase() === item.state.toLowerCase())) {
                states.push(item.state);
            }
            return _.uniq(states.filter(Boolean));
        },
        initialParentStateFor(type) {
            const pi = this.processInfo;
            const states = pi && pi.statesByWorkItemType ? this.ciGet(pi.statesByWorkItemType, type) : null;
            if (Array.isArray(states) && states.length > 0) {
                const preferred = pi.defaultInitialRequirementState;
                const match = preferred ? states.find(s => s.toLowerCase() === preferred.toLowerCase()) : null;
                return match || states[0];
            }
            return (pi && pi.defaultInitialRequirementState) || this.WI_STATE_NEW;
        },
        getCreatableParentTypes() {
            const pi = this.processInfo;
            if (!pi) return [this.WI_TYPE_USER_STORY || 'User Story'];
            if (pi.allowedChildTypes && Object.keys(pi.allowedChildTypes).length > 0) {
                return Object.keys(pi.allowedChildTypes);
            }
            const reqs = Array.isArray(pi.requirementWorkItemTypes) ? pi.requirementWorkItemTypes : [];
            const bugs = Array.isArray(pi.bugWorkItemTypes) ? pi.bugWorkItemTypes : [];
            const parentTypes = _.uniq([...reqs, ...bugs].filter(t => t && !this.isPlannableType(t)));
            return parentTypes.length > 0 ? parentTypes : [pi.primaryRequirementType || this.WI_TYPE_USER_STORY || 'User Story'];
        },
        onParentAction(key) {
            if (this.isViewOnlyMode) {
                this.toastUpgradeRequired('Pro Plan', 'Adding or editing tasks requires the Pro Plan.');
                return;
            }
            if (key === 'move-incomplete-next-sprint') {
                this.openMoveNextSprintModal();
                return;
            }
            if (key && key.indexOf('new-parent:') === 0) {
                this.addNewParentAsync(key.substring('new-parent:'.length));
            }
        },
        async addNewParentAsync(workItemType) {
            const existingArea = (this.userStoryItems || []).map(i => i && i.area).find(Boolean) || '';
            let newItem = this.getUserStoryItemInstance();
            _.assign(newItem, {
                workItemType: workItemType,
                title: "",
                titleHint: "Enter Title",
                area: existingArea,
                iteration: this.selectedSprint || "",
                effort: 0,
                isAssignedToMe: true,
                isContext: false,
                assignedToName: "",
                assignedToSource: "",
                children: []
            });
            newItem.loadStash();
            newItem.state = this.initialParentStateFor(workItemType);
            this.userStoryItems.unshift(newItem);
        },
        getCreatableChildTypes(node) {
            const pi = this.processInfo;
            if (!node || !node.isSaved() || !pi) return [];
            if (!pi.allowedChildTypes) {
                return node.isPlannable ? [] : [pi.primaryTaskType || this.WI_TYPE_TASK];
            }
            const allowed = this.ciGet(pi.allowedChildTypes, node.workItemType);
            return Array.isArray(allowed) ? allowed.filter(t => this.isPlannableType(t)) : [];
        },
        nodeActionButtons(row) {
            const node = row.node;
            if (this.isOutOfSprintParent(node)) {
                return [];
            }
            const isMine = !this.isReadOnly(node);
            const buttons = [
                { key: 'save', icon: 'bi bi-save', variant: 'warning', title: node.isPlannable ? 'Save' : 'Save Work', type: 'submit', visible: isMine && node.canSave() },
                { key: 'reset', icon: 'bi bi-arrow-counterclockwise', variant: 'primary', title: 'Reset Changes', visible: isMine && node.isSaved() && node.canReset() }
            ];
            if (node.isPlannable) {
                buttons.push({
                    key: 'delete', icon: 'bi bi-trash', variant: 'danger', title: 'Delete ' + (node.workItemType || 'Task'),
                    visible: isMine && node.canDeleteTask() && this.stateCategory(node) !== 'Removed' && !!this.removedStateFor(node.workItemType)
                });
            } else if (!node.isSaved()) {
                buttons.push({
                    key: 'delete', icon: 'bi bi-trash', variant: 'danger', title: 'Delete ' + (node.workItemType || 'Work Item'),
                    visible: isMine
                });
            }
            if (!this.isViewOnlyMode && !node.isModifying()) {
                this.getCreatableChildTypes(node).forEach(type => buttons.push({
                    key: 'new:' + type,
                    icon: 'bi bi-clipboard-plus',
                    variant: 'info',
                    title: 'New ' + type,
                    ariaLabel: 'Add ' + type,
                    dataTest: 'btn-add-' + type.toLowerCase().replace(/[^a-z0-9]+/g, '-')
                }));
                if (this.canMoveNodeToNextSprint(node)) {
                    buttons.push({
                        key: 'move-next-sprint',
                        icon: 'bi bi-fast-forward',
                        variant: 'outline-info',
                        title: `Move to ${this.nextSprint.name}`,
                        ariaLabel: `Move to ${this.nextSprint.name}`,
                        disabled: Boolean(this.isMovingToNextSprint),
                        dataTest: 'btn-move-next-sprint'
                    });
                }
            }
            return buttons;
        },
        onNodeAction(row, key) {
            if (this.isViewOnlyMode) {
                this.toastUpgradeRequired('Pro Plan', 'Adding or editing tasks requires the Pro Plan.');
                return;
            }
            const node = row.node;
            if (key === 'reset') {
                node.resetStash();
            } else if (key === 'delete') {
                this.removeTaskItemAsync(node);
            } else if (key === 'move-next-sprint') {
                this.moveNodeToNextSprintAsync(node);
            } else if (key.indexOf('new:') === 0) {
                this.addNewChildAsync(node, key.substring(4));
            }
        },
        isNodeEligibleForRollover(node) {
            if (!node || !node.isSaved || !node.isSaved() || !node.workItemId) return false;
            if (this.isReadOnly(node)) return false;
            if (!this.isNodeInSelectedSprint(node)) return false;
            if (this.isCompletedState(node)) return false;
            if (this.stateCategory(node) === 'Removed') return false;
            return true;
        },
        canMoveNodeToNextSprint(node) {
            if (!this.nextSprint || this.isViewOnlyMode) return false;
            if (!node || !node.isSaved || !node.isSaved() || node.isModifying()) return false;
            if (this.isOutOfSprintParent(node)) return false;
            if (this.isNodeEligibleForRollover(node)) return true;
            if (!node.isPlannable && Array.isArray(node.children)) {
                return node.children.some(c => this.isNodeEligibleForRollover(c));
            }
            return false;
        },
        collectRolloverIdsForNode(node) {
            if (!node) return [];
            if (node.isPlannable) {
                return this.isNodeEligibleForRollover(node) ? [node.workItemId] : [];
            }
            const ids = [];
            if (this.isNodeEligibleForRollover(node)) {
                ids.push(node.workItemId);
            }
            if (Array.isArray(node.children)) {
                node.children.forEach(ch => {
                    if (this.isNodeEligibleForRollover(ch)) {
                        ids.push(ch.workItemId);
                    }
                });
            }
            return _.uniq(ids);
        },
        openMoveNextSprintModal() {
            if (!this.nextSprint) return;
            this.moveNextSprintScopeNode = null;
            this.moveNextSprintSelectedIds = [...this.incompleteRolloverAllIds];
            this.showMoveNextSprintModal = true;
        },
        selectAllRolloverItems() {
            this.moveNextSprintSelectedIds = [...this.modalRolloverAllIds];
        },
        clearAllRolloverItems() {
            this.moveNextSprintSelectedIds = [];
        },
        toggleIncludeScopedChildren(checked) {
            const childIds = this.scopedEligibleChildIds;
            if (checked) {
                this.moveNextSprintSelectedIds = _.uniq([...this.moveNextSprintSelectedIds, ...childIds]);
            } else {
                const removeSet = new Set(childIds);
                this.moveNextSprintSelectedIds = this.moveNextSprintSelectedIds.filter(id => !removeSet.has(id));
            }
        },
        toggleRolloverItem(workItemId, checked) {
            const idx = this.moveNextSprintSelectedIds.indexOf(workItemId);
            if (checked && idx === -1) {
                this.moveNextSprintSelectedIds.push(workItemId);
            } else if (!checked && idx !== -1) {
                this.moveNextSprintSelectedIds.splice(idx, 1);
            }
        },
        async moveNodeToNextSprintAsync(node) {
            if (!node || !this.nextSprint) return;
            // If moving a parent item that has incomplete child items, prompt the user whether to also move children
            if (!node.isPlannable) {
                const eligibleChildren = (node.children || []).filter(ch => this.isNodeEligibleForRollover(ch));
                if (eligibleChildren.length > 0) {
                    this.moveNextSprintScopeNode = node;
                    this.moveNextSprintSelectedIds = this.collectRolloverIdsForNode(node);
                    this.showMoveNextSprintModal = true;
                    return;
                }
            }
            const ids = this.collectRolloverIdsForNode(node);
            if (ids.length === 0) return;
            await this.executeMoveWorkItemsToNextSprintAsync(ids);
        },
        async confirmMoveParentOnlyAsync() {
            if (!this.moveNextSprintScopeNode || !this.moveNextSprintScopeNode.workItemId) return;
            const ok = await this.executeMoveWorkItemsToNextSprintAsync([this.moveNextSprintScopeNode.workItemId]);
            if (ok) {
                this.showMoveNextSprintModal = false;
                this.moveNextSprintScopeNode = null;
            }
        },
        async confirmMoveNextSprintAsync() {
            if (this.moveNextSprintSelectedIds.length === 0) return;
            const ok = await this.executeMoveWorkItemsToNextSprintAsync(this.moveNextSprintSelectedIds);
            if (ok) {
                this.showMoveNextSprintModal = false;
                this.moveNextSprintScopeNode = null;
            }
        },
        async executeMoveWorkItemsToNextSprintAsync(workItemIds) {
            if (!this.nextSprint || !Array.isArray(workItemIds) || workItemIds.length === 0) return false;
            const targetSprint = this.nextSprint;
            this.isMovingToNextSprint = true;
            try {
                const response = await this.$http.post('/TaskManagement/MoveWorkItemsToSprint', {
                    workItemIds,
                    targetSprintPath: targetSprint.path
                });
                const movedIds = (response && response.data && Array.isArray(response.data.movedIds))
                    ? response.data.movedIds
                    : workItemIds;
                this.applyMovedWorkItemsLocally(movedIds, targetSprint.path);
                this.toastSuccess(`Moved ${movedIds.length} work item${movedIds.length === 1 ? '' : 's'} to ${targetSprint.name}.`);
                return true;
            } catch (err) {
                this.httpError(err);
                return false;
            } finally {
                this.isMovingToNextSprint = false;
            }
        },
        applyMovedWorkItemsLocally(movedIds, targetSprintPath) {
            const movedSet = new Set((movedIds || []).map(id => Number(id)));
            if (movedSet.size === 0) return;

            const nextRoots = [];
            (this.userStoryItems || []).forEach(root => {
                if (Array.isArray(root.children) && root.children.length > 0) {
                    root.children = root.children.filter(ch => !movedSet.has(Number(ch.workItemId)));
                }
                const rootMoved = movedSet.has(Number(root.workItemId));
                const hasRemainingChildren = Array.isArray(root.children) && root.children.length > 0;

                if (rootMoved) {
                    if (hasRemainingChildren) {
                        if (targetSprintPath) {
                            this.$set(root, 'iteration', targetSprintPath);
                        }
                        this.$set(root, 'isContext', true);
                        nextRoots.push(root);
                    }
                } else if (this.isOutOfSprintParent(root) && !hasRemainingChildren) {
                    // Out-of-sprint context parent has no remaining children in the current sprint
                } else {
                    nextRoots.push(root);
                }
            });
            this.userStoryItems = nextRoots;
        },
        findNodeLocation(vid, list, parent) {
            const items = list || this.userStoryItems;
            for (let i = 0; i < items.length; i++) {
                if (items[i].vid === vid) {
                    return { list: items, index: i, parent: parent || null };
                }
                const found = this.findNodeLocation(vid, items[i].children || [], items[i]);
                if (found) return found;
            }
            return null;
        },
        replaceNode(oldNode, newNode) {
            const location = this.findNodeLocation(oldNode.vid);
            if (location) location.list.splice(location.index, 1, newNode);
        },
        removeNode(node) {
            const location = this.findNodeLocation(node.vid);
            if (location) location.list.splice(location.index, 1);
        },
        async refreshNodeAsync(node) {
            if (!node || !node.workItemId) return;
            const resp = await this.$http.get("/TaskManagement/GetWorkItem", { params: { workItemId: node.workItemId } });
            const fresh = node.isPlannable ? this.getTaskItemInstance() : this.getUserStoryItemInstance();
            _.assign(fresh, resp.data, { children: node.children, isContext: node.isContext });
            if (resp.data && resp.data.customFields) {
                fresh.customFields = { ...resp.data.customFields };
            }
            fresh.loadStash();
            this.replaceNode(node, fresh);
        },
        buildNode(dto, depth) {
            const hasPlannableInfo = !!(this.processInfo && Array.isArray(this.processInfo.plannableWorkItemTypes));
            const plannable = hasPlannableInfo ? this.isPlannableType(dto.workItemType) : depth > 0;
            const node = plannable ? this.getTaskItemInstance() : this.getUserStoryItemInstance();
            _.assign(node, _.omit(dto, ['children', 'customFields']));
            node.customFields = { ...(dto.customFields || {}) };
            const ordered = _.orderBy(
                dto.children || [],
                [c => (c.isAssignedToMe ? 0 : 1), c => this.stateRankOf(c, !this.isPlannableType(c.workItemType)), 'createdDate'],
                ['asc', 'asc', 'desc']);
            node.children = ordered.map(c => this.buildNode(c, depth + 1));
            node.loadStash();
            return node;
        },
        applySearchResults(searchItems) {
            const roots = _.orderBy(
                searchItems || [],
                [r => (r.isAssignedToMe ? 0 : 1), r => this.stateRankOf(r, true), 'createdDate'],
                ['asc', 'asc', 'desc']);
            this.userStoryItems = roots.map(r => this.buildNode(r, 0));
        },
        async addNewChildAsync(parent, workItemType) {
            let newItem = this.getTaskItemInstance();
            _.assign(newItem, {
                workItemType: workItemType,
                title: "",
                titleHint: "Enter Title",
                parentWorkItemId: parent.workItemId,
                area: parent.area,
                iteration: parent.iteration,
                activity: this.defaultActivity(),
                assignedToName: "",
                assignedToSource: ""
            });
            newItem.loadStash();
            newItem.state = this.initialStateFor(workItemType);
            parent.children.unshift(newItem);

            this.$set(this.expandOverrides, this.nodeKey(parent), true);
        },
        async removeTaskItemAsync(item) {
            let component = this;
            const location = this.findNodeLocation(item.vid);
            const parentItem = location ? location.parent : null;

            if (!item.isSaved()) {
                this.removeNode(item);
                return;
            }

            const formData = new FormData();
            formData.append("workItemId", item.workItemId);
            formData.append("state", this.removedStateFor(item.workItemType));

            let httpActionAsync = async function () {
                component.loadingUserStoryItems = true;
                try {
                    const resp = await component.$http.patch("/TaskManagement/ChangeTaskItemState", formData);
                    _.assign(item, resp.data, { children: item.children });
                    item.loadStash();
                    if (!component.showRemovedWorkItems) {
                        component.removeNode(item);
                    }
                    await component.refreshNodeAsync(parentItem);
                    component.toastSuccess("Deleted!");
                } catch (err) {
                    component.handleMutationError(err, "Failed to delete task item");
                } finally {
                    component.stopSpinners();
                }
            }

            if (item.totalHours === 0) {
                await httpActionAsync();
                return;
            }

            this.$bvModal
                .msgBoxConfirm("Are you sure you want to mark this task as Removed? Logged hours will remain preserved in your timesheets.", {
                    size: "md",
                    buttonSize: "sm",
                    okVariant: "warning",
                    okTitle: "Mark as Removed",
                    cancelTitle: "Cancel",
                    headerClass: "p-2 border-bottom-0",
                    footerClass: "p-2 border-top-0",
                    centered: true
                })
                .then(async (isOk) => {
                    if (!isOk)
                        return;

                    await httpActionAsync();
                });
        },
        loadProcessInfoAsync() {
            if (!this._processInfoRequest) {
                const request = this.$http.get("/TaskManagement/GetProcessInfo")
                    .then(resp => {
                        if (this._processInfoRequest !== request) return;
                        this.processInfo = resp.data;
                        this.initStateFilter();
                    })
                    .catch(err => {
                        console.warn("Could not load process info:", err);
                    })
                    .finally(() => {
                        if (this._processInfoRequest === request) this._processInfoRequest = null;
                    });
                this._processInfoRequest = request;
            }
            return this._processInfoRequest;
        },
        async ensureProcessInfoAsync() {
            if (!this.processInfo) {
                await this.loadProcessInfoAsync();
            }
        },
        stopSpinners() {
            this.loadingUserStoryItems = false;
        }
    },
    beforeRouteLeave(to, from, next) {
        this.checkChangedItemsBeforeLeave(next);
        this.cleanupColResize();
    }
};
</script>

<style scoped>
.task-management-page a,
.task-management-page button,
.task-management-page textarea,
.task-management-page input,
.task-management-page select,
.task-management-page option,
.task-management-page .input-group-text {
    font-size: 0.75rem;
}

.time-grid-scroll {
    overflow-x: auto;
    overflow-y: auto;
    width: 100%;
    min-height: 260px;
    max-height: calc(100vh - 180px);
    scrollbar-width: thin;
    scrollbar-color: var(--vt-scrollbar-thumb, #475569) var(--vt-scrollbar-track, transparent);
}

.time-grid-scroll::-webkit-scrollbar {
    width: 8px;
    height: 8px;
}

.time-grid-scroll::-webkit-scrollbar-track {
    background: var(--vt-scrollbar-track, transparent);
    border-radius: 4px;
}

.time-grid-scroll::-webkit-scrollbar-thumb {
    background: var(--vt-scrollbar-thumb, #475569);
    border-radius: 4px;
}

.time-grid-scroll::-webkit-scrollbar-thumb:hover {
    background: var(--vt-scrollbar-thumb-hover, #64748b);
}

.time-grid-content {
    min-width: 100%;
    width: -moz-fit-content;
    width: fit-content;
    display: flex;
    flex-direction: column;
}

.time-grid-content > .grid-filter-toolbar {
    width: 0;
    min-width: 100%;
    box-sizing: border-box;
}

.is-context-row {
    opacity: 0.72;
}

.is-out-of-sprint-parent {
    opacity: 0.78;
    background: repeating-linear-gradient(
        -45deg,
        rgba(148, 163, 184, 0.06),
        rgba(148, 163, 184, 0.06) 8px,
        rgba(148, 163, 184, 0.13) 8px,
        rgba(148, 163, 184, 0.13) 16px
    );
    border-left: 3px dashed #94a3b8;
    border-radius: 4px;
}

[data-theme="dark"] .is-out-of-sprint-parent {
    background: repeating-linear-gradient(
        -45deg,
        rgba(30, 41, 59, 0.45),
        rgba(30, 41, 59, 0.45) 8px,
        rgba(51, 65, 85, 0.45) 8px,
        rgba(51, 65, 85, 0.45) 16px
    );
    border-left: 3px dashed #64748b;
}

.wi-id-stack {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    max-width: 100%;
    min-width: 0;
}

.out-of-sprint-badge {
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 0.1rem 0.35rem;
    border-radius: 4px;
    font-size: 0.65rem;
    line-height: 1.2;
    font-weight: 600;
    letter-spacing: 0.01em;
    color: #92400e;
    background-color: rgba(245, 158, 11, 0.15);
    border: 1px solid rgba(245, 158, 11, 0.4);
    cursor: help;
    user-select: none;
}

[data-theme="dark"] .out-of-sprint-badge {
    color: #fcd34d;
    background-color: rgba(245, 158, 11, 0.18);
    border-color: rgba(245, 158, 11, 0.45);
}

.parent-move-prompt {
    background-color: rgba(14, 165, 233, 0.08);
    border: 1px solid rgba(14, 165, 233, 0.3);
    color: var(--vt-text, #1e293b);
}

[data-theme="dark"] .parent-move-prompt {
    background-color: rgba(56, 189, 248, 0.12);
    border-color: rgba(56, 189, 248, 0.35);
    color: #e2e8f0;
}

.task-items-grid>.time-grid-scroll>.time-grid-content>.card:first-child {
    position: sticky;
    top: 0;
    z-index: 20;
}

.task-items-grid .card {
    min-width: 100%;
    width: 100%;
    box-sizing: border-box;
}

.task-items-grid ::v-deep .form-control,
.task-items-grid ::v-deep .custom-select,
.task-items-grid ::v-deep .input-group-text {
    font-size: 0.8rem;
    padding: 0.2rem 0.35rem;
    height: calc(1.5em + 0.5rem + 2px);
    min-height: 1.9rem;
}

.task-items-grid ::v-deep .custom-select {
    padding-right: 1.6rem !important;
}

.task-items-grid ::v-deep .btn {
    font-size: 0.8rem;
    padding: 0.2rem 0.35rem;
}

.parent-add-actions-bar {
    padding: 0.35rem 1.25rem 0 1.25rem;
}

.parent-add-actions-bar > .time-col-buttons {
    min-width: 4.5rem;
}

.time-col-buttons {
    display: flex;
    align-items: center;
    justify-content: center;
}

.time-col-buttons ::v-deep .btn-group > .btn {
    padding: 0.18rem 0.28rem;
}

.time-col-capacity ::v-deep .input-group,
.time-col-estimate ::v-deep .input-group,
.time-col-custom ::v-deep .input-group {
    flex-wrap: nowrap;
    width: auto;
}

.time-col-state>div {
    position: relative;
    width: 100%;
    min-height: 1.9rem;
    display: flex;
    align-items: center;
}

.time-col-activity {
    position: relative;
}

.time-col-activity>div {
    position: relative;
    width: 100%;
}

.time-col-state ::v-deep .custom-select.is-overflowing:hover,
.time-col-state ::v-deep select.is-overflowing:hover,
.time-col-state > div:hover ::v-deep .custom-select.is-overflowing,
.time-col-activity ::v-deep .custom-select.is-overflowing:hover,
.time-col-activity ::v-deep select.is-overflowing:hover,
.time-col-custom ::v-deep .custom-select.is-overflowing:hover,
.time-col-custom ::v-deep select.is-overflowing:hover {
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
    z-index: 30;
    width: max-content !important;
    min-width: 100%;
    max-width: 90vw;
    padding-right: 1.6rem !important;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
}

.time-col-capacity ::v-deep .form-control,
.time-col-estimate ::v-deep .form-control,
.time-col-custom ::v-deep input[type="number"] {
    width: 3.1rem !important;
    max-width: 3.1rem !important;
    min-width: 3.1rem !important;
    flex: 0 0 3.1rem !important;
    padding-left: 0.2rem !important;
    padding-right: 0.1rem !important;
    text-align: center;
}

.time-col-capacity ::v-deep input[type="number"]::-webkit-inner-spin-button,
.time-col-estimate ::v-deep input[type="number"]::-webkit-inner-spin-button,
.time-col-custom ::v-deep input[type="number"]::-webkit-inner-spin-button {
    opacity: 1;
    cursor: pointer;
}

[data-theme="dark"] .time-col-capacity ::v-deep input[type="number"],
[data-theme="dark"] .time-col-estimate ::v-deep input[type="number"],
[data-theme="dark"] .time-col-custom ::v-deep input[type="number"] {
    color-scheme: dark;
}

.time-col-capacity ::v-deep .input-group-text,
.time-col-estimate ::v-deep .input-group-text,
.time-col-custom ::v-deep .input-group-text {
    padding-left: 0.35rem;
    padding-right: 0.35rem;
    font-size: 0.8rem;
    white-space: nowrap;
}

.task-items-grid .time-row {
    display: grid !important;
    width: 100%;
    min-width: 0 !important;
    margin-left: 0;
    margin-right: 0;
    align-items: center;
    column-gap: 0.35rem;
    box-sizing: border-box;
}

.task-items-grid .time-row>[class*="col"] {
    flex: none;
    width: auto;
    max-width: none;
    min-width: 0;
    padding-left: 0;
    padding-right: 0;
}

.time-col-title {
    min-width: 0;
}

.task-items-grid .backlog-header-row {
    align-items: stretch !important;
}

.task-items-grid .backlog-header-row > [class*="col"] {
    position: relative;
    user-select: none;
    align-self: stretch !important;
    display: flex;
    align-items: center;
    justify-content: center;
}

.task-items-grid .backlog-header-row .time-col-state > div,
.task-items-grid .backlog-header-row .time-col-assigned > div {
    position: static;
}

.task-items-grid .header-col-label {
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: bottom;
}

.task-items-grid .col-resize-handle {
    position: absolute;
    top: -2px;
    bottom: -2px;
    right: -5px;
    width: 9px;
    cursor: col-resize;
    z-index: 15;
    display: flex;
    align-items: center;
    justify-content: center;
}

.task-items-grid .col-resize-handle::after {
    content: "";
    width: 1px;
    height: 80%;
    min-height: 24px;
    background-color: rgba(148, 163, 184, 0.35);
    border-radius: 1px;
    transition: background-color 0.15s ease, width 0.15s ease, height 0.15s ease, box-shadow 0.15s ease;
}

[data-theme="dark"] .task-items-grid .col-resize-handle::after {
    background-color: rgba(148, 163, 184, 0.28);
}

.task-items-grid .backlog-header-row > [class*="col"]:hover .col-resize-handle::after {
    background-color: rgba(100, 116, 139, 0.65);
}

[data-theme="dark"] .task-items-grid .backlog-header-row > [class*="col"]:hover .col-resize-handle::after {
    background-color: rgba(148, 163, 184, 0.55);
}

.task-items-grid .col-resize-handle:hover::after,
.task-items-grid .col-resize-handle.is-resizing::after {
    width: 3px !important;
    height: 100% !important;
    background-color: #0284c7 !important;
    box-shadow: 0 0 6px rgba(2, 132, 199, 0.45);
}

[data-theme="dark"] .task-items-grid .col-resize-handle:hover::after,
[data-theme="dark"] .task-items-grid .col-resize-handle.is-resizing::after {
    background-color: #38bdf8 !important;
    box-shadow: 0 0 8px rgba(56, 189, 248, 0.55);
}

.task-items-grid .grid-node-row .time-row > [class*="col"]:not(.time-col-title) {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
}

.task-items-grid .grid-node-row .wi-id-stack {
    align-items: center;
}

.task-items-grid .grid-node-row .time-col-progress > div {
    width: 100%;
}

.task-items-grid .grid-node-row .time-col-custom > div {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.time-col-title-noop {
    min-width: 0;
}

.time-col-assigned .assigned-to-image {
    width: 30px;
    height: 30px;
    margin: 0px 2px;
}

.time-col-assigned .assigned-to-image.sa {
    width: 18px;
    height: 18px;
    /*border: 1px solid red;*/
}

.time-col-assigned .assigned-to-image.dev {
    width: 18px;
    height: 18px;
    /*border: 1px solid green;*/
}

.time-col-assigned .assigned-to-image.qa {
    width: 18px;
    height: 18px;
    /*border: 1px solid #6f42c1;*/
}

.time-row {
    padding: 5px;
}

.time-col-id {
    position: relative;
    overflow: visible;
    display: flex;
    align-items: center;
}

.time-col-state {
    position: relative;
    overflow: visible;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.time-status-filter-wrap {
    position: relative;
    display: inline-block;
    max-width: 100%;
}

.time-status-filter {
    margin-top: 2px;
    font-size: 0.7rem;
    max-width: 100%;
    position: relative;
}

.time-status-filter>>>.dropdown-toggle {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.7rem;
    padding: 0.15rem 0.35rem;
}

.time-status-filter>>>.dropdown-toggle.is-pinned {
    position: fixed;
    z-index: 1050;
    width: max-content !important;
    max-width: 90vw;
    overflow: visible;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
}

.time-status-filter>>>.dropdown-menu {
    padding: 0.15rem 0 !important;
    min-width: 6.5rem;
}

.time-status-filter-form {
    padding: 0.1rem 0 !important;
    min-width: 6.5rem;
}

.time-status-filter-form>>>.b-dropdown-form {
    padding: 0.15rem 0.35rem !important;
}

.time-status-filter-form>>>.custom-control {
    position: relative !important;
    display: block !important;
    padding: 0.15rem 0.4rem 0.15rem 1.1rem !important;
    margin: 0 !important;
    font-size: 0.8rem !important;
    white-space: nowrap;
    min-height: 0 !important;
}

.time-status-filter-form>>>.custom-control:hover {
    background-color: #f8f9fa;
}

[data-theme="dark"] .time-status-filter-form>>>.custom-control:hover {
    background-color: #334155;
}

.time-status-filter-form>>>.custom-control-label {
    width: 100%;
    cursor: pointer;
    margin-bottom: 0;
}

.time-status-filter-form>>>.custom-control-label::before,
.time-status-filter-form>>>.custom-control-label::after {
    top: 0.05rem;
    left: -0.95rem;
    width: 0.8rem;
    height: 0.8rem;
}

.time-status-filter-form>>>.custom-control-input {
    left: 0.05rem;
}

.action-buttons button {
    margin: 2px;
}

.disabled-option {
    color: lightgray !important;
    background-color: #f0f0f0 !important;
    pointer-events: none;
    cursor: not-allowed;
    opacity: 1 !important;
}

.wi-show-switch,
.wi-show-switch input,
.wi-show-switch label {
    cursor: pointer;
}



.time-col-custom {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
}

.time-col-custom > div {
    position: relative;
    width: 100%;
}

.time-col-custom .custom-field-cell {
    display: block;
    width: 100%;
    text-align: center;
    padding: 0.2rem 0.35rem;
    border-radius: 4px;
    background-color: rgba(108, 117, 125, 0.08);
}
.custom-field-na {
    color: #94a3b8;
    font-weight: 600;
    font-size: 0.9rem;
    user-select: none;
}
[data-theme="dark"] .custom-field-na {
    color: #64748b;
}
.task-grid-overlay {
    min-height: 260px;
    width: 100%;
    display: block;
}

/* Sprint Capacity Goal Indicator Banner */
.sprint-capacity-banner {
    background-color: var(--vt-card-bg, #ffffff);
    border: 1px solid var(--vt-card-border, #e2e8f0);
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    transition: background-color 0.2s ease, border-color 0.2s ease;
}
.sprint-capacity-banner.compact {
    min-height: 38px;
}
[data-theme="dark"] .sprint-capacity-banner {
    background-color: #1a2332;
    border-color: #2d3748;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
}
.sprint-capacity-pill {
    font-size: 0.72rem;
    font-weight: 600;
    padding: 0.2rem 0.5rem;
    border-radius: 12px;
}
.sprint-capacity-subtext {
    font-size: 0.8rem;
    color: var(--vt-muted, #6c757d);
}
.sprint-capacity-subtext strong,
.sprint-capacity-subtext .sprint-capacity-val {
    color: var(--vt-text, #212529);
    font-weight: 600;
}
[data-theme="dark"] .sprint-capacity-subtext {
    color: var(--vt-muted, #94a3b8);
}
[data-theme="dark"] .sprint-capacity-subtext strong,
[data-theme="dark"] .sprint-capacity-subtext .sprint-capacity-val {
    color: #f1f5f9 !important;
}
.sprint-capacity-progress-wrap {
    cursor: default;
}
.sprint-capacity-progress {
    width: 140px;
    min-width: 100px;
    background-color: rgba(100, 116, 139, 0.15);
}
[data-theme="dark"] .sprint-capacity-progress {
    background-color: rgba(255, 255, 255, 0.1);
}
.sprint-capacity-pct {
    min-width: 28px;
    text-align: right;
    font-size: 0.75rem;
}
.sprint-capacity-badge-pill {
    display: inline-flex;
    align-items: center;
    padding: 0.35rem 0.85rem;
    border-radius: 20px;
    font-size: 0.95rem;
    font-weight: 600;
    line-height: 1.3;
    border: 1.5px solid transparent;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    transition: all 0.2s ease;
}
.sprint-capacity-badge-pill .status-icon {
    font-size: 1.15rem;
}
.sprint-capacity-badge-pill .status-label {
    font-size: 0.95rem;
    font-weight: 600;
    letter-spacing: 0.01em;
}
.sprint-capacity-badge-pill .status-value {
    font-size: 1.18rem;
    font-weight: 800;
    letter-spacing: 0.02em;
}
.sprint-capacity-badge-pill.status-neutral {
    background-color: rgba(100, 116, 139, 0.15);
    color: #475569;
    border-color: rgba(100, 116, 139, 0.35);
}
[data-theme="dark"] .sprint-capacity-badge-pill.status-neutral {
    background-color: rgba(148, 163, 184, 0.2);
    color: #e2e8f0;
    border-color: rgba(148, 163, 184, 0.4);
}
.sprint-capacity-badge-pill.status-overspend {
    background-color: rgba(220, 38, 38, 0.15);
    color: #b91c1c;
    border-color: rgba(220, 38, 38, 0.45);
}
[data-theme="dark"] .sprint-capacity-badge-pill.status-overspend {
    background-color: rgba(239, 68, 68, 0.25);
    color: #fca5a5;
    border-color: rgba(239, 68, 68, 0.5);
}
.sprint-capacity-badge-pill.status-met {
    background-color: rgba(22, 163, 74, 0.15);
    color: #15803d;
    border-color: rgba(22, 163, 74, 0.45);
}
[data-theme="dark"] .sprint-capacity-badge-pill.status-met {
    background-color: rgba(34, 197, 94, 0.25);
    color: #86efac;
    border-color: rgba(34, 197, 94, 0.5);
}
.sprint-capacity-badge-pill.status-info {
    background-color: rgba(2, 132, 199, 0.15);
    color: #0369a1;
    border-color: rgba(2, 132, 199, 0.45);
}
[data-theme="dark"] .sprint-capacity-badge-pill.status-info {
    background-color: rgba(56, 189, 248, 0.25);
    color: #7dd3fc;
    border-color: rgba(56, 189, 248, 0.5);
}

/* Comments column styles */
.time-col-comments {
    display: flex;
    align-items: center;
    justify-content: center;
}
.wi-comment-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-decoration: none !important;
    transition: opacity 0.15s ease, transform 0.15s ease;
}
.wi-comment-btn:hover {
    transform: scale(1.1);
}
.wi-comment-count {
    font-size: 0.68rem;
    padding: 0.15rem 0.35rem;
}
.wi-mention-count {
    font-size: 0.68rem;
    padding: 0.15rem 0.35rem;
    font-weight: 700;
}
.filter-options-list {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.state-group-section {
    margin-bottom: 0.45rem;
}
.state-group-section:last-child {
    margin-bottom: 0;
}
.state-group-header {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: var(--text-muted, #64748b);
    border-bottom: 1px solid var(--border-color, rgba(148, 163, 184, 0.2));
    padding-bottom: 0.2rem;
    margin-bottom: 0.3rem;
}
.group-select-all {
    font-size: 0.7rem;
    color: var(--primary, #0d6efd);
    padding: 0;
    line-height: 1;
}
.compact-checkbox {
    font-size: 0.82rem;
    line-height: 1.3;
    margin-bottom: 0 !important;
    width: 100%;
}
.compact-checkbox >>> .custom-control-label {
    cursor: pointer;
    font-size: 0.82rem;
    padding-top: 1px;
    user-select: none;
    white-space: nowrap;
    width: 100%;
}
.compact-checkbox >>> .custom-control-input:disabled ~ .custom-control-label {
    cursor: not-allowed;
    opacity: 0.65;
}

/* Value color select styling */
.value-color-select {
    background-color: #f1f5f9 !important;
    border-color: #cbd5e1 !important;
    color: #0f172a !important;
}

[data-theme="dark"] .value-color-select {
    background-color: #1e293b !important;
    border-color: #475569 !important;
    color: #f8fafc !important;
}

.value-color-select[style*="--val-color"] {
    transition: background-color 0.2s ease, border-color 0.2s ease;
    font-weight: 500;
}

[data-theme="dark"] .value-color-select[style*="--val-color"] {
    background-color: color-mix(in srgb, var(--val-color) 25%, rgba(15, 23, 42, 0.9)) !important;
    border-color: color-mix(in srgb, var(--val-color) 60%, transparent) !important;
    color: #ffffff !important;
}

[data-theme="light"] .value-color-select[style*="--val-color"],
:root:not([data-theme="dark"]) .value-color-select[style*="--val-color"] {
    background-color: color-mix(in srgb, var(--val-color) 18%, #f1f5f9) !important;
    border-color: color-mix(in srgb, var(--val-color) 55%, #cbd5e1) !important;
    color: #0f172a !important;
}

/* ============================================================================
 * Responsive Structured Card Layout (<=991.98px Tablets & Phones)
 * ============================================================================ */
@media (max-width: 991.98px) {
    .mobile-col-caption {
        display: block !important;
        font-size: 0.65rem !important;
        font-weight: 700 !important;
        text-transform: uppercase !important;
        letter-spacing: 0.5px !important;
        color: #64748b !important;
        margin-bottom: 0.25rem !important;
        line-height: 1 !important;
        white-space: nowrap !important;
        user-select: none;
    }

    [data-theme="dark"] .mobile-col-caption {
        color: #94a3b8 !important;
    }

    .task-items-grid {
        padding-left: 0.25rem !important;
        padding-right: 0.25rem !important;
    }

    .time-grid-scroll {
        max-height: none !important;
        overflow-x: visible !important;
        overflow-y: visible !important;
    }

    .time-grid-content {
        width: 100% !important;
        min-width: 0 !important;
    }

    .task-items-grid > .time-grid-scroll > .time-grid-content > .card:first-child {
        position: static !important;
    }

    .task-items-grid .task-items-grid-header .time-row {
        display: none !important;
    }

    .task-items-grid-header .grid-filter-toolbar.mobile-filters-collapsed {
        display: none !important;
    }

    .task-items-grid-header .mobile-filter-toggle-bar + .grid-filter-toolbar:not(.mobile-filters-collapsed) {
        margin-top: 0.45rem !important;
        padding-top: 0.45rem !important;
        border-top: 1px dashed var(--vt-border, rgba(148, 163, 184, 0.3)) !important;
    }

    .task-items-grid-header .grid-filter-toolbar {
        border-bottom: none !important;
        margin-bottom: 0 !important;
        padding-bottom: 0 !important;
    }

    /* Flatten outer card wrapper on mobile/tablet so individual items are clear cards */
    .task-items-grid .time-grid-content > div[data-test="grid-group"] > .card {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        padding: 0 !important;
        margin: 0 !important;
    }

    .task-items-grid .time-grid-content > div[data-test="grid-group"] > .card > .card-body {
        padding: 0 !important;
    }

    /* Structured Card per Work Item & Task */
    .task-items-grid .grid-node-row {
        background-color: var(--vt-surface-card, rgba(30, 41, 59, 0.45));
        border: 1px solid var(--vt-border, rgba(148, 163, 184, 0.22));
        border-radius: 0.65rem;
        padding: 0.75rem 0.85rem !important;
        margin-bottom: 0.75rem !important;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
        transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }

    [data-theme="dark"] .task-items-grid .grid-node-row {
        background-color: rgba(30, 41, 59, 0.65) !important;
        border-color: rgba(148, 163, 184, 0.24) !important;
    }

    [data-theme="light"] .task-items-grid .grid-node-row {
        background-color: #ffffff !important;
        border-color: rgba(203, 213, 225, 0.85) !important;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    }

    .task-items-grid .grid-node-row[data-depth="1"],
    .task-items-grid .grid-node-row[data-depth="2"],
    .task-items-grid .grid-node-row[data-depth="3"] {
        border-left: 3px solid #38bdf8 !important;
        padding-left: 0.75rem !important;
        margin-left: 0.85rem !important;
    }

    .task-items-grid .grid-node-row .time-row {
        display: flex !important;
        flex-wrap: wrap !important;
        align-items: flex-start !important;
        gap: 0.45rem 0.65rem !important;
        padding: 0 !important;
        height: auto !important;
    }

    /* Row 1: Collapser + ID badge + Comments + Assigned on left, Action buttons on far right */
    .task-items-grid .grid-node-row .time-col-collapser,
    .task-items-grid .grid-node-row .time-col-id,
    .task-items-grid .grid-node-row .time-col-comments,
    .task-items-grid .grid-node-row .time-col-assigned {
        order: 1 !important;
        flex: 0 0 auto !important;
        width: auto !important;
        max-width: none !important;
        padding: 0 !important;
        display: flex !important;
        align-items: center !important;
    }

    .task-items-grid .grid-node-row .time-col-buttons {
        order: 1 !important;
        margin-left: auto !important;
        flex: 0 0 auto !important;
        width: auto !important;
        max-width: none !important;
        padding: 0 !important;
        display: flex !important;
        align-items: center !important;
    }

    /* Row 2: Full-width Title */
    .task-items-grid .grid-node-row .time-col-title {
        order: 2 !important;
        flex: 0 0 100% !important;
        width: 100% !important;
        max-width: 100% !important;
        padding: 0 !important;
        margin-top: 0.2rem !important;
        margin-bottom: 0.35rem !important;
        text-align: left !important;
    }

    .task-items-grid .grid-node-row .time-col-title ::v-deep textarea {
        font-size: 0.95rem !important;
        font-weight: 600 !important;
        line-height: 1.35 !important;
    }

    /* Row 3: Controls with Captions */
    .task-items-grid .grid-node-row .time-col-state {
        order: 3 !important;
        flex: 0 0 auto !important;
        width: auto !important;
        max-width: 200px !important;
        padding: 0 !important;
        display: flex !important;
        flex-direction: column !important;
        align-items: flex-start !important;
    }

    .task-items-grid .grid-node-row .time-col-state ::v-deep .custom-select {
        height: 31px !important;
        font-size: 0.85rem !important;
        min-width: 130px;
        max-width: 180px;
        width: 100%;
    }

    .task-items-grid .grid-node-row .time-col-capacity {
        order: 3 !important;
        flex: 0 0 auto !important;
        width: auto !important;
        padding: 0 !important;
        display: flex !important;
        flex-direction: column !important;
        align-items: flex-start !important;
    }

    .task-items-grid .grid-node-row .time-col-capacity:empty {
        display: none !important;
    }

    .task-items-grid .grid-node-row .time-col-estimate {
        order: 3 !important;
        flex: 0 0 auto !important;
        width: auto !important;
        padding: 0 !important;
        display: flex !important;
        flex-direction: column !important;
        align-items: flex-start !important;
    }

    .task-items-grid .grid-node-row .time-col-estimate:empty {
        display: none !important;
    }

    .task-items-grid .grid-node-row .time-col-capacity ::v-deep .form-control,
    .task-items-grid .grid-node-row .time-col-estimate ::v-deep .form-control {
        width: 3.2rem !important;
        max-width: 3.2rem !important;
        min-width: 3.2rem !important;
        flex: 0 0 3.2rem !important;
        height: 31px !important;
        padding: 0.2rem 0.25rem !important;
        text-align: center;
        font-size: 0.85rem !important;
    }

    .task-items-grid .grid-node-row .time-col-capacity ::v-deep .input-group-text,
    .task-items-grid .grid-node-row .time-col-estimate ::v-deep .input-group-text {
        padding: 0.2rem 0.35rem !important;
        font-size: 0.75rem !important;
        height: 31px !important;
    }

    .task-items-grid .grid-node-row .time-col-custom {
        order: 3 !important;
        flex: 0 0 auto !important;
        width: auto !important;
        padding: 0 !important;
        display: flex !important;
        flex-direction: column !important;
        align-items: flex-start !important;
    }

    .task-items-grid .grid-node-row .time-col-progress {
        order: 4 !important;
        flex: 0 0 100% !important;
        width: 100% !important;
        max-width: 100% !important;
        padding: 0 !important;
        margin-top: 0.2rem !important;
    }

    .task-items-grid .grid-node-row [data-test="grid-row-task"] .time-col-progress {
        display: none !important;
    }

    .sprint-capacity-banner .sprint-capacity-progress-wrap {
        display: none !important;
    }
}

.hygiene-filter-chip {
    height: 31px;
    padding: 0 0.65rem;
    border-radius: 6px;
    font-size: 0.78rem;
    font-weight: 600;
    border: 1px solid #cbd5e1;
    background: #f8fafc;
    color: #475569;
    transition: all 0.15s ease;
    white-space: nowrap;
}

.hygiene-filter-chip:hover {
    background: #f1f5f9;
    border-color: #94a3b8;
    color: #1e293b;
}

.hygiene-filter-chip.has-alerts {
    border-color: rgba(245, 158, 11, 0.45);
    background: rgba(245, 158, 11, 0.08);
    color: #b45309;
}

.hygiene-filter-chip.is-active {
    background: linear-gradient(135deg, #d97706 0%, #b45309 100%) !important;
    border-color: #b45309 !important;
    color: #ffffff !important;
    box-shadow: 0 2px 6px rgba(180, 83, 9, 0.25);
}

.hygiene-filter-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 700;
    background: rgba(15, 23, 42, 0.08);
    color: inherit;
}

.hygiene-filter-chip.has-alerts .hygiene-filter-count {
    background: rgba(245, 158, 11, 0.22);
    color: #92400e;
}

.hygiene-filter-chip.is-active .hygiene-filter-count {
    background: rgba(255, 255, 255, 0.24);
    color: #ffffff;
}

.hygiene-alert-bar {
    gap: 0.25rem;
    line-height: 1.2;
}

.hygiene-badge {
    font-size: 0.71rem;
    font-weight: 600;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    border: 1px solid transparent;
    letter-spacing: 0.01em;
}

.hygiene-badge-amber {
    background: rgba(245, 158, 11, 0.14);
    border-color: rgba(245, 158, 11, 0.38);
    color: #b45309;
}

.hygiene-badge-teal {
    background: rgba(13, 148, 136, 0.13);
    border-color: rgba(13, 148, 136, 0.36);
    color: #0f766e;
}

.hygiene-badge-rose {
    background: rgba(225, 29, 72, 0.12);
    border-color: rgba(225, 29, 72, 0.35);
    color: #be123c;
}

.hygiene-badge-emerald {
    background: rgba(16, 185, 129, 0.14);
    border-color: rgba(16, 185, 129, 0.38);
    color: #047857;
}

.hygiene-badge-violet {
    background: rgba(139, 92, 246, 0.14);
    border-color: rgba(139, 92, 246, 0.38);
    color: #6d28d9;
}

.hygiene-fix-btn {
    font-size: 0.7rem;
    font-weight: 600;
    padding: 0.12rem 0.48rem;
    border-radius: 999px;
    border: 1px solid #cbd5e1;
    background: #ffffff;
    color: #1e293b;
    transition: all 0.15s ease;
    line-height: 1.25;
}

.hygiene-fix-btn:hover:not(:disabled) {
    background: #0284c7;
    border-color: #0284c7;
    color: #ffffff;
    box-shadow: 0 2px 5px rgba(2, 132, 199, 0.25);
}

[data-theme="dark"] .hygiene-filter-chip {
    background: rgba(30, 41, 59, 0.8);
    border-color: rgba(148, 163, 184, 0.28);
    color: #cbd5e1;
}

[data-theme="dark"] .hygiene-filter-chip:hover {
    background: rgba(51, 65, 85, 0.9);
    border-color: rgba(148, 163, 184, 0.45);
    color: #f8fafc;
}

[data-theme="dark"] .hygiene-filter-chip.has-alerts {
    background: rgba(245, 158, 11, 0.14);
    border-color: rgba(251, 191, 36, 0.45);
    color: #fbbf24;
}

[data-theme="dark"] .hygiene-filter-chip.has-alerts .hygiene-filter-count {
    background: rgba(245, 158, 11, 0.25);
    color: #fde68a;
}

[data-theme="dark"] .hygiene-badge-amber {
    background: rgba(245, 158, 11, 0.18);
    border-color: rgba(251, 191, 36, 0.42);
    color: #fcd34d;
}

[data-theme="dark"] .hygiene-badge-teal {
    background: rgba(20, 184, 166, 0.18);
    border-color: rgba(45, 212, 191, 0.42);
    color: #5eead4;
}

[data-theme="dark"] .hygiene-badge-rose {
    background: rgba(244, 63, 94, 0.18);
    border-color: rgba(251, 113, 133, 0.42);
    color: #fda4af;
}

[data-theme="dark"] .hygiene-badge-emerald {
    background: rgba(16, 185, 129, 0.18);
    border-color: rgba(52, 211, 153, 0.42);
    color: #6ee7b7;
}

[data-theme="dark"] .hygiene-badge-violet {
    background: rgba(139, 92, 246, 0.2);
    border-color: rgba(167, 139, 250, 0.45);
    color: #c4b5fd;
}

[data-theme="dark"] .hygiene-fix-btn {
    background: rgba(30, 41, 59, 0.92);
    border-color: rgba(148, 163, 184, 0.35);
    color: #e2e8f0;
}

[data-theme="dark"] .hygiene-fix-btn:hover:not(:disabled) {
    background: #0284c7;
    border-color: #38bdf8;
    color: #ffffff;
}

    .mobile-backlog-noop {
    color: #0f172a !important;
}
</style>
<style>
.action-hover-pop {
    pointer-events: none;
    z-index: 1030;
}
</style>
